// Release metadata: single source of truth for the in-app
// "Sobre / Versão / Funcionalidades / Limitações / Ajuda" UI shown
// in the Diagnóstico popover.
//
// Workflow para publicar uma nova versão:
//   1. Adicionar nova entrada ao topo de RELEASES (data ISO, summary,
//      added/changed/fixed). Manter formato "0.X.Y-channel.N".
//   2. Actualizar VERSION e CHANNEL para essa nova entrada.
//   3. Actualizar FEATURES e LIMITATIONS se aplicável (estas listas
//      reflectem o estado actual da plataforma, não a história).
//   4. Mirror em CHANGELOG.md na raiz do repo (mesmo conteúdo, formato
//      Keep a Changelog).
//   5. Bump em frontend/package.json.
//   6. Commit + deploy.

export type ReleaseChannel = 'alpha' | 'beta' | 'stable'

export const VERSION = '0.2.0-beta.210'
export const CHANNEL: ReleaseChannel = 'beta'

export interface ReleaseNote {
  version: string
  date: string // YYYY-MM-DD
  summary?: string
  added?: string[]
  changed?: string[]
  fixed?: string[]
  removed?: string[]
}

export const RELEASES: ReleaseNote[] = [
  {
    version: '0.2.0-beta.210',
    date: '2026-10-26',
    summary: 'Mapa redimensionável, minimizável e destacável para outro ecrã.',
    added: [
      'Mapa: a janela muda de tamanho pela pega do canto inferior direito, e o mapa ocupa a altura que houver.',
      'Mapa: minimizar para uma barra e destacar para uma janela do browser, para o pôr noutro ecrã com o 3D no principal. Lá, o mapa arrasta-se e faz zoom como no painel; "Voltar ao painel" devolve-o ao sítio.',
      'Mapa: posição, tamanho e minimizado ficam guardados neste browser.',
    ],
    changed: [
      'Mapa e Pins BCF usam a mesma janela dentro do 3D (arrastar, redimensionar, minimizar, destacar). Os Pins não mudam de comportamento.',
    ],
  },
  {
    version: '0.2.0-beta.209',
    date: '2026-10-26',
    summary: 'Painel do Mapa sempre visível e com pega para o mover.',
    added: [
      'Mapa: o painel move-se arrastando pelo cabeçalho, sempre dentro da área do 3D. Se crescer ou a área encolher, volta a caber sozinho. Duplo clique no cabeçalho devolve-o ao canto.',
    ],
    fixed: [
      'Mapa: o painel deixa de ficar parcialmente escondido. Tem altura máxima e o conteúdo rola por dentro, por isso o cabeçalho e o botão de fechar ficam sempre à vista, mesmo com as Ferramentas e a federação abertas.',
    ],
  },
  {
    version: '0.2.0-beta.208',
    date: '2026-10-24',
    summary: 'Globo 3D: os modelos da federação pousam no relevo juntos, e a posição é a do edifício.',
    changed: [
      'Globo 3D: "Pousar no relevo" passa a um só comando para a federação. Desloca todos os modelos pelo mesmo valor, dado pelo modelo de base mais baixa, e diz quanto. Os modelos ficam alinhados entre si como no projeto.',
      'Globo 3D: o relevo usado é o mais baixo sob a planta do edifício, para o modelo assentar sem ficar no ar. O painel explica que o relevo Terrarium, com cerca de 30 m de resolução, pode incluir parte dos edifícios em cidade.',
    ],
    fixed: [
      'Globo 3D: com "Pousar no relevo" em cada modelo, cada um subia por um valor diferente e a federação desalinhava-se na vertical (5,0 a 8,6 m no Cha139).',
      'Globo 3D: a posição, o relevo e "Ir para o modelo" usam o centro do edifício, e não o do modelo inteiro, que inclui os marcadores de coordenação na origem e podia ficar dezenas de metros ao lado.',
    ],
  },
  {
    version: '0.2.0-beta.207',
    date: '2026-10-23',
    summary: 'Os modelos com marcadores de coordenação na origem deixam de aparecer no ar sobre o mapa e no Globo 3D.',
    fixed: [
      'Mapa no viewer (sem relevo): o chão fica à cota da maioria dos elementos, e não à do elemento mais baixo. Os marcadores de coordenação do Revit, à cota 0, punham o chão a 0 m e o edifício aparecia no ar à sua altitude real.',
      'Globo 3D: a base do modelo segue a mesma regra. A diferença para o relevo e o "Pousar no relevo" passam a medir-se a partir da base do edifício, e não dos marcadores, que deixavam o modelo dezenas de metros no ar.',
    ],
  },
  {
    version: '0.2.0-beta.206',
    date: '2026-10-23',
    summary: 'Selo LoGeoRef na lista de modelos. Correcção na verificação de entregas e README ao dia.',
    added: [
      'Lista de modelos: cada modelo mostra o seu nível LoGeoRef num selo, com a cor da pior verificação (verde certo, âmbar aviso, vermelho erro) e o resumo ao passar o rato. A lista completa das verificações continua no Info do modelo.',
    ],
    changed: [
      'README: o invariante dos ficheiros IFC passa a dizer o que o produto faz desde a beta.181: nada sai do browser sem partilha explícita, e o que se partilha fica no servidor com quota e reciclagem de 7 dias.',
    ],
    fixed: [
      'Entrega assinada: ao verificar um contentor que inclui o próprio termo, o termo deixou de aparecer como "fora da entrega".',
    ],
  },
  {
    version: '0.2.0-beta.205',
    date: '2026-10-22',
    summary: 'Tirar as coordenadas grandes dos IFC: a geometria vem para junto da origem do ficheiro e a deslocação passa para a georreferenciação.',
    added: [
      'Opção "Tirar as coordenadas grandes" no editor de posição (Gravar georref no IFC e Gravar todos). Quando o ficheiro tem as coordenadas de mapa na colocação do IfcSite, como o Revit exporta com coordenadas partilhadas, essa colocação passa para a origem e a deslocação soma-se aos Eastings/Northings da IfcMapConversion (ou do ePSet_MapConversion, em IFC2X3). Cada elemento fica no mesmo sítio do mapa e as cotas não mudam. Se houver geometria com coordenadas grandes fora dessa colocação, o ficheiro grava-se sem esta alteração e é indicado porquê.',
    ],
  },
  {
    version: '0.2.0-beta.204',
    date: '2026-10-22',
    summary: 'Estilo de apresentação: fundo em degradê e iluminação mais suave, para imagens e vídeos.',
    added: [
      'Botão "Apresentação" no separador Aparência: fundo em degradê, do cinzento-azulado em cima ao quase branco em baixo, e uma luz de hemisfério que distingue melhor as faces viradas para cima e para baixo. Destina-se a imagens e vídeos para terceiros; o fundo liso continua a ser o de trabalho. Liga o fundo claro, e escolher o fundo escuro desliga-o. A escolha fica guardada neste browser.',
    ],
  },
  {
    version: '0.2.0-beta.203',
    date: '2026-10-22',
    summary: 'Verificação da georreferência dos IFC, com o nível LoGeoRef e os erros mais comuns, e a georreferenciação dos IFC do Revit 2026 corrigida.',
    added: [
      'Verificação da georreferência (Info do modelo): o nível LoGeoRef do modelo (10 a 50, Clemen e Görne, 2019) e uma lista de verificações, com o que está certo, a conferir ou errado: sistema de coordenadas (EPSG desconhecido, geográfico ou Web Mercator), unidades do mapa (MapUnit em falta ou diferente da do sistema), Scale (em falta, invertido ou fora do factor de escala), origem (E = N = 0, ou fora da área de uso do sistema), norte verdadeiro contra a rotação da IfcMapConversion e a convergência de meridianos, latitude e longitude do IfcSite contra a posição da conversão, geometria longe da origem do ficheiro (aviso a 10 km, erro a 100 km) e cota. Segue o artigo "The Hidden Minefield of IFC Georeferencing" (T. Krijnen e J. Gevaert, 2026). Nada muda a posição: só diz o que encontrou.',
    ],
    changed: [
      'Quando o ficheiro se contradiz sobre a unidade do mapa (por exemplo, um IFC em milímetros sem MapUnit e com Scale 0,001), a verificação diz qual das leituras dá o tamanho real dentro da área de uso. A plataforma continua a não a aplicar sozinha: escolhe-se na leitura do mapa.',
    ],
    fixed: [
      'Georreferenciação de IFC exportados do Revit 2026: quando a unidade do projecto vinha depois dos primeiros 2 MB do ficheiro, a IfcMapConversion lia-se mas não se aplicava, e o modelo ficava na origem em vez de no sítio. Passa a ler-se o resto do ficheiro nesse caso.',
    ],
  },
  {
    version: '0.2.0-beta.202',
    date: '2026-10-21',
    summary: 'Entrega assinada: termo de entrega com a impressão digital de cada ficheiro, para assinar com o Cartão de Cidadão, e verificação de uma entrega recebida.',
    added: [
      'Entrega assinada (Ficheiro > Entrega assinada): termo de entrega em PDF que identifica cada ficheiro (IFC, PDF, BCF ou outro) pela impressão digital SHA-256, com projecto, entrega, emissor, destinatário e data, para assinar na aplicação Autenticação.gov com o Cartão de Cidadão ou a Chave Móvel Digital (assinatura electrónica qualificada) ou noutra ferramenta de assinatura de PDF. Os ficheiros não são alterados: continuam a abrir e a poder ser editados, e qualquer alteração passa a ser detectável.',
      'Verificar entrega: com o termo assinado (ou um contentor ASiC-E/ASiC-S) e os ficheiros recebidos (do disco ou abertos no viewer), indica quem assinou e quem emitiu o certificado, a data declarada, se o conteúdo assinado está intacto e, ficheiro a ficheiro, se confere, se chegou com outro nome, se é diferente do assinado ou se falta. Lê também o contentor ASiC-E que a aplicação Autenticação.gov cria ao assinar ficheiros que não são PDF (por exemplo um IFC junto com o termo), e o termo que vier lá dentro. A validade do certificado confirma-se na aplicação Autenticação.gov, em validador.autenticacao.gov.pt ou no validador da Comissão Europeia.',
    ],
  },
  {
    version: '0.2.0-beta.201',
    date: '2026-10-19',
    summary: 'IFC 4.3 completo e modelos muito grandes: o motor de leitura do IFC passa a ter as correcções que reportámos à That Open Company.',
    fixed: [
      'IFC 4.3: as peças varridas ao longo de uma curva (IfcSurfaceCurveSweptAreaSolid, IfcFixedReferenceSweptAreaSolid) passam a ter geometria. Acontecia em acessórios redondos de condutas (tês e curvas), apliques de iluminação e elementos curvos exportados do Revit em IFC 4.3. O Snowdon Architectural em IFC 4.3 abre com os 6216 elementos (eram 6204). Já não é preciso re-exportar em IFC 4.',
      'Modelos muito grandes que o motor não conseguia converter (uma estrutura de 78 MB com 85 mil elementos rebentava ao fim de mais de meia hora) passam a abrir; essa estrutura converte em menos de 3 minutos.',
    ],
    changed: [
      'Motor: o web-ifc passa a ser uma compilação nossa do ramo principal da That Open Company (commit dd44aa7, 19/10/2026), com as correcções dos dois problemas que reportámos (#2247 e #2248) e do erro da 0.0.78 no browser (#2219). É provisório: quando sair a 0.0.79 no npm, troca-se pela oficial. Os modelos guardados em cache voltam a converter-se na primeira abertura.',
    ],
    removed: [
      'Limitação "IFC 4.3: peças varridas ao longo de uma curva sem geometria": resolvida.',
    ],
  },
  {
    version: '0.2.0-beta.200',
    date: '2026-10-18',
    summary: 'Cortes com preenchimento e trama (diagonal em fundo branco por omissão, estilos por classe e material, iguais no 2D), e planos de corte com rotação em volante, esconder, travar e combinações com nome, guardados na sessão.',
    added: [
      'Preenchimento a cheio dos elementos cortados (Section box e Planos de corte, interruptor "Preenchimento a cheio" nos dois painéis, ligado por omissão): a secção de cada elemento aparece cheia, com o traço do corte, em vez de duas faces soltas. Vale para todos os cortes (caixa, planos soltos e os de um viewpoint BCF), elemento a elemento, por isso um elemento isolado também fica cheio, com os vãos como furos. Os elementos escondidos e em fantasma não se preenchem. Um elemento cuja malha não fecha fica só com as faces, e o painel diz quantos são. O preenchimento aparece um instante depois de o corte parar de mexer.',
      'Planos de corte: esconder o desenho de cada plano (olho) ou de todos, e o corte mantém-se.',
      'Planos de corte: travar um plano (cadeado). Travado, não se move, não roda e não se tira por engano; desenha-se a tracejado e sem pegas. "Tirar todos" deixa os travados.',
      'Planos de corte: combinações com nome. "Guardar os ligados" guarda os planos ligados agora; "Aplicar" liga os dessa combinação e desliga os outros.',
      'Planos de corte: botão para rodar 90° à volta da vertical.',
      'Estilo do corte (no painel da Section box e dos Planos de corte, por baixo do "Preenchimento a cheio"): por omissão, trama diagonal em fundo branco, com fundo cinza à escolha; outras tramas (diagonal inversa, cruzada, horizontal, vertical, pontos, betão, isolamento, cheio) e cores do fundo e do traço. A trama desenha-se como nos programas de CAD, com o mesmo espaçamento a qualquer distância.',
      'Regras do estilo do corte por classe IFC ("IfcWall" apanha também IfcWallStandardCase), por material (pedaços do nome, sem distinguir maiúsculas nem acentos; lê o material do elemento ou do tipo, incluindo camadas, perfis e constituintes) ou por lente (os elementos que alguma regra activa de uma lente guardada apanhe; gravar a lente actualiza o corte). A primeira regra que apanha um elemento decide; as regras sobem e descem. "+ Sugeridas" junta betão pontilhado, alvenaria e madeira a diagonal, isolamento em ondas, aço a cheio e vidro.',
      'Vistas 2D: as plantas e os cortes usam os mesmos estilos do corte que o 3D, na pré-visualização, no SVG e no PDF. Uma camada com cor de preenchimento escolhida à mão fica com ela. O DXF continua sem preenchimentos (o R12 não tem HATCH).',
      'Os planos de corte, as combinações e as regras do estilo vão na sessão: no ficheiro .vn2r-session, na sessão partilhada e na recuperação depois de fechar a aba. O estilo base fica guardado no browser, como preferência.',
    ],
    changed: [
      'Planos de corte: as pegas de rotação rodam o plano como um volante, à volta do centro, seguindo o rato no plano de rotação, com o círculo desenhado e o ângulo no rótulo enquanto se arrasta (Shift: de 5 em 5 graus). Antes, a conta seguia a normal no ecrã e encravava com o plano de frente para a câmara.',
      'Planos de corte: o nome de cada plano vê-se como campo editável (lápis e contorno ao passar o rato).',
    ],
  },
  {
    version: '0.2.0-beta.199',
    date: '2026-10-18',
    summary: 'Fantasma sem seleccionar: por modelo, por lente e como realce por omissão nos Conflitos e no BCF.',
    added: [
      'Fantasma por modelo: na lista de modelos abertos, o botão do fantasma ao lado de cada modelo deixa-o só com as arestas, sem ter de seleccionar nada. Os outros modelos ficam sólidos (por exemplo, a arquitectura em fantasma e as especialidades à vista). Seleccionar um elemento desse modelo põe-no sólido enquanto estiver seleccionado.',
      'Lentes: nova acção "Fantasma". Os elementos que cumprem a regra ficam só com as arestas, como no Ghost, e o resto fica como estava.',
      'Conflitos e BCF: controlos do realce por omissão, como as definições de visualização do Navisworks. Cores (A ciano, B magenta), Fantasma (o resto só com as arestas) e Caixa (caixa de corte à volta dos elementos) ligam-se e desligam-se ao lado do conflito ou do tópico, e a escolha fica guardada para os seguintes. Por omissão: Cores e Fantasma ligados, Caixa desligada.',
    ],
    changed: [
      'Conflitos: o botão "Realce A/B" dá lugar aos três controlos do realce (Cores, Fantasma, Caixa).',
      'BCF: no detalhe do tópico, "Limpar cores" dá lugar a "No Ver:" com os mesmos três controlos e "Limpar". Com Caixa ligada, o "Ver" de um tópico sem vista utilizável põe a caixa de corte sozinho, e o aviso deixa de oferecer a acção.',
    ],
    fixed: [
      'Fantasma por contornos com um modelo inteiro em fantasma: o modelo desaparecia, arestas incluídas. As faces deixam de ser escondidas pelo Fragments e passam a ser descartadas no desenho, por isso os elementos continuam visíveis para o resto da plataforma (contagem de escondidos, Mostrar tudo, viewpoints).',
    ],
  },
  {
    version: '0.2.0-beta.198',
    date: '2026-10-18',
    summary: 'Tudo o que está na barra lateral passa a ter lugar no ribbon.',
    added: [
      'Ribbon: os seis painéis que só tinham ícone na barra lateral ganham botão. Vista > Câmara: Planos de corte. Vista > Painéis (grupo novo): Estrutura e Propriedades. Aparência > Desenho: Lentes. Ficheiro > Modelo: IFC5. Ficheiro > Ferramentas: Documento. O botão e o ícone da barra abrem e fecham o mesmo painel e mostram-no aberto nos dois.',
    ],
  },
  {
    version: '0.2.0-beta.197',
    date: '2026-10-18',
    summary: 'Acompanhar numa sessão partilhada: ver o que o outro vê, sem salas à parte.',
    added: [
      'Acompanhar numa sessão partilhada: com mais alguém na sessão, ao lado do nome da sessão na barra de cima aparece "Acompanhar <nome>". A câmara e a selecção dessa pessoa passam para esta janela, com os modelos que a sessão já abriu. Mexer na câmara, ou a pessoa sair da sessão, deixa de acompanhar; "Parar" também. A vista passa pelo canal da sessão, que só aceita quem tem acesso à partilha, e o IFC não sai de cada browser.',
    ],
    removed: [
      'Sala de revisão: substituída por Acompanhar dentro da sessão partilhada. Obrigava quem entrava a abrir os modelos à mão, e o link da sala era a única credencial.',
    ],
  },
  {
    version: '0.2.0-beta.196',
    date: '2026-10-17',
    summary: 'Fantasma por contornos: o resto do modelo fica só com as arestas, e a selecção ou o conflito ficam à vista.',
    added: [
      'Ghost por contornos (Aparência > Ghost, novo estilo por omissão): os elementos que não estão seleccionados perdem as faces e ficam só com as arestas, esbatidas, e os seleccionados ficam sólidos. Vê-se o edifício inteiro sem tapar o que interessa, como num conflito dentro de uma parede. O estilo translúcido continua disponível no botão ao lado do Ghost.',
    ],
    changed: [
      'Conflitos e BCF: o realce dos elementos de um conflito usa o fantasma por contornos, e o "Ver" de um tópico importado com a vista do ficheiro passa também a pintar os elementos A/B e a pôr o resto em fantasma (antes ficavam misturados com o resto dentro da caixa de corte).',
      'Aviso de coordenadas grandes: diz "perto da origem do ficheiro (assinalada pelo marcador de coordenação)" em vez de "o ponto base do projecto", que se confundia com o Project Base Point do Revit.',
    ],
    fixed: [
      'O fantasma translúcido não deixava ver através dos elementos: a película cinzenta era desenhada por cima do material original, que continuava opaco.',
    ],
  },
  {
    version: '0.2.0-beta.195',
    date: '2026-10-16',
    summary: 'Reciclagem de 7 dias para os modelos e as sessões partilhadas apagados, e aviso de coordenadas grandes nos IFC.',
    added: [
      'Reciclagem: um modelo ou uma sessão partilhada apagados ficam 7 dias na Reciclagem (barra lateral, secção "No servidor"), de onde se restauram com um clique; só depois são apagados de vez. Uma sessão na Reciclagem deixa logo de dar acesso a quem a recebeu, e volta a dar ao ser restaurada.',
      'IFC com coordenadas grandes: ao abrir um modelo com a geometria a mais de 10 km da origem do ficheiro, a plataforma avisa que é má prática em qualquer versão do IFC e diz o que fazer (modelar perto da origem e declarar a deslocação na IfcMapConversion). O mesmo aviso fica em Informações do modelo, CRS / Origem.',
    ],
  },
  {
    version: '0.2.0-beta.194',
    date: '2026-10-16',
    summary: 'O filtro dos pins diz o que a lista do BCF está a filtrar, e abre os filtros ao clicar.',
    changed: [
      'Pins BCF: a linha "A filtrar" diz o que a lista do módulo BCF está a mostrar (pesquisa, estado, prioridade, responsável, etiquetas, prazo, grupos abertos), em vez de só "o que a lista do módulo BCF mostra". Clicar nela abre o painel dos pins para mudar os filtros.',
    ],
  },
  {
    version: '0.2.0-beta.193',
    date: '2026-10-16',
    summary: 'Descrições dos tópicos de conflitos reduzidas ao que fazer, e nada passa por cima do painel do BCF acoplado em baixo.',
    changed: [
      'Conflitos, BCF: a descrição de cada tópico diz só o que fazer (a recomendação), mais as notas. O grupo, o responsável, a quem comunicar, o prazo, o estado e a regra já estão nos campos e nas etiquetas do tópico e deixam de se repetir; as marcas internas da plataforma ("por verificar", profundidade estimada) não saem no BCF.',
      'Módulo BCF, agrupar por grupo: o grupo tem o nome do primeiro tópico (os BCF antigos mantêm o título que traziam na descrição).',
    ],
    fixed: [
      'Com um módulo acoplado em baixo e alto, a barra lateral e o cubo de vistas transbordavam por cima dele; ficam dentro da sua área, com barra de deslocação.',
    ],
  },
  {
    version: '0.2.0-beta.192',
    date: '2026-10-16',
    summary: 'A barra lateral separa o que está aberto do que está guardado no servidor, e cada pessoa só sabe do servidor o que lhe diz respeito.',
    changed: [
      'Barra lateral: "Abertos" é o que está carregado agora; por baixo, numa secção "No servidor", ficam os modelos guardados, as sessões partilhadas e o que foi partilhado consigo.',
      'Espaço no servidor: o total ocupado no workspace só o vê a administração; cada pessoa vê quanto ocupam os ficheiros que ela própria guardou. A mensagem de falta de espaço deixa de dizer quanto o workspace tem ocupado a quem não administra.',
    ],
  },
  {
    version: '0.2.0-beta.191',
    date: '2026-10-16',
    summary: 'A barra de cima só diz que se está numa sessão partilhada quando ela está mesmo aberta.',
    fixed: [
      'Sessão partilhada: sem nenhum modelo aberto, a barra de cima continuava a mostrar "CHA139 · só você" (a marca ficava gravada no browser depois de recarregar a página ou de fechar os modelos um a um). A marca passa a apagar-se quando nada da sessão volta ao arrancar ou quando se fecha tudo, e o indicador e o menu Sessão só a mostram com a sessão aberta.',
    ],
  },
  {
    version: '0.2.0-beta.190',
    date: '2026-10-16',
    summary: 'As ligações ao vivo recusadas (sem sessão, sem acesso) dizem logo porquê, em vez de ficarem 20 segundos à espera e voltarem a tentar.',
    fixed: [
      'Ligações ao vivo (BCF, sessão partilhada, triagem dos conflitos, sala de revisão): em produção, quando o servidor recusava a ligação, o browser não recebia o motivo, ficava 20 segundos à espera e depois voltava a tentar sem fim. O servidor passa a dizer o motivo numa mensagem antes de fechar, e o browser pára e mostra-o.',
    ],
  },
  {
    version: '0.2.0-beta.189',
    date: '2026-10-16',
    summary: 'O ecrã de arranque diz quando o servidor está a acordar, em vez de ficar só em "A carregar…".',
    changed: [
      'Arranque: quando o servidor estava a dormir, a primeira entrada podia ficar perto de um minuto em "A carregar…", sem explicação. Ao fim de três segundos o ecrã passa a dizer que o servidor está a acordar, que pode demorar até um minuto e que não é preciso recarregar, e conta o tempo.',
    ],
  },
  {
    version: '0.2.0-beta.188',
    date: '2026-10-16',
    summary: 'Painel dos conflitos em três passos: Detetar, Rever e Entregar BCF, com a lista logo à vista.',
    changed: [
      'Conflitos: o painel passa a ter três separadores. Detetar tem os conjuntos, a configuração, as regras de classificação e de exclusão e o resumo da corrida; Rever mostra a lista logo a seguir aos filtros; Entregar BCF junta tudo o que vai para o BCF (criar ou atualizar, exportar, opções avançadas).',
      'Conflitos: detetar já classifica e agrupa sozinho (pode desligar-se em "Classificar e agrupar ao detetar"); os pareceres manuais e de proposta ficam como estavam. Depois de uma corrida, o painel abre em Rever.',
      'Conflitos: na lista por grupos, cada grupo diz o que tem no BCF do módulo ("tópico aberto", "resolvido", "sem tópico"), e o separador Entregar mostra quantas alterações há por levar ao BCF, com "Abrir no módulo BCF".',
      'Conflitos: o convite para partilhar a triagem que uma sessão traz fica numa linha, e o formulário só abre ao carregar em "Partilhar".',
    ],
  },
  {
    version: '0.2.0-beta.187',
    date: '2026-10-16',
    summary: 'Ver quem está na sessão partilhada; convidar sem esperar pelo email; janelas que já não ficam por trás umas das outras.',
    added: [
      'Sessão partilhada: na barra de cima aparece o nome da sessão e quem mais a tem aberta agora ("Cha139 · com Vítor"), com um ponto verde quando a ligação está viva. Clicar abre a partilha.',
      'Partilhar: depois de dar acesso, a janela mostra a ligação de entrada de cada pessoa, para copiar e mandar logo por outro meio. Quem não tem conta entra por ela sem conta nem palavra-passe; num convite pendente, "Ligação" gera outra sem mandar email.',
    ],
    changed: [
      'Um convite para uma sessão abre logo a sessão, em vez de "Partilhados comigo".',
      'Os painéis dentro da vista 3D (conflitos, pins, IDS, diferenças, mapa, histórico, nuvem de pontos) são opacos: o que está por trás já não se vê através deles.',
    ],
    fixed: [
      'Janelas: clicar numa janela flutuante traz sempre essa janela para a frente, seja de que tipo for, e as janelas de diálogo (partilhar, recuperar a sessão) e os menus abrem sempre por cima de todas.',
    ],
  },
  {
    version: '0.2.0-beta.186',
    date: '2026-10-15',
    summary: 'Os tópicos criados por "Criar BCF" nos conflitos mantêm o mesmo Guid de uma exportação para a seguinte.',
    fixed: [
      'Conflitos, "Criar BCF" no módulo: cada tópico recebia um Guid novo em cada exportação, ao contrário do que já acontecia ao gravar o .bcfzip directamente. Agora o Guid deriva do projecto BCF e dos GlobalId dos dois elementos, e voltar a exportar actualiza os mesmos tópicos noutras ferramentas em vez de os duplicar.',
    ],
  },
  {
    version: '0.2.0-beta.185',
    date: '2026-10-15',
    summary: 'Conflitos com prioridades mais realistas e editáveis por regra; sessões partilhadas que já não ficam presas a abrir; os pins dizem como estão filtrados.',
    added: [
      'Conflitos: a prioridade de cada regra muda-se no painel "Regras de classificação"; "Classificar automaticamente" aplica-a a todos os conflitos sem parecer manual.',
      'Pins BCF: ao lado do botão dos pins, quando há filtros, uma linha diz como se está a filtrar ("A filtrar: Prioridade: Crítica"), com "Limpar".',
    ],
    changed: [
      'Conflitos, prioridades por omissão: tubagem a atravessar laje é falta de negativa ou courette (Média, ou Baixa com Ø pequeno); atravessar parede ARQ resolve-se com abertura ou roço (Baixa, salvo paredes espessas); cruzamentos entre redes com menos de 50 mm são pequenos ajustes (Baixa). Só a viga ou o pilar da estrutura atravessados ficam Críticos. No Cha139: 66 críticos passam a 40 e 86 altos a 30.',
      'Conflitos: os marcadores de coordenação (triângulo de coordenadas partilhadas, marcador de origem da federação) ficam fora do âmbito.',
      'Pins BCF: as prioridades aparecem em português (Crítica, Alta, Média, Baixa).',
    ],
    fixed: [
      'Abrir uma sessão partilhada já não fica presa em "a abrir…" quando se carrega em "Reabrir" enquanto a sessão repõe os modelos: os modelos abrem um de cada vez, o mesmo modelo nunca abre duas vezes, e um passo do motor 3D sem resposta deixa de prender a abertura.',
    ],
  },
  {
    version: '0.2.0-beta.184',
    date: '2026-10-15',
    summary: 'Tópicos BCF dos conflitos com uma descrição curta, escrita para quem resolve.',
    changed: [
      'BCF dos conflitos: a descrição diz o grupo, o que acontece e onde, quem resolve e a quem comunicar, o que fazer, o estado, as notas e o código da regra. Os GlobalId, a câmara e o ponto continuam no ponto de vista do tópico (é por aí que as outras ferramentas seleccionam os elementos); a explicação da regra e do agrupamento fica na plataforma.',
    ],
  },
  {
    version: '0.2.0-beta.183',
    date: '2026-10-15',
    summary: 'Google Earth com todos os modelos num só KMZ e à altura certa; gravar a georreferenciação com datum vertical, latitude/longitude do IfcSite e em todos os modelos de uma vez.',
    added: [
      'Exportar Google Earth (KMZ): "Todos os abertos" põe os modelos num só KMZ, um por camada; escolha da altitude: "Assente no terreno" (por omissão) ou "Do IFC (absoluta)", para modelos com datum vertical.',
      'Gravar georref no IFC: campo "Datum vertical" (por exemplo EPSG:5780, Cascais), "Corrigir latitude/longitude do IfcSite" (pela origem do sítio no mapa) e "Gravar todos" (um .zip com os _georef.ifc de todos os modelos abertos).',
    ],
    fixed: [
      'KMZ assente no terreno: a cota pousada no chão é a da maioria dos elementos (pode escrever-se outra); os marcadores de coordenação na origem, à cota 0, punham o edifício dezenas de metros no ar.',
      'Gravar georref no IFC com o mesmo CRS mantém a descrição e o datum geodésico que o ficheiro já tinha (saíam em branco).',
    ],
  },
  {
    version: '0.2.0-beta.182',
    date: '2026-10-15',
    summary: 'Sessões partilhadas: peças de outras pessoas com autorização do dono, e apagar uma sessão.',
    added: [
      'Partilhar sessão com uma peça de outra pessoa (por exemplo, um BCF que um colega registou primeiro): a peça entra na sessão à espera de autorização, o dono recebe um aviso (sino e email) e autoriza ou recusa em "Pedidos de autorização", vendo a quem dá acesso. Até autorizar, quem recebe a sessão é avisado de que essa peça espera pelo dono.',
      'Sessões partilhadas (barra lateral, por baixo dos modelos do workspace): as sessões que vê, com abrir, gerir a partilha, o espaço que os pontos de vista ocupam e "Apagar", que tira os acessos que a sessão deu e liberta esse espaço (os modelos, a triagem e os BCF ficam como estavam).',
    ],
    fixed: [
      'Uma peça sem nome (um BCF registado sem nome) aparece com o nome que tem na sessão, em vez de "sem nome".',
    ],
  },
  {
    version: '0.2.0-beta.181',
    date: '2026-10-15',
    summary: 'Partilhar a sessão de uma vez: modelos, triagem dos conflitos, BCF e pontos de vista, com uma só lista de pessoas.',
    added: [
      'Partilhar sessão (menu Sessão): partilha de uma vez os modelos abertos, a triagem dos conflitos, os BCF, os pontos de vista (com as miniaturas) e as vistas 2D. Quem recebe tem um só aviso (sino e email) e abre tudo pela ligação; "Gerir partilha da sessão" acrescenta ou tira pessoas de todas as peças ao mesmo tempo, e "Retirar a partilha" tira tudo o que veio da sessão sem mexer no que cada peça já partilhava por si.',
      'Modelos: "Gerir partilha" em cada modelo partilhado, e o "partilhado · rev. N" abre a mesma janela.',
    ],
    changed: [
      'Os pontos de vista de uma sessão partilhada vão num pacote comprimido, em blocos, e contam para o espaço do workspace (os 1191 do Cha139 ocupam cerca de 30 MB).',
    ],
    fixed: [
      'Abrir uma sessão partilhada com trabalho anterior por recuperar: a sessão espera pela resposta a "Recuperar a sessão anterior?" (antes abria por baixo, e a triagem ficava parada).',
      'BCF partilhado: um BCF aberto de novo (depois de fechar a sessão) deixa de ficar vazio; os tópicos vêm logo ao abrir a sessão, com os pins.',
    ],
  },
  {
    version: '0.2.0-beta.180',
    date: '2026-10-15',
    summary: 'Partilhar com pessoas escolhidas (e convidados), notificações na plataforma e por email, e um funil para gerir muitos conflitos.',
    added: [
      'Partilhar com…: modelos, BCF e triagens dos conflitos partilham-se com pessoas escolhidas (de qualquer workspace) e/ou com o workspace inteiro; "Visível para" sempre à vista; "Remover" por pessoa e "Retirar a partilha", com efeito imediato (incluindo as ligações ao vivo). Por omissão, o que se cria fica só para quem o criou até escolher.',
      'Convidados: quem não tem conta recebe um convite por email e entra numa página "Partilhados comigo", só com o que lhe partilharam.',
      'Partilhados comigo: o que outras pessoas partilharam consigo a partir de outros workspaces, no viewer, no BCF e nos conflitos.',
      'Notificações: sino na barra de topo e email (resumo diário por omissão; imediato, semanal ou nunca, por tipo) para atribuições, comentários e @menções, estados e prazos, novas corridas e partilhas; mapa "especialidade -> pessoas" por projeto; ligação para deixar de receber em cada email.',
      'Conflitos, gerir muitos: filtro à entrada (folga só entre pares escolhidos, penetração mínima), regras de exclusão sugeridas e reversíveis ("Ignorados por regra"), lista por grupos com ações em lote e filtro "Só para reunião", e "Criar tópico para o modelador" para os casos de modelação.',
      'Barra lateral: a lista dos modelos dobra-se e muda de altura.',
    ],
    changed: [
      'Os administradores VN2R só veem modelos, BCF e triagens que lhes forem partilhados; continuam a gerir contas, workspaces, módulos e auditoria.',
      'Os emails dos comentários e atribuições do BCF passam a seguir as preferências de notificação (resumo diário por omissão) em vez de saírem um a um.',
    ],
    fixed: [
      'Viewpoints: os importados de BCF aparecem com "Mostrar só viewpoints do modelo ativo" (num grupo "Importados de BCF").',
    ],
  },
  {
    version: '0.2.0-beta.179',
    date: '2026-10-15',
    summary: 'Trabalho em simultâneo: BCF ao vivo, triagem dos conflitos partilhada no workspace e modelos IFC partilhados (opcional, por ficheiro).',
    added: [
      'BCF ao vivo: com o BCF partilhado no workspace, as alterações (estado, prioridade, responsável, etiquetas, comentários, tópicos novos) chegam aos outros em segundos, sem carregar em "Sincronizar"; mostra quem está no mesmo BCF; o tópico fica só de leitura para os outros enquanto alguém o edita (os comentários continuam abertos). Sem ligação continua a funcionar e envia ao voltar.',
      'BCF: a primeira sincronização de um BCF importado do mesmo ficheiro deixa de dar conflitos falsos (a versão do ficheiro serve de base comum).',
      'Triagem dos conflitos partilhada: "Partilhar a triagem neste workspace" e "Triagens partilhadas" no painel dos conflitos. Estados, prioridades, responsáveis, notas, grupos, classificação e configuração ficam no workspace e atualizam-se ao vivo para todos; os conflitos casam-se pelos GlobalIds dos elementos, e a deteção continua a correr em cada computador.',
      'Modelos partilhados (módulo "Modelos partilhados", a ativar no workspace): "Partilhar no workspace" em cada modelo guarda o IFC comprimido na plataforma para os colegas o abrirem; revisões numeradas, aviso de revisão nova, espaço ocupado à vista e limite por workspace. Opcional por ficheiro: quem não partilha continua a trabalhar só com os seus ficheiros.',
      'Sessão leve: a sessão pode referenciar os modelos partilhados em vez de os levar dentro.',
    ],
  },
  {
    version: '0.2.0-beta.178',
    date: '2026-10-15',
    summary: 'Sala de revisão pronta para sessões a várias pessoas, e clicar num pin abre o tópico BCF.',
    changed: [
      'Pins BCF: clicar num pin (ou escolher na lista de uma bolha) aplica o viewpoint e abre o tópico no módulo BCF.',
      'Sala de revisão: cada pessoa aparece com o nome da sua conta (editável antes de entrar).',
    ],
    fixed: [
      'Sala de revisão: o link do convite abre a sala com o botão "Entrar na sala do convite"; antes só funcionava com o painel já aberto.',
      'Sala de revisão: quem conduz a visita passa a enviar a câmara (não ficava a saber que conduzia, porque o servidor não lhe devolve a própria mensagem).',
      'Sala de revisão: a seleção deixa de ser reenviada em ricochete entre participantes (cerca de 30 mensagens por segundo com duas pessoas).',
    ],
  },
  {
    version: '0.2.0-beta.177',
    date: '2026-10-15',
    summary: 'O "Ver" dos viewpoints importados leva a câmara e os cortes ao sítio certo; filtros dos pins e da lista BCF por facetas; painel dos pins em janela móvel e destacável; detalhe dos tópicos dentro do módulo destacado.',
    changed: [
      'Filtros dos pins e da lista do módulo BCF por facetas: dentro de cada faceta basta um dos valores (OU), entre facetas têm de passar todas (E). As etiquetas dividem-se em Especialidade, Nível, Grupo e Outras; marcar ARQ e EST mostra os de ARQ ou EST, e MAC com Nível P2 mostra os de MAC no P2.',
      'Painel dos pins: estado e origem funcionam como as outras facetas ("só estes"); contagens que já têm em conta os outros filtros; quando nada passa, o painel diz quantos pins passariam sem cada filtro; "Filtros (N)" conta facetas.',
      'O painel dos pins é uma janela móvel, redimensionável, minimizável e destacável para uma janela do browser, com posição e tamanho guardados.',
      '"Ver" de um viewpoint importado: com a câmara do ficheiro perto dos modelos abertos, aplica a câmara e os cortes (seis planos em caixa passam a caixa de corte) e selecciona os elementos; sem câmara utilizável, selecciona e pinta os elementos, põe o resto em fantasma, enquadra-os e oferece a caixa de corte à volta.',
    ],
    fixed: [
      'Viewpoints de BCF importados com a câmara a cerca de 150 km do edifício: a leitura usava o referencial do modelo activo (o EST, com IfcMapConversion) em vez do referencial comum da federação usado na exportação. Os viewpoints já importados passam a funcionar sem reimportar.',
      'Exportação do módulo BCF: as coordenadas usam o mesmo referencial comum da exportação dos conflitos, e não o do primeiro modelo aberto.',
      'Com o módulo BCF destacado numa janela do browser, o detalhe do tópico abria fora da janela; passa a abrir dentro dela, e arrastar e redimensionar funcionam nessa janela.',
    ],
  },
  {
    version: '0.2.0-beta.176',
    date: '2026-10-15',
    summary: 'Escolha dos tópicos BCF dos grupos visível, com o número de tópicos de cada opção, e pergunta "versão nova ou BCF separado" ao importar um BCF do mesmo projeto.',
    changed: [
      'Conflitos: a escolha entre "Um tópico por conflito, com o grupo indicado" e "Um tópico por grupo" passa a estar visível no painel de exportação, com o número de tópicos que sai em cada opção.',
      'Módulo BCF: importar (botão ou arrastar) um BCF do mesmo projeto de um que já está na biblioteca pergunta se entra como versão nova ou fica como BCF separado, com o número de tópicos de cada um; o separado recebe um nome distinto ("Conflitos (2)").',
    ],
  },
  {
    version: '0.2.0-beta.175',
    date: '2026-10-15',
    summary: 'Pins BCF legíveis (bolhas, oclusão, corte e filtros), arrastar BCF para o 3D, caixa de corte que se pode esconder e seleção que ignora a parte cortada, e painéis que abrem numa janela do browser.',
    added: [
      'Pins BCF: os pins próximos no ecrã juntam-se numa bolha com o número de tópicos e a cor da prioridade mais alta; clicar aproxima a câmara ou, se já não der para separar, mostra a lista.',
      'Pins BCF: os pins tapados pela geometria ficam esbatidos e por baixo dos visíveis; com a caixa de corte ativa, os pins fora do volume visível escondem-se (ou esbatem-se, por opção).',
      'Pins BCF: filtros no painel por pesquisa, prioridade, estado, grupo, etiqueta, responsável e origem, com a contagem "N de M pins"; botão "Pins (N)" junto ao comutador 3D/2D.',
      'Arrastar um ficheiro .bcfzip ou .bcf para o 3D importa-o para o módulo BCF (versão nova quando o projeto BCF já existe), com aviso e botão "Abrir".',
      'Caixa de corte: opção "Mostrar caixa" (Shift+X) para esconder arestas, faces e pegas mantendo o corte; pegas mais discretas.',
      'Abrir numa janela do browser: detalhe de cada tópico BCF, caixa de corte, planos de corte, Dados IFC e Colorir por critério, além das propriedades, viewpoints e vistas 2D; as ações sobre o 3D continuam a funcionar a partir da janela destacada.',
    ],
    fixed: [
      'Com a caixa de corte ou planos de corte ativos, a pré-seleção e a seleção ignoram a parte cortada dos elementos: o raio passa a ir até ao primeiro elemento visível. O mesmo vale para a seleção por retângulo, o snap das medições e "Alinhar à face".',
    ],
  },
  {
    version: '0.2.0-beta.174',
    date: '2026-10-15',
    summary: 'BCF dos conflitos com um tópico por conflito e o grupo indicado; a lista de tópicos do módulo BCF passa a poder ser agrupada.',
    added: [
      'Exportação BCF dos conflitos: por omissão, um tópico por conflito, cada um com o seu viewpoint e a sua imagem. O grupo segue em cada tópico como etiqueta "Grupo <id>", como ligação aos restantes tópicos do mesmo grupo (RelatedTopics) e na primeira linha da descrição. A opção "um tópico por grupo" mantém-se disponível.',
      'Título de cada tópico de conflito com o problema, os dois elementos (nome IFC), o nível e a medida (profundidade ou folga).',
      'Módulo BCF: "Agrupar por" grupo, prioridade, estado, responsável, etiqueta ou tipo, com blocos dobráveis (número de tópicos, abertos e prioridade mais alta). O agrupamento por grupo usa a etiqueta "Grupo" ou, nos BCF de outras plataformas, as ligações entre tópicos.',
      'Módulo BCF: em cada bloco, "Ver no 3D" seleciona e realça os elementos ligados de todos os tópicos do bloco, e "Caixa de corte" coloca a caixa à volta deles.',
      'Módulo BCF: as ligações entre tópicos (RelatedTopics) são lidas e mantidas ao importar, exportar e sincronizar, em BCF 2.1 e 3.0.',
      'Módulo BCF: "Ver no 3D" e "Caixa de corte" pedem confirmação quando o bloco junta mais de 300 elementos.',
      'Pins BCF para os tópicos criados pelos conflitos (no ponto da interferência) e para BCF importados de qualquer plataforma (no centro dos elementos do viewpoint ou, sem elementos, à frente da câmara). A opção "Seguir os filtros do módulo BCF" mostra só os pins dos tópicos visíveis na lista e dos blocos abertos.',
      'Conflitos: depois de repetir a deteção, "Acrescentar os novos ao BCF" cria no módulo os tópicos dos conflitos novos, cada um com o seu grupo e ligado aos tópicos desse grupo que já existem.',
      'Exportação para ficheiro: opção "Só o que mudou desde a última exportação" (conflitos novos e alterações de estado), com os mesmos identificadores de tópico, para atualizar os tópicos já existentes noutras plataformas. O registo da última exportação fica na sessão.',
    ],
    fixed: [
      'Conflitos: quando uma revisão nova do IFC muda as chaves dos conflitos, os tópicos já enviados para o módulo BCF continuam a seguir os seus conflitos.',
      'Conflitos: a opção "Incluir imagens" passa a indicar o formato correto das imagens (JPEG).',
    ],
  },
  {
    version: '0.2.0-beta.173',
    date: '2026-10-15',
    summary: 'Classificação e agrupamento automáticos dos conflitos, caixa de corte numa janela móvel e o modelo de origem de cada elemento.',
    added: [
      'Conflitos: classificação automática por regras (problema, especialidade responsável, a quem comunicar, prioridade e recomendação), com a regra aplicada indicada em cada parecer; as alterações manuais prevalecem sobre a classificação automática.',
      'Conflitos: métodos de agrupamento automático por sítio físico (interferências próximas com o mesmo problema), por elemento, por família, por nível, ou misto (Crítica, Alta e Média por sítio; Baixa por família, como lista para validação). Os grupos mantêm o identificador quando a maioria dos pares se mantém entre corridas.',
      'A classificação, as regras e os grupos ficam guardados na sessão (.vn2r-session) e seguem para a exportação BCF.',
      'Caixa de corte: painel numa janela que se pode mover e minimizar, com os limites em E, N e Cota.',
      'Modelo de origem de cada elemento (sigla e nome do ficheiro) nas propriedades, ao passar o rato, na seleção múltipla, nos elementos ligados do BCF e na barra lateral.',
    ],
    fixed: [
      'O trabalho feito nos conflitos com o aviso de recuperar sessão por responder fica guardado à parte e junta-se à triagem depois da decisão, sem a substituir.',
      'BCF: as etiquetas do tópico deixam de repetir o piso quando o nível já está indicado.',
    ],
  },
  {
    version: '0.2.0-beta.172',
    date: '2026-10-14',
    summary: 'Coordenação de conflitos de ponta a ponta: triagem com prioridade e responsável, BCF com imagens e viewpoints, reconciliação ao repetir a corrida e conflitos guardados na sessão.',
    added: [
      'Conflitos: triagem de coordenação com prioridade (Crítica, Alta, Média, Baixa), especialidade responsável, a quem comunicar, recomendação de resolução, prazo e etiquetas; edição em lote por seleção ou por grupo.',
      'Conflitos: importar uma proposta de triagem em JSON (por conflito ou por grupo, casada pelos GlobalIds), com o número de conflitos triados, por triar e sem correspondência.',
      'Exportação BCF (2.1 ou 3.0) com prioridade, responsável, prazo, estado, tipo, etiquetas e recomendação na descrição; extensions.xml com as listas usadas; um tópico por conflito ou por grupo; opção de exportar só os conflitos com triagem.',
      'Cada tópico leva viewpoint completo (câmara, elementos selecionados e coloridos A/B, planos de corte à volta da zona) e imagem JPEG 16:9 com a zona isolada; a vista do utilizador é reposta no fim.',
      'Coordenadas BCF no referencial comum da federação (um modelo sem IfcMapConversion, ou o CRS), escolhido na exportação e independente do modelo ativo.',
      'Repetir a corrida reconcilia a triagem: o que deixou de ocorrer passa a resolvido, o que volta passa a reaberto, o que não é comparável fica por verificar; grupos mostram "x de y resolvidos"; resumo depois de cada corrida. Os tópicos BCF têm identificadores estáveis, por isso uma nova exportação atualiza os mesmos tópicos noutras plataformas.',
      'A sessão (.vn2r-session) passa a guardar os conjuntos, a configuração, a triagem e o histórico das corridas da deteção de conflitos.',
      'BCF: secção "Elementos ligados" no tópico, com GlobalId, classe e nome de cada elemento, seleção individual ou em conjunto, cores distintas e caixa de corte à volta.',
    ],
    fixed: [
      'Repetir a deteção com o aviso de recuperar sessão por responder já não apaga a triagem gravada; uma gravação que deixasse menos de metade dos pareceres pede confirmação.',
      'Aviso de recuperar sessão: a lista fica como estava quando abriu, e o Escape adia a decisão.',
      'Painel de conflitos: o aviso "os conjuntos partilham o mesmo modelo" só aparece quando os dois conjuntos são o mesmo modelo único.',
    ],
  },
  {
    version: '0.2.0-beta.171',
    date: '2026-10-14',
    summary: 'Ir para uma origem: a câmara aproxima-se, só essa origem fica à vista e a etiqueta diz as coordenadas.',
    added: [
      'Info do modelo > Origens da hierarquia espacial: botão "ir" na origem do ficheiro IFC (sempre disponível) e em cada IfcSite, IfcBuilding, IfcBuildingStorey e IfcSpace. A câmara centra-se nessa origem, o marcador mostra só essa e a etiqueta passa a dizer as coordenadas no ficheiro IFC e, com georreferenciação, no CRS (E, N, Cota). Um aviso no topo do 3D diz qual está isolada, com Centrar e Mostrar todas.',
    ],
  },
  {
    version: '0.2.0-beta.170',
    date: '2026-10-10',
    summary: 'Vários BCF sincronizados no mesmo workspace, e as migrações da base de dados voltam a ficar gravadas.',
    added: [
      'BCF partilhado: cada BCF sincroniza à parte, identificado pelo ProjectId do próprio ficheiro. Dois BCF no mesmo workspace deixam de se misturar e de se bloquear um ao outro. Quem importar o mesmo .bcfzip sincroniza com os mesmos tópicos; a exportação passa a gravar sempre o project.bcfp, para isso ser possível. A sincronização que já existia passa para o BCF certo sem perder estado.',
    ],
    fixed: [
      'Servidor: desde a beta.113 as migrações da base de dados corriam e eram desfeitas no fim, sem erro (o trinco contra execuções simultâneas deixava a transacção por confirmar). Ficam agora gravadas; ensaiado num Postgres de 0017 até 0022.',
    ],
  },
  {
    version: '0.2.0-beta.169',
    date: '2026-10-10',
    summary: 'Revisão geral por oito agentes, área a área: cerca de cem defeitos encontrados e corrigidos, cada um com teste de regressão.',
    fixed: [
      'Abrir modelos: mudar entre o layout de telemóvel e o de desktop já não esvazia a cena; dois IFC com o mesmo nome deixam de criar um modelo fantasma (o segundo fica "Nome (2).ifc"); uma falha a abrir deixa de ser anunciada como sucesso; Substituir só descarta os modelos antigos depois de o novo abrir; as aberturas ficam em fila; Recuperar já não reabre modelos substituídos ou removidos.',
      'Nuvens de pontos com coordenadas grandes (UTM, PT-TM06) lidas em precisão dupla com origem local: acabam os degraus de centímetros. As arestas libertam a memória da placa gráfica ao fechar.',
      'Contas: revogar um membro corta-lhe o acesso de imediato, e a data "Expira" do workspace passa a aplicar-se. Pedidos de link de entrada limitados, link gasto ou inválido leva a uma página com "Enviar novo link", e um servidor em erro mostra um aviso em vez de mudar para modo local sem dizer nada.',
      'Atalhos: Escape fecha menus, visita guiada e gavetas; Tab volta a navegar pelo teclado; letras num campo de escolha já não disparam atalhos; a tecla ? abre a lista de atalhos. Desfazer e refazer seguem a ordem das acções e esquecem o modelo que se fecha.',
      'BCF: importar um BCF 3.0 traz os comentários e os viewpoints; câmara e cortes exportados no referencial do IFC (Z para cima), legíveis no BIMcollab, Solibri e Revit; importar e reexportar mantém câmaras, cortes e visibilidade; a sincronização envia só o que mudou, em lotes, não reverte alterações de colegas, grava no browser o que recebe e respeita comentários apagados.',
      'Vistas 2D: a cota do piso é o Elevation declarado no IFC; aviso quando o desenho não cabe na folha; PDF, SVG e DXF com a mesma escala; Regenerar mantém a rotação; DXF com acentos legíveis e unidade declarada. Fechar a sessão tira os planos de corte.',
      'Coordenadas: com georreferenciação mostram-se Este, Norte e Cota, com o Norte positivo.',
      'Conflitos: a tolerância vale também para peças oblíquas; a linha de visão usa o raio certo; a triagem mantém-se ao trocar os conjuntos A e B. O Filtro vê todos os elementos (não só os primeiros 10 000) e as quantidades Qto. Lentes não apagam as cores das outras camadas.',
      'Georreferenciação: CRS com eixos em pés, IfcSite com latitude e longitude sem IfcMapConversion, unidades por conversão (pés, polegadas) e nomes de CRS compostos ou em URN. Sem rede, a verificação de elevação diz que não tem dados em vez de propor enterrar o modelo. Índice EPSG conferido contra o PROJ; os CRS que o motor não sabe converter ficam assinalados.',
      'Dados: a Tabela traz Psets, Qtos e GlobalId; o BoQ escreve áreas e volumes nas unidades do projecto; a Elevation do piso mostra o valor do ficheiro; a árvore espacial segue a contenção e mostra as referências à parte; IfcZone conta os seus membros; números importados do Excel sem 17 algarismos; exportação principal em .xlsx.',
      'Edição de IFC: textos com acentos, símbolos e barras gravam-se correctamente; editar ou apagar valores de Psets já não gera IFC inválido.',
      'IDS: texto compara-se como texto ("01" não é "1"); faceta proibida com valor só reprova quando o valor corresponde; padrões xs:pattern com classes Unicode. Suite oficial: 334 de 334.',
      'Juntar IFC: opção própria para reparar GlobalIds repetidos, com aviso visível.',
      'Assistente: pedidos à API certa em produção; uma chave própria vai sempre para o endereço oficial do fornecedor.',
      'Da revisão feita em paralelo (PR #8): ficheiros vazios ou que não são IFC recusados à cabeça, com a razão; o handoff do FireBIM só é aceite do próprio computador ou da própria origem; uma miniatura estragada não interrompe a reposição dos viewpoints; o nome do workspace e o link vão escapados no email de entrada; o relé da sala de revisão não reenvia mensagens que fingem ser dele; o nome de uma especificação IDS só tem de ser único entre as activas (apagar liberta o nome).',
    ],
  },
  {
    version: '0.2.0-beta.168',
    date: '2026-10-07',
    summary: 'Motor do ThatOpen actualizado (fragments 3.4.8), com a configuração do importador corrigida: as caixas de ±100000 eram nossas.',
    changed: [
      'Motor: @thatopen/fragments 3.4.8, components 3.4.9, components-front ' +
        '3.4.5. O web-ifc fica fixo em 0.0.77: a 0.0.78 não abre IFC no ' +
        'browser (BindingError no StreamMeshes, corrigido no main do ' +
        'web-ifc mas ainda sem versão).',
      'Configuração do importador corrigida. Desde maio baixávamos todas as ' +
        'GeometryProcessSettings para 1e-5, julgando que eram tolerâncias; ' +
        'a precision é um factor (1e6 = 1 µm). Na 3.4.3 passava despercebido; ' +
        'era a causa das caixas de ±100000 que impediam subir o motor, e na ' +
        '3.4.8 colapsava a geometria. Fica só o limiar que deixa as malhas ' +
        'em bruto: a mesma geometria que a 3.4.3 dava, conferida elemento a ' +
        'elemento em três modelos (geo, MEP com 6587 elementos e IFC 4.3).',
      'Os modelos guardados em cache convertem-se outra vez na primeira ' +
        'abertura (motor e conversão novos).',
      'IFC 4.3: a limitação passa a dizer a causa certa. Não é o ' +
        'IfcMappedRepresentation, é o StartParam/EndParam tipado do ' +
        'IfcSurfaceCurveSweptAreaSolid, que o web-ifc não lê.',
    ],
    fixed: [
      'Colorir por classe e o modo fantasma deixam de dar "Memory overflow!" ' +
        'ao fim de algumas vezes em modelos grandes (corrigido na fragments ' +
        '3.4.8; medido: 12 de 12 rondas sem erro onde a 3.4.3 falhava à 6.ª).',
    ],
  },
  {
    version: '0.2.0-beta.167',
    date: '2026-10-02',
    summary: 'Grelhas de mudança de datum (Datum 73, Lisboa, ED50, OSGB36, RD, BD72, DHDN, CH1903) para precisão de centímetros, e o KMZ alinhado ao norte verdadeiro.',
    added: [
      'Grelhas de mudança de datum incluídas, as mesmas que o PROJ usa: ' +
        'Datum 73 e Datum Lisboa (DGT), ED50 (IGN Espanha), OSGB36 (OSTN15), ' +
        'RD (RDNAPTRANS 2018), BD72, DHDN (BeTA2007) e CH1903 (CHENyx06). ' +
        'Quando o modelo está dentro de uma delas, o mapa, o relevo 3D, o ' +
        'globo e o KMZ usam-na: conferido contra o PROJ, a menos de 5 cm ' +
        '(o Helmert ficava entre 5 cm e 8 m ao lado, conforme o datum). Fora ' +
        'da grelha continua o Helmert. Informações do modelo e o globo dizem ' +
        'qual se usou.',
    ],
    changed: [
      'KMZ para o Google Earth: o modelo roda pela convergência da quadrícula ' +
        'no ponto (calculada), para o norte da grelha do CRS coincidir com o ' +
        'norte verdadeiro do Google Earth. Antes ia com heading 0.',
    ],
  },
  {
    version: '0.2.0-beta.166',
    date: '2026-10-02',
    summary: 'Globo 3D com relevo e geoide, e um índice EPSG de 6400 sistemas de coordenadas incluído, com a transformação para WGS 84 e a área de uso de cada um.',
    added: [
      'Globo 3D (Ficheiro, Ferramentas; barra lateral; botão no painel Mapa): ' +
        'os modelos georreferenciados num globo (Cesium), sobre o OpenStreetMap ' +
        'e o relevo Terrarium, no sítio e à cota que o ficheiro declara. Sem ' +
        'Cesium ion, sem contas nem chaves; os ficheiros do motor vêm da nossa ' +
        'origem.',
      'Geoide EGM96 incluído: as alturas do IFC e do relevo são ortométricas e ' +
        'o globo trabalha sobre o elipsóide; soma-se a ondulação N (em Lisboa, ' +
        'cerca de 54 m) aos dois. A grelha do CRS roda-se para o norte ' +
        'verdadeiro pela convergência no ponto. Tudo aparece marcado como ' +
        'calculado.',
      'No globo, cada modelo diz a que altura fica a base face ao relevo. ' +
        '"Pousar no relevo" e "Terreno translúcido" são só da vista: a posição ' +
        'do modelo não muda.',
      'Índice EPSG incluído (cerca de 6400 CRS projectados, geográficos e ' +
        'compostos, como o 7415 RD + NAP), gerado da base de dados do PROJ: a ' +
        'definição, a unidade dos eixos, a área de uso e a transformação para ' +
        'WGS 84 usada, com a precisão. Funciona sem rede; o epsg.io fica só ' +
        'para o que faltar.',
      'Informações do modelo: o nome do CRS e a transformação para WGS 84. Uma ' +
        'origem fora da área de uso do CRS dá aviso (o sinal típico de unidades ' +
        'trocadas), e as leituras alternativas dizem qual cai fora.',
    ],
    changed: [
      'As definições de CRS deixam de vir do epsg.io: conferido contra o PROJ, ' +
        'o epsg.io tem as rotações do Datum 73 (EPSG:27493) com o sinal trocado, ' +
        '50 m ao lado em Lisboa.',
    ],
  },
  {
    version: '0.2.0-beta.165',
    date: '2026-10-02',
    summary: 'Mapa: IFC2X3 georreferenciado (ePset_MapConversion), modelos só com latitude/longitude do IfcSite, IfcMapConversionScaled, mais CRS sem rede e pegada orientada.',
    added: [
      'IFC2X3: o ePset_MapConversion e o ePset_ProjectedCRS (o guia da ' +
        'buildingSMART) passam a ler-se ao abrir, com o nome em qualquer ' +
        'capitalização e o TargetCRS quando falta o CRS. A unidade é a que as ' +
        'propriedades declaram, senão a do projecto; o MapUnit, quando existe, ' +
        'confere-se, e se se contradizem mostram-se as leituras para escolher. ' +
        'O que o viewer grava num IFC2X3 volta a ler-se igual.',
      'Modelos só com latitude e longitude no IfcSite: ficam no mapa nessa ' +
        'posição, com a elevação (RefElevation) e o norte do TrueNorth. A ' +
        'grelha é o fuso UTM da longitude e a convergência entra na rotação; ' +
        'ambos aparecem como calculados. Latitude e longitude a zero não ' +
        'contam, e um modelo com coordenadas de CRS já embutidas não leva o ' +
        'IfcSite por cima (fica um botão para o usar).',
      'Informações do modelo, CRS / Origem: de onde veio a georreferenciação ' +
        '(IfcMapConversion, ePset_MapConversion ou IfcSite, com o #id), os ' +
        'valores declarados e os calculados.',
      'Mais sistemas de coordenadas sem rede: todos os fusos UTM WGS 84 e ' +
        'ETRS89, os portugueses (PT-TM06, Datum 73, Lisboa, Açores, Madeira) ' +
        'e alguns europeus. Os outros continuam a pedir-se ao epsg.io.',
    ],
    changed: [
      'A georreferenciação lê-se do texto do IFC e não só do que o motor de ' +
        'visualização devolve: um IfcMapConversionScaled sozinho, ou um IFC2X3, ' +
        'já não ficam na origem.',
      'A pegada no mapa 2D segue a orientação do modelo (um rectângulo rodado) ' +
        'em vez da caixa alinhada aos eixos do mapa.',
    ],
  },
  {
    version: '0.2.0-beta.164',
    date: '2026-10-01',
    summary: 'Estrutura IFC (modo avançado): qualquer entidade do ficheiro, com os atributos pelo esquema, as referências para navegar, quem a referencia e a linha STEP.',
    added: [
      'Estrutura IFC (Ficheiro, Ferramentas, e na barra lateral): abre qualquer ' +
        'entidade do ficheiro, com ou sem geometria, por #id, GlobalId ou ' +
        'classe. Cada atributo aparece com o nome e a ordem que o esquema ' +
        'declarado no cabeçalho (IFC2X3, IFC4, IFC4X3) lhe dá; os derivados ' +
        'ficam marcados. Sem esquema reconhecido não se escolhe nenhum: os ' +
        'argumentos ficam numerados.',
      'Cada referência leva à entidade apontada (com classe e nome), com ' +
        'histórico para trás e para a frente. "Referenciado por" mostra quem ' +
        'aponta para a entidade, agrupado pelo atributo inverso que o esquema ' +
        'declara (IsDefinedBy, ContainedInStructure, HasAssociations...) e ' +
        'pelo atributo de origem.',
      'Linha STEP tal como está no ficheiro, para copiar; herança da classe; ' +
        'separador Classes com a contagem de cada classe e as suas entidades; ' +
        'separador Cabeçalho com o HEADER do ficheiro.',
      'Segue a selecção do 3D, e "Mostrar no 3D" selecciona e enquadra. No ' +
        'painel Propriedades, com Avançado ligado, o botão Estrutura IFC abre ' +
        'o elemento seleccionado.',
    ],
    changed: [
      'Os nomes dos atributos, os inversos e a herança vêm das tabelas do ' +
        'esquema que o web-ifc já traz (geradas dos EXPRESS da buildingSMART): ' +
        'nada se pede à rede.',
    ],
  },
  {
    version: '0.2.0-beta.163',
    date: '2026-10-01',
    summary: 'Dados IFC completo: listas, intervalos, quantidades, materiais e classificações, no inventário, no modelo de preenchimento e na importação.',
    added: [
      'Dados IFC: propriedades em lista (IfcPropertyListValue) e em ' +
        'intervalo (IfcPropertyBoundedValue). No cabeçalho, ' +
        '"Pset.Prop [lista IfcLabel]" com os valores separados por "|", e ' +
        '"Pset.Prop [intervalo IfcReal]" com "min..max". A forma só se ' +
        'escolhe para propriedades novas: uma que existe mantém a sua.',
      'Dados IFC: quantidades. "Qto_X.Area [IfcQuantityArea]" altera ou cria ' +
        'a quantidade num IfcElementQuantity; valores negativos são recusados.',
      'Dados IFC: material. Uma coluna "Material" troca o IfcMaterial simples ' +
        'de cada elemento, reaproveitando o material com o mesmo nome. ' +
        'Materiais compostos (camadas, perfis, constituintes, listas) não se ' +
        'mudam por folha: são recusados com o motivo.',
      'Dados IFC: classificação. Uma coluna "Classificação: Uniclass 2015" ' +
        'põe o código (e opcionalmente "código | nome") nesse sistema, ' +
        'substituindo o anterior do mesmo sistema; os outros sistemas ficam.',
      'Inventário: resumo de materiais e de classificações (e folhas no ' +
        'Excel), listas e intervalos legíveis, e quantidades escolhíveis para ' +
        'o modelo de preenchimento, com colunas de material e de sistemas de ' +
        'classificação.',
    ],
    changed: [
      'O modelo de preenchimento escreve a forma e o tipo no cabeçalho, para ' +
        'a importação de volta não ter de os adivinhar. Devolvido sem ' +
        'alterações, não muda nada.',
    ],
  },
  {
    version: '0.2.0-beta.162',
    date: '2026-10-01',
    summary: 'Georreferenciação lida como a norma manda, com as duas leituras quando o ficheiro se contradiz. Dados IFC: valores enumerados e Psets dos tipos.',
    added: [
      'Dados IFC: valores enumerados. Só se aceitam os valores que a ' +
        'IfcPropertyEnumeration do ficheiro permite, escritos como lá estão; ' +
        'vários valores separados por "|". Um valor fora da lista é recusado ' +
        'e o relatório diz quais são os permitidos.',
      'Dados IFC: Psets dos tipos (IfcWallType, IfcDoorStyle...). Uma folha ' +
        'com a GlobalId de um tipo altera o Pset do tipo; o modelo de ' +
        'preenchimento aceita linhas de tipo. O relatório diz quantas ' +
        'instâncias herdam o valor e quantas têm valor próprio, que prevalece.',
      'Informações do modelo, CRS / Origem: de onde veio cada unidade ' +
        '(projecto, mapa, com o #id), a escala e a origem calculadas, e os ' +
        'avisos da leitura.',
    ],
    changed: [
      'IfcMapConversion lido como a norma manda: a translação está nas ' +
        'unidades do MAPA (o MapUnit do IfcProjectedCRS, ou a do projecto ' +
        'quando falta), e a escala é Scale x unidade do mapa / unidade do ' +
        'projecto. Antes assumia-se o mapa sempre em metros.',
      'Quando o ficheiro se contradiz (a unidade do mapa que declara não é a ' +
        'dos eixos do EPSG que nomeia), não se aplica nenhuma leitura sem ' +
        'escolha: o painel mostra as duas, com a escala e a origem de cada ' +
        'uma, e um botão para usar a certa.',
      'A unidade de comprimento do projecto passa a ler-se pelo ' +
        'IfcUnitAssignment do IfcProject, incluindo pés e polegadas; duas ' +
        'unidades de comprimento são ambíguas e não se escolhe nenhuma.',
      'IfcMapConversionScaled (IFC4.3): factores iguais nos três eixos ' +
        'entram na escala; diferentes não se representam e diz-se isso.',
    ],
  },
  {
    version: '0.2.0-beta.161',
    date: '2026-09-30',
    summary: 'Dados do IFC: inventário, acrescentar a partir de Excel, renomear e apagar Psets. Juntar IFC. IDS a partir de Excel. Gráficos e selecção através do modelo.',
    added: [
      'Dados IFC (Ficheiro, Ferramentas): inventário de Psets e propriedades ' +
        'por classe, com tipo de valor, unidade (com a entidade de onde vem) ' +
        'e quantos elementos os preenchem; sai para Excel. Lido do texto do ' +
        'IFC, não do que chegou ao ecrã.',
      'Acrescentar e alterar propriedades a partir de uma folha (.xlsx ou ' +
        '.csv), por GlobalId ou por uma propriedade que identifique cada ' +
        'elemento, com pré-visualização antes e depois e uma simulação ' +
        'completa antes de aceitar. O inventário gera o modelo de ' +
        'preenchimento.',
      'Mudar o nome e apagar Psets e propriedades em todo o modelo, ' +
        'incluindo nos tipos. Nada fica a apontar para o que foi apagado.',
      'Alterações pendentes em lotes, com anular e refazer; grava-se um IFC ' +
        'novo ou abre-se o resultado no viewer para conferir.',
      'Juntar IFC: vários ficheiros num só, sem os abrir. Motor do liteAECO ' +
        '(Apache-2.0), incluído sem alterações e a correr no browser; pisos ' +
        'juntos pelo nome ou pela cota, unidades convertidas pelas que os ' +
        'ficheiros declaram.',
      'IDS a partir de uma folha de Excel: no painel IDS e no editor, com ' +
        'modelo para descarregar; o editor também exporta para Excel.',
      'Colorir por: a legenda passa a gráfico de barras; clicar selecciona ' +
        'os elementos no 3D (Shift junta).',
      'Ver através (Aparência): a selecção desenha-se por cima de tudo, com ' +
        'um véu e as arestas, e vê-se mesmo tapada por outros elementos.',
    ],
    changed: [
      'Um Pset partilhado por vários elementos é copiado antes de mudar só ' +
        'parte deles: os outros ficam como estavam.',
      'O tipo de um valor novo nunca se adivinha: vem do cabeçalho da ' +
        'coluna, do que a propriedade já tinha, do único tipo que o modelo ' +
        'usa para ela, ou é IfcLabel; o relatório diz qual e porquê. Com dois ' +
        'tipos no modelo, pede-se o tipo.',
      'Texto escrito no IFC com as directivas da norma (\\X2\\), em vez de ' +
        'bytes latin1 soltos.',
      'O que trouxemos de outros projectos fica registado em ' +
        'docs/origens.json, e o Novidades.bat diz quando os ficheiros de ' +
        'origem mudaram. O motor incluído actualiza-se com um comando.',
    ],
    fixed: [
      'Uma folha de requisitos com TRUE ou VERDADEIRO numa propriedade ' +
        'booleana passa a "true", como o IDS pede.',
    ],
  },
  {
    version: '0.2.0-beta.160',
    date: '2026-09-25',
    summary: 'Caixa de corte à volta do modelo e sempre visível; planos de corte desenhados no 3D e ajustados à mão.',
    added: [
      'Planos de corte no 3D: cada plano ligado aparece como o polígono ' +
        'que corta no modelo, em ciano, com três pegas. A do centro ' +
        'desloca-o ao longo da normal; as outras duas inclinam-no em ' +
        'torno do centro (com Shift, de 5 em 5 graus, e o ângulo aparece ' +
        'junto ao nome enquanto se arrasta).',
    ],
    changed: [
      'Caixa de corte desenhada como volume: faces translúcidas, ' +
        'arestas contínuas e pegas maiores. Com a câmara perto ou ' +
        'dentro da caixa, desenha-se o bocado que fica à frente em vez ' +
        'de desaparecer tudo.',
    ],
    fixed: [
      'A caixa de corte nascia ao lado do edifício em modelos com ' +
        'deslocamento: a caixa do motor já vem em coordenadas do mundo ' +
        'e era transformada outra vez. Cortava o modelo todo e ficava ' +
        'fora do enquadramento, e por isso não se via.',
    ],
  },
  {
    version: '0.2.0-beta.159',
    date: '2026-09-25',
    summary: 'IDS a 100% na suite oficial actualizada (334 casos), e o clash medido nos Snowdon.',
    added: [
      'IDS: propriedades de materiais. Uma especificação aplicável a ' +
        'IfcMaterial vê as IfcMaterialProperties (IFC4) e as ' +
        'IfcExtendedMaterialProperties (IFC2X3) do material.',
      'IDS: tabela de mapeamento de tipos do IFC2X3. Um IfcFlowTerminal ' +
        'tipado por IfcAirTerminalType conta como IfcAirTerminal, com o ' +
        'PredefinedType do tipo (incluindo USERDEFINED), como a norma ' +
        'manda para o IFC2X3.',
    ],
    changed: [
      'A suite oficial de conformidade da buildingSMART tinha 16 casos ' +
        'novos que não tínhamos (entity e property). Trazidos; o motor ' +
        'passa os 334, sem lista de excepções.',
      'Clash medido com os dois Snowdon (estrutural 6,7 MB e ' +
        'arquitectónico 150 MB): 7.446 elementos, 34.863 pares, 13.761 ' +
        'conflitos hard, em 4,9 s.',
    ],
  },
  {
    version: '0.2.0-beta.158',
    date: '2026-09-25',
    summary: 'Auditar o IDS contra o modelo antes de validar.',
    added: [
      'No painel do IDS, "Auditar o IDS contra o modelo" lê o IDS e o que ' +
        'o modelo contém e diz, por especificação, o que não pode dar ' +
        'certo: uma classe que o modelo não tem (a especificação não se ' +
        'aplica a nada), um property set ou uma propriedade que nenhum ' +
        'elemento tem, uma classificação ou um material ausentes (vai ' +
        'falhar em todos), ou um esquema IFC que não é o do modelo. Um ' +
        'valor por padrão ou uma enumeração só em parte presente ficam ' +
        'como "a confirmar", nunca como certeza. Um requisito proibido ' +
        'sobre o que não existe não é reparo: é o que ele pede.',
    ],
  },
  {
    version: '0.2.0-beta.157',
    date: '2026-09-25',
    summary: 'Documentos herdados do type, e exportação em BCF 3.0.',
    added: [
      'Os documentos associados ao type (IfcWallType, IfcColumnType, ...) ' +
        'aparecem em todas as instâncias desse type, marcados "Do type ' +
        '<nome>". É a forma mais comum de documentar um produto: uma ' +
        'ficha técnica por tipo, e não uma por parede.',
      'Exportar .bcfzip em BCF 3.0, à escolha ao lado do botão (a 2.1 ' +
        'continua a ser a omissão). Na 3.0 as labels vão num contentor, ' +
        'os comentários e os viewpoints dentro do tópico, os comentários ' +
        'sem Status, as câmaras com AspectRatio, e o TopicType e o ' +
        'TopicStatus são obrigatórios: um tópico sem eles leva o primeiro ' +
        'valor do catálogo do ficheiro.',
    ],
  },
  {
    version: '0.2.0-beta.156',
    date: '2026-09-25',
    summary: 'Quem está no BCF agora, e arrumação dos ficheiros maiores.',
    added: [
      'No BCF com servidor, o painel de sincronização diz quem mais tem ' +
        'este workspace aberto neste momento ("Agora aqui: Ana, Luís"). ' +
        'Reaproveita a sala de revisão: só o nome viaja, e o servidor não ' +
        'guarda nada.',
    ],
    changed: [
      'Os dois ficheiros maiores da interface foram divididos: a vista ' +
        'de tópicos BCF em três (lista, detalhe, vista) e a selecção ' +
        'múltipla saiu do painel de propriedades. Sem mudança de ' +
        'comportamento; é para se poder continuar a mexer neles.',
    ],
  },
  {
    version: '0.2.0-beta.155',
    date: '2026-09-25',
    summary: 'BCF a várias mãos com cadeado à vista, avisos por email, IFC5 no painel de propriedades, e a sala a libertar quem sai.',
    added: [
      'Abrir um tópico BCF para editar, com servidor, pega nele: os ' +
        'outros vêem um cadeado com o nome na lista de tópicos, e se ' +
        'alguém já o tiver os campos ficam só de leitura com aviso. O ' +
        'bloqueio renova-se sozinho enquanto a janela estiver aberta e ' +
        'larga-se ao fechar. Os comentários continuam abertos a todos.',
      'Avisos por email quando alguém comenta um tópico (para quem o tem ' +
        'atribuído, o criou ou o segue) e quando um tópico é atribuído a ' +
        'alguém. Só para endereços de email, nunca para quem fez a ' +
        'alteração, e nunca a travar a sincronização.',
      'O painel de propriedades mostra o nó IFC5 seleccionado: classe, ' +
        'nome, caminho e propriedades com o property set. Clicar numa ' +
        'parede de um .ifcx deixava o painel a pedir que se clicasse.',
    ],
    fixed: [
      'Sair da sala de revisão demorava uns 12 segundos a desaparecer da ' +
        'lista dos outros: o proxy só dava o socket por morto ao fim ' +
        'desse tempo. Agora quem sai diz que sai, e a saída é imediata.',
    ],
    changed: [
      'A comparação nuvem-IFC foi medida com o levantamento real ' +
        '(Snowdon, 16 GB, 10 milhões de pontos guardados) contra a ' +
        'estrutura metálica do IFC estrutural (948 elementos): 7,7 s.',
    ],
  },
  {
    version: '0.2.0-beta.154',
    date: '2026-09-25',
    summary: 'IFC5: propriedades com o property set, e colisões de nome à vista.',
    added: [
      'O leitor de .ifcx aceita as propriedades qualificadas pelo ' +
        'property set (bsi::ifc::v5a::Pset_WallCommon::IsExternal), que ' +
        'as ferramentas passaram a escrever a 24-09-2026. Continua a ler ' +
        'a forma plana do esquema. O property set aparece ao lado do nome ' +
        'da propriedade.',
      'Quando duas chaves dão pelo mesmo nome de propriedade, mostram-se ' +
        'as duas e diz-se de que property sets vêm. Ficar com uma delas ' +
        'era decidir pelo ficheiro.',
    ],
    changed: [
      'O acompanhamento do motor passa a registar que o web-ifc 0.2.0.78 ' +
        'não se pode subir enquanto o Fragments estiver preso na 3.4.3: ' +
        'mudou a assinatura do StreamMeshes e o modelo deixa de abrir. ' +
        'Medido a 25-09-2026.',
    ],
  },
  {
    version: '0.2.0-beta.153',
    date: '2026-09-22',
    summary: 'Unidades rastreáveis até ao ficheiro, e sem escolhas por quem lê.',
    added: [
      'Cada unidade mostra a entidade do IFC de onde foi lida (por exemplo ' +
        '"#65 IfcDerivedUnit"), na tabela do projecto e ao passar o rato ' +
        'numa propriedade, para se poder conferir no ficheiro.',
      'Nas unidades derivadas, a forma habitual ao lado da declarada, ' +
        'marcada como calculada: kg·K⁻¹·s⁻³ (declarado) = W/(m²·K) ' +
        '(calculado). Só aparece quando a conta pelas unidades de base ' +
        'fecha exactamente; com um prefixo que muda a escala, ou com grau ' +
        'Celsius, não se mostra nenhuma.',
    ],
    changed: [
      'Se o projecto declarar duas unidades para o mesmo tipo (o IFC não o ' +
        'permite), já não se usa a primeira: o tipo fica marcado como ' +
        'ambíguo e as propriedades desse tipo dizem "unidade ambígua", com ' +
        'as candidatas e as suas entidades.',
    ],
  },
  {
    version: '0.2.0-beta.152',
    date: '2026-09-22',
    summary: 'Types, unidades e materiais: o que o IFC declara, lido como está.',
    added: [
      'Separador "Types" na barra da esquerda: todos os IfcXxxType do ' +
        'modelo, por classe e por nome, com os atributos e os property ' +
        'sets do type e as suas instâncias (seleccionar uma mostra as ' +
        'propriedades dela; "Seleccionar todas" selecciona o type ' +
        'inteiro). Assinala nomes com vários types repetidos, como o ' +
        'Revit exporta, e diz se são cópias; lista os elementos sem type.',
      'Numa instância, a secção "Tipo IFC" mostra os property sets do ' +
        'type, que a instância herda, e marca os que a instância repete.',
      'Unidades: em "Info do modelo", as unidades que o projecto declara ' +
        '(SI, por conversão como o grau, derivadas, moeda), com aviso ' +
        'quando falta comprimento, área, volume ou ângulo, ou quando há ' +
        'duas para o mesmo tipo. "Analisar as propriedades" diz quantas ' +
        'declaram unidade própria e quais.',
      'Cada propriedade numérica mostra a sua unidade ao lado do valor: a ' +
        'que declara (em destaque) ou a do projecto, e "sem unidade" ' +
        'quando nem uma nem outra existem. Ao passar o rato, diz de onde ' +
        'vem.',
    ],
    fixed: [
      'O aviso "Instance-only" aparecia em todos os elementos, mesmo em ' +
        'modelos com types: o importador do motor deixava de fora o ' +
        'IfcRelDefinesByType e as classes de type. No modelo de exemplo ' +
        'havia 39 types e nenhum elemento sabia o seu.',
      'O aviso deixa de aparecer em IfcSite, edifícios, pisos e no ' +
        'projecto, que não têm type no esquema IFC.',
      'A secção "Materiais" das propriedades dizia 0 em todos os ' +
        'elementos: o motor liga o elemento directamente ao material, e ' +
        'só se procurava a relação.',
      'O "Local ID" do type não aparecia (o motor devolve-o embrulhado).',
      'Os modelos já abertos antes voltam a converter-se na próxima ' +
        'abertura, para ganharem os types.',
    ],
  },
  {
    version: '0.2.0-beta.151',
    date: '2026-09-21',
    summary: 'As IfcSpatialZone passam a ter geometria no 3D.',
    added: [
      'Compartimentos corta-fogo, zonas térmicas e as outras ' +
        'IfcSpatialZone aparecem como volumes violeta translúcidos, ' +
        'distintos dos espaços (cinzentos), e sem taparem o que têm ' +
        'dentro. Escondem-se com o "Esconder spaces", como os espaços, e ' +
        'aparecem quando se desliga.',
    ],
    fixed: [
      'As IfcSpatialZone não tinham geometria: o importador do motor só ' +
        'converte uma lista de classes, e as zonas não estavam nela. Nem ' +
        'sequer existiam como elemento no modelo convertido.',
      'Um modelo já aberto antes desta versão volta a converter-se na ' +
        'próxima abertura, em vez de vir da cache sem as zonas. A cache ' +
        'passa a mudar também quando muda a nossa configuração de ' +
        'conversão, e não só a versão do motor.',
    ],
  },
  {
    version: '0.2.0-beta.150',
    date: '2026-09-21',
    summary: 'Comparar a nuvem de pontos com os elementos do IFC, elemento a elemento.',
    added: [
      'Escolhe-se o que comparar: estrutura metálica (pilares, vigas e ' +
        'barras de aço), pilares e vigas de qualquer material, os ' +
        'elementos seleccionados, ou classes à escolha, com filtro por ' +
        'material. Comparar o levantamento inteiro com o modelo inteiro ' +
        'dizia pouco: o reboco, a betonilha e o terreno contavam como desvio.',
      'Cada ponto mede-se à superfície do elemento, triângulo a ' +
        'triângulo, e só conta se estiver a menos do raio escolhido. Os ' +
        'elementos vizinhos entram como concorrentes: um ponto mais perto ' +
        'de um pilar de betão não é dado ao pilar de aço ao lado.',
      'Relatório por elemento, os piores primeiro: pontos, média, P95, ' +
        'percentagem dentro da tolerância, e "Não visto" para o que o ' +
        'laser não apanhou. Carregar numa linha selecciona e enquadra o ' +
        'elemento. Exporta-se em CSV para o Excel.',
      'Para pilares e vigas, o desaprumo (ou a inclinação) e a fuga ' +
        'lateral, por ajuste da peça medida à peça do projecto, com a ' +
        'incerteza ao lado. Diz quando o valor está dentro do ruído, e ' +
        'quando só uma face foi vista e a fuga só se conhece numa direcção.',
      'Só os pontos que o corte deixa ver, quando há planos de corte ou ' +
        'caixa de corte activos.',
      'Cores do levantamento ou do desvio, trocáveis sem comparar outra vez.',
      'Nuvem de exemplo: gera pontos nas faces dos pilares e vigas do ' +
        'modelo, com ruído de laser e desvios postos de propósito, para ' +
        'experimentar a comparação sem levantamento.',
    ],
    removed: [
      'O "Desvio ao IFC" antigo, que media cada ponto à caixa do ' +
        'elemento. Um ponto no vão de uma viga em I dava zero.',
    ],
    fixed: [
      '"Colorir por material" não encontrava o material na maior parte ' +
        'dos IFC: o motor liga o elemento directamente ao IfcMaterial, ' +
        'sem a relação pelo meio, e só se procurava a relação. No modelo ' +
        'de exemplo, os 85 pilares e vigas saíam sem material.',
      'O painel das nuvens chama-se "Nuvens de pontos" e não "Point clouds".',
    ],
  },
  {
    version: '0.2.0-beta.149',
    date: '2026-09-21',
    summary: 'Escolher a densidade da nuvem de pontos, e mudá-la depois.',
    added: [
      'Antes de importar escolhe-se quantos pontos guardar: até 5, 10, ' +
        '20 ou 40 milhões, com a memória que cada um ocupa ao lado. ' +
        'Estava fixo em 5 milhões, e num levantamento de 230 milhões ' +
        'isso é um ponto em cada 46, grosseiro de mais.',
      'Numa nuvem já aberta, "Voltar a ler com" outra densidade, sem ' +
        'escolher o ficheiro outra vez. A posição, o alinhamento ao IFC e ' +
        'o tamanho dos pontos ficam como estavam.',
    ],
    changed: [
      'A cor de cada ponto ocupa agora 3 bytes em vez de 12. É o que ' +
        'deixa caber o dobro dos pontos na mesma memória.',
      'A densidade por omissão passa de 5 para 10 milhões, e lembra-se ' +
        'da última escolhida neste browser.',
    ],
  },
  {
    version: '0.2.0-beta.148',
    date: '2026-09-21',
    summary: 'Rodar com o botão direito do rato.',
    added: [
      'Arrastar com o botão direito roda a vista, sem ser preciso Shift. ' +
        'O Shift + roda continua a rodar como antes.',
    ],
    changed: [
      'O botão esquerdo fica como estava: clique selecciona e arrastar ' +
        'no vazio faz a janela de seleção. Não passou a rodar porque ' +
        'rodar e fazer a janela são o mesmo gesto, e um dos dois perdia-se.',
      'Um clique direito sem mexer o rato continua a abrir o menu de ' +
        'contexto e a acabar as medições. Um arrasto com o direito já ' +
        'não o abre no fim, nem acaba a medição a meio.',
    ],
  },
  {
    version: '0.2.0-beta.147',
    date: '2026-09-21',
    summary: 'Abrir só uma nuvem de pontos, sem IFC.',
    added: [
      'Botão "Abrir nuvem de pontos" na caixa inicial. A caixa só ' +
        'oferecia IFC, e quem viesse com um levantamento tinha de ' +
        'descobrir o painel das nuvens ou abrir um IFC que não queria.',
      'O "Abrir modelo" e o arrastar para a janela aceitam também ' +
        'nuvens de pontos (PTS, XYZ, LAS, LAZ, E57, PLY), e desviam-nas ' +
        'para o sítio certo.',
      'Uma nuvem aberta sozinha leva a câmara consigo. Ficava na vista ' +
        'inicial, apontada à origem, e uma nuvem longe da origem não se ' +
        'via.',
      'O progresso de uma nuvem grande vê-se venha ela por onde vier, ' +
        'e não só quando se abre pelo painel.',
    ],
    fixed: [
      'Abrir uma nuvem de pontos ou um IFC5 pelo "Substituir", com um ' +
        'IFC aberto, apagava o IFC antes de ver que tipo de ficheiro ' +
        'era. Uma nuvem vive ao lado do modelo, não no lugar dele. O ' +
        'IFC5 tinha este defeito desde a beta.140.',
    ],
  },
  {
    version: '0.2.0-beta.146',
    date: '2026-09-21',
    summary: 'A nuvem de pontos fica de pé e ao lado do IFC.',
    fixed: [
      'Todas as nuvens de pontos entravam deitadas de lado, em todos os ' +
        'formatos. Os levantamentos têm o Z para cima e o 3D tem o Y; o ' +
        'leitor de IFC convertia e o de nuvens não.',
      'Abrir um IFC depois de uma nuvem parecia apagá-la. Não apagava: ' +
        'o IFC do Snowdon traz georreferenciação e o viewer põe-no nas ' +
        'coordenadas reais, a 425 km da origem, e a nuvem ficava na ' +
        'origem, fora do alcance da câmara.',
      'A caixa "Sem modelo carregado" ficava por cima de uma nuvem de ' +
        'pontos ou de um IFC5 abertos, e só desaparecia abrindo um IFC.',
    ],
    added: [
      'Quando a nuvem está a mais de um quilómetro do modelo, o painel ' +
        'diz a que distância está e oferece Alinhar ao modelo. É um botão ' +
        'e não uma coisa automática: serve quando as duas vieram do mesmo ' +
        'projecto, e uma nuvem de um topógrafo pode vir em coordenadas ' +
        'próprias.',
    ],
  },
  {
    version: '0.2.0-beta.145',
    date: '2026-09-21',
    summary: 'Nuvens de pontos PTS e XYZ de qualquer tamanho.',
    fixed: [
      'Um PTS de 15,3 GB não abria, e a mensagem dizia que o ficheiro ' +
        'estava numa pasta do OneDrive por descarregar. Não estava: ' +
        'estava nas Transferências, e era grande de mais para o browser ' +
        'o ler de uma vez. O Chrome recusa com o mesmo erro nos dois ' +
        'casos, e nós escolhíamos a explicação errada.',
      'A mensagem de erro passa a verificar primeiro o tamanho, que é o ' +
        'único motivo que se consegue provar daqui.',
    ],
    added: [
      'PTS e XYZ lêem-se aos pedaços, sem nunca ter o ficheiro inteiro ' +
        'em memória. Um levantamento de 230 milhões de pontos abre em ' +
        'cerca de três minutos, com barra de progresso e botão para ' +
        'cancelar.',
      'Quando o ficheiro tem mais pontos do que o browser desenha bem, ' +
        'guarda-se um em cada N, e o painel DIZ que o fez: mostrar 5 ' +
        'milhões de 230 e deixar pensar que se vê tudo seria mentir ' +
        'sobre o levantamento.',
      'Um PTS cortado ou com o cabeçalho errado já não é reduzido sem ' +
        'necessidade: acredita-se no menor entre o que o cabeçalho diz ' +
        'e o que o tamanho do ficheiro permite.',
    ],
  },
  {
    version: '0.2.0-beta.144',
    date: '2026-09-21',
    summary: 'BCF partilhado pelo workspace, com sincronização.',
    added: [
      'Botão Sincronizar no BCF, em modo cloud. Traz o que os outros ' +
        'fizeram e envia o que fizeste. Os modelos não sobem: o que ' +
        'viaja são os tópicos e as respostas.',
      'Duas pessoas podem responder ao mesmo tópico ao mesmo tempo. ' +
        'Os comentários não dão conflito nunca: juntam-se pela ordem ' +
        'das datas e ninguém perde nada.',
      'Quando duas pessoas mexem no MESMO campo do mesmo tópico, a ' +
        'sincronização pára e mostra os três valores, o que estava, o ' +
        'teu e o dele, para escolheres. Não se funde em silêncio: no ' +
        'BCF isso significava fechar um tópico que o outro reabriu.',
      'Enquanto alguém está a editar os campos de um tópico, os outros ' +
        'vêem quem é. Os comentários ficam abertos na mesma.',
    ],
    changed: [
      'Sem ligação, o BCF continua a funcionar como sempre funcionou: ' +
        'importar, editar e exportar. A sincronização é o momento em ' +
        'que os vários lados se encontram, e não uma condição para se ' +
        'trabalhar.',
    ],
  },
  {
    version: '0.2.0-beta.143',
    date: '2026-09-20',
    summary: 'Vários planos de corte ao mesmo tempo.',
    added: [
      'Planos de corte soltos, na régua. Um por eixo, ou um paralelo a ' +
        'uma face que se clique, e quantos se quiserem ao mesmo tempo. ' +
        'Cada um liga-se, desliga-se e tira-se sozinho, sem mexer nos ' +
        'outros.',
      'Clicar numa face é a maneira de os rodar que serve para alguma ' +
        'coisa: uma fachada oblíqua não tem ângulo redondo nenhum, e ' +
        'apanhá-lo arrastando um manípulo é trabalho perdido. Quem ' +
        'quiser o número exacto tem azimute, elevação e posição ao ' +
        'lado.',
      'O aviso a partir de três planos ligados. Cortam em conjunto: só ' +
        'fica visível o que estiver do lado bom de todos, e é fácil ' +
        'ficar com o ecrã vazio sem se perceber porquê.',
    ],
    fixed: [
      'No painel de IFC5, um nó que apareça duas vezes na árvore (um ' +
        'protótipo herdado por dois sítios) juntava as duas linhas ' +
        'numa e a segunda desaparecia.',
    ],
  },
  {
    version: '0.2.0-beta.142',
    date: '2026-09-20',
    summary: 'Especificações IDS partilhadas pelo workspace.',
    added: [
      'O editor de IDS passa a poder guardar a especificação no ' +
        'workspace, e não só no disco de quem a está a escrever. Quem ' +
        'tiver o módulo IDS vê a lista, abre qualquer uma e grava ' +
        'versões novas.',
      'Cada gravação declara a versão de que partiu. Se alguém gravou ' +
        'entretanto, a gravação é recusada a dizer quem foi e quando, ' +
        'com o histórico à mão. Não se funde nada em silêncio: uma ' +
        'fusão errada de requisitos dá um documento limpo que ninguém ' +
        'aprovou.',
      'Arquivar não apaga. O histórico de uma especificação é o ' +
        'registo do que foi exigido a quem entregou o modelo.',
    ],
    changed: [
      'O que vai para o servidor é a especificação, não os modelos. A ' +
        'validação continua a correr no browser e os IFC não sobem.',
    ],
  },
  {
    version: '0.2.0-beta.141',
    date: '2026-09-20',
    summary: 'IFC5: clicar, fronteiras de espaço e curvas.',
    added: [
      'Clicar num elemento IFC5 no 3D acende-o e traz a linha dele à ' +
        'vista no painel. O clique acerta no nó que guarda a malha, ' +
        'que não tem classe nem diz nada a ninguém, por isso sobe-se ' +
        'até ao elemento: quem clica numa parede quer a parede.',
      'Fronteiras de espaço, lidas nos dois sentidos: de uma parede ' +
        'vê-se que sala fecha, de uma sala vê-se o que a fecha. Vivem ' +
        'em nós que não são filhos de ninguém, e por isso liam-se zero ' +
        'ao descer a árvore, sem erro nenhum.',
      'Curvas desenhadas como polilinhas: eixos de grelha e linhas de ' +
        'referência, finas e apagadas para não competirem com o ' +
        'edifício.',
    ],
  },
  {
    version: '0.2.0-beta.140',
    date: '2026-09-20',
    summary: 'IFC5: abrir .ifcx e empilhar camadas.',
    added: [
      'Painel IFC5 na régua. Abre um .ifcx e desenha-o, com a árvore, ' +
        'as classes e as propriedades. Arrastar um .ifcx para a janela ' +
        'também funciona: o carregador desvia-o para lá.',
      'Camadas. Por cima de um ficheiro aberto acrescenta-se outro, e ' +
        'é isso que o IFC5 tem de diferente: no exemplo oficial, um ' +
        'ficheiro de 631 bytes passa a resistência ao fogo da parede ' +
        'de inexistente a R30, sem tocar no modelo.',
      'A geometria vem quase sempre por herança: a janela não tem ' +
        'malha, quem tem é o tipo que ela herda. Sem resolver isso, um ' +
        'ficheiro IFC5 aparece vazio.',
      'As chaves que ainda não sabemos ler ficam à vista no painel, em ' +
        'vez de desaparecerem em silêncio. Num formato alfa, isso é a ' +
        'lista de trabalho.',
    ],
  },
  {
    version: '0.2.0-beta.139',
    date: '2026-09-20',
    summary: 'Os documentos que o modelo associa aos elementos.',
    added: [
      'Secção "Documentos associados" nas propriedades do elemento. O ' +
        'IFC sabe dizer que uma parede tem uma ficha técnica e que um ' +
        'equipamento tem um manual, em IfcRelAssociatesDocument, e nós ' +
        'não líamos nada disso: quem abrisse um modelo bem preparado ' +
        'não via os documentos que o autor teve o trabalho de associar.',
      'Mostra código, nome, descrição, revisão e estado, lendo os ' +
        'nomes de atributo do IFC2x3 e do IFC4. O que a referência não ' +
        'disser é preenchido pelo documento que ela aponta.',
      'A localização abre numa aba nova quando é http ou https. Um ' +
        'caminho local ou de rede aparece como texto para copiares: o ' +
        'browser não os abre a partir de uma página, e uma ligação que ' +
        'não faz nada é pior do que texto.',
    ],
  },
  {
    version: '0.2.0-beta.138',
    date: '2026-09-19',
    summary: 'Identidade de elementos entre revisões.',
    added: [
      'Chave autoral na comparação. O GlobalId é o identificador que a ' +
        'ferramenta de autor regenera sempre que o elemento é ' +
        'redesenhado, e é por isso que uma revisão nova aparece com ' +
        'tudo removido e tudo novo. Se o modelo trouxer um código que ' +
        'alguém mantém de propósito, um código de activo numa property ' +
        'set ou o Tag, a comparação passa a casar por esse código. ' +
        'Escreve-se no campo Chave: "Pset_Asset.AssetId" ou "Tag".',
      'Mapa de identidade. Os pares que a comparação aceitou podem ser ' +
        'gravados num ficheiro, revistos, e voltar a ser aplicados na ' +
        'próxima vez. O mapa fica preso ao SHA-256 dos dois modelos: ' +
        'lido contra outro par de ficheiros é recusado, com o motivo, ' +
        'em vez de ser aplicado às cegas.',
      'A comparação diz o que a identidade fez E o que não conseguiu ' +
        'fazer: valores repetidos recusados, pares do mapa sem ' +
        'correspondência nestes modelos, colisões de chave.',
    ],
  },
  {
    version: '0.2.0-beta.137',
    date: '2026-09-19',
    summary: 'Três coisas cortadas: a régua e o guia de dois minutos.',
    fixed: [
      'A régua de ícones da esquerda mostrava uma barra de deslocação ' +
        'horizontal quando os ícones não cabiam na altura. Tinha 44 px ' +
        'de largura, os botões 36 e a barra vertical come 10: sobravam ' +
        '34 px para botões de 36. Os 2 px a mais chegavam para nascer ' +
        'uma barra horizontal, que por sua vez roubava mais 10 px de ' +
        'altura aos ícones. A régua passa a 48 px.',
      'O guia de dois minutos media a posição uma vez por passo e ' +
        'ficava assim. Mudar a janela de tamanho ou abrir um painel ao ' +
        'lado deixava o balão e o recorte onde o elemento estava: ' +
        'numa medição, a janela passou de 2200 para 1200 px e o balão ' +
        'ficou 920 px fora do ecrã, com o recorte 1010 px ao lado do ' +
        'alvo. Passa a acompanhar o elemento enquanto o guia estiver ' +
        'aberto.',
      'O recorte do guia não recortava nada: havia uma camada escura ' +
        'por cima do ecrã inteiro, incluindo o elemento realçado, e o ' +
        'anel do realce acrescentava outra por fora. O elemento que o ' +
        'guia apontava ficava tão escuro como o resto. Agora escurece ' +
        'só o que está à volta.',
    ],
  },
  {
    version: '0.2.0-beta.136',
    date: '2026-09-19',
    summary: 'Duas coisas que a régua partiu, corrigidas.',
    fixed: [
      'No modo cloud, o painel de propriedades saía pela direita do ' +
        'ecrã fora e ficava cortado. A régua acrescentou 44 px de ' +
        'largura ao layout, e o contentor ao lado recusava-se a ' +
        'encolher: media 3298 px onde só havia 956. Faltava-lhe um ' +
        'min-width de zero, que é o que deixa um filho de um contentor ' +
        'flex encolher abaixo do seu conteúdo.',
      'O passo do guia que aponta para a régua punha o balão 153 px ' +
        'abaixo do ecrã, com metade das frases invisíveis. A régua ' +
        'ocupa a altura toda, portanto "por baixo dela" é fora da ' +
        'janela. O balão passa a ir para o lado quando não há espaço ' +
        'nem acima nem abaixo, e nunca sai do ecrã.',
    ],
  },
  {
    version: '0.2.0-beta.135',
    date: '2026-09-19',
    summary:
      'Limpeza de 24 avisos do compilador de React que arrastávamos há ' +
      'meses.',
    fixed: [
      'Vinte e três painéis limpavam estado no corpo de um efeito antes ' +
        'de irem buscar dados. Além de encadear renders, isso deixava ' +
        'um render a mostrar os dados do modelo anterior debaixo do ' +
        'cabeçalho do novo. Passam a guardar consigo o modelo (ou a ' +
        'selecção) de que vieram, e ficam velhos sozinhos.',
      'A árvore espacial já não mistura o nome dos elementos de um ' +
        'modelo com a árvore de outro quando um lote de leitura chega ' +
        'atrasado depois de se trocar de modelo.',
      'O contador de segundos do assistente passa a contar a partir do ' +
        'instante em que a pergunta partiu, e não a somar batidas: com ' +
        'a página escondida, o browser suspende o relógio e o número ' +
        'ficava atrasado.',
      'As miniaturas dos viewpoints deixam de custar um render extra ' +
        'cada uma.',
    ],
    removed: [
      'Um destaque das linhas do painel de Entidades que continham a ' +
        'selecção. Nunca chegou a funcionar: a contagem vinha sempre a ' +
        'zero, com um comentário a dizer "saltar por agora". O painel ' +
        'também deixou de se redesenhar a cada mudança de selecção.',
    ],
  },
  {
    version: '0.2.0-beta.134',
    date: '2026-09-19',
    summary: 'Tema claro para a interface.',
    added: [
      'A interface passa a ter tema claro, escuro ou "como o sistema", ' +
        'no botão ao lado dos atalhos. O escuro continua a ser o de ' +
        'origem: quem já usava a aplicação não a vê mudar de cor. O ' +
        'claro serve para o que sai daqui, que é onde o preto ' +
        'atrapalha: imagens para relatórios, documentos, apresentações.',
      'No tema claro o destaque passa do âmbar para o azul da bSPT. O ' +
        'âmbar sobre branco tem contraste 2,15 e não se lê.',
    ],
    changed: [
      'As cores de estado (aviso, erro, sucesso, nota) estavam escritas ' +
        'à mão em 69 sítios, com tons diferentes entre painéis para a ' +
        'mesma intenção, e todas escolhidas para fundo escuro. Passou a ' +
        'haver um token por intenção e é o tema que decide a cor. São ' +
        '372 substituições em 60 ficheiros, e sem elas o tema claro ' +
        'nasceria partido.',
      'O tema da interface não é o mesmo que o fundo da cena 3D, que ' +
        'continua onde estava (Aparência > Desenho). Pode-se querer a ' +
        'interface clara com a cena escura, ou ao contrário.',
    ],
  },
  {
    version: '0.2.0-beta.133',
    date: '2026-09-19',
    summary: 'Sala de revisão: rever o mesmo modelo a várias mãos.',
    added: [
      'Sala de revisão (régua da esquerda). Abre-se uma sala, copia-se ' +
        'o link, e quem entra vê quem lá está. Uma pessoa conduz a ' +
        'visita e as outras acompanham a câmara dela; a selecção de ' +
        'qualquer um aparece aos outros.',
      'O IFC não passa pelo servidor. Cada um abre o seu ficheiro e o ' +
        'que viaja são coordenadas e identificadores. A sala reconhece ' +
        'que estão todos no mesmo modelo comparando o SHA-256, e avisa ' +
        'quando alguém está a ver outro ficheiro.',
      'A ligação volta sozinha quando cai: uma reunião dura mais do que ' +
        'uma ligação de rede.',
    ],
    changed: [
      'Isto não é edição em conjunto, e está escrito no painel. O ' +
        'IFClite faz colaboração com um CRDT sobre IFCX, o que obriga a ' +
        'converter o modelo e a enviá-lo para o servidor. Para uma ' +
        'reunião de coordenação, em que todos já têm o ficheiro, o que ' +
        'é preciso partilhar é estado efémero, e para isso um relé que ' +
        'reenvia o que recebe chega. O servidor não guarda nada: quando ' +
        'a última pessoa sai, a sala deixa de existir.',
    ],
  },
  {
    version: '0.2.0-beta.132',
    date: '2026-09-18',
    summary: 'Documento: um relatório sobre o modelo, impresso em PDF.',
    added: [
      'Documento (régua da esquerda). Monta-se com blocos (título, ' +
        'subtítulo, parágrafo, tabela de classes, tópicos BCF, quebra ' +
        'de página) e imprime-se em PDF A4 ou A3.',
      'O texto usa ligações entre chavetas, e o documento guarda as ' +
        'ligações e não os valores: {entidades}, {classes}, {pisos}, ' +
        '{ficheiro}, {esquema}, {data} e mais. O mesmo documento aberto ' +
        'sobre a revisão seguinte do IFC lê a revisão seguinte, em vez ' +
        'de ser uma fotografia que se desactualiza sem avisar.',
      'Uma ligação que não resolve aparece escrita como [chave: ' +
        'motivo] e não como espaço em branco: um relatório com um ' +
        'buraco silencioso é pior do que um que diz onde está o buraco.',
    ],
    fixed: [
      'O contador de entidades de um modelo nascia a zero e nunca era ' +
        'preenchido. A barra de estado escondia-o e o Documento dizia ' +
        '"0 entidades" sobre um modelo cheio. Passa a ser contado ' +
        'quando o modelo acaba de carregar.',
    ],
  },
  {
    version: '0.2.0-beta.131',
    date: '2026-09-18',
    summary:
      'A comparação passa a reconhecer elementos que só mudaram de ' +
      'GlobalId.',
    added: [
      'Comparar IFC ganhou "GlobalId trocados". Vários exportadores ' +
        'regeneram os GlobalId a cada exportação: o mesmo pilar, no ' +
        'mesmo sítio, com o mesmo nome, sai com identificador novo, e a ' +
        'comparação dizia que um foi removido e outro acrescentado. Um ' +
        'modelo onde nada mudou aparecia com centenas de alterações.',
      'Agora, entre os que ficaram por casar, procura-se quem tem o ' +
        'mesmo conteúdo (classe, nome, ObjectType, Tag, PredefinedType ' +
        'e piso). Quando há um só de cada lado, são o mesmo elemento. ' +
        'Com vários iguais, desempata-se pela posição, exigindo que os ' +
        'dois se escolham um ao outro.',
    ],
    changed: [
      'Grupos com candidatos iguais a mais ficam declarados como "por ' +
        'decidir" em vez de emparelhados a adivinhar. Um relatório que ' +
        'diz "não sei" é melhor do que um que diz mal.',
    ],
  },
  {
    version: '0.2.0-beta.130',
    date: '2026-09-18',
    summary: 'Lentes: ver o modelo por regras.',
    added: [
      'Lentes (régua da esquerda). Uma lente é uma lista de regras: ' +
        'cada uma apanha elementos por classe IFC, nome ou modelo, e ' +
        'manda colorir, apagar ou esconder. A primeira regra que casa é ' +
        'a que vale, e o que não casar com nenhuma fica cinzento para o ' +
        'resto sobressair.',
      'Lente automática: escolhe-se o eixo e sai uma regra por valor ' +
        'distinto, já com cores e contagens, da mais frequente para a ' +
        'menos. É o que responde a "mostra-me isto por classe" sem ' +
        'escrever regra nenhuma.',
      'As lentes podem ser guardadas com nome, neste browser.',
    ],
    changed: [
      'Isto complementa o que já havia e não o substitui: o "Colorir ' +
        'por" pinta tudo por uma dimensão só e não esconde nada; o ' +
        'Filtro esconde e isola mas não pinta. Uma pergunta de ' +
        'coordenação como "paredes a vermelho, lajes a azul, esconde o ' +
        'resto" não se respondia com nenhum dos dois.',
    ],
    fixed: [
      'Desligar a lente repõe sempre as cores e a visibilidade. Trocar ' +
        'de lente desfaz a anterior primeiro, para não ficarem ' +
        'elementos escondidos por uma lente que já não está ligada.',
    ],
  },
  {
    version: '0.2.0-beta.129',
    date: '2026-09-18',
    summary: 'Uma régua à esquerda com tudo o que se pode abrir.',
    added: [
      'Régua de painéis na margem esquerda: estrutura, propriedades, ' +
        'filtro, aparência, colorir por, viewpoints, pins BCF, mapa, ' +
        'nuvem de pontos e IDS rápido, mais os módulos que o workspace ' +
        'tiver ligados. Até aqui era preciso saber em que separador do ' +
        'friso estava o interruptor de cada painel, e os módulos nem no ' +
        'friso estavam.',
      'A régua pode ser personalizada: mudar a ordem e esconder o que ' +
        'não se usa, guardado neste browser. Entradas novas da ' +
        'aplicação entram sempre no fim, para quem personaliza não ' +
        'deixar de ver o que for acrescentado.',
    ],
    changed: [
      'A régua não substitui o friso. O friso é para comandos, fazer ' +
        'uma coisa; a régua é para sítios, abrir um painel.',
      'O guia de dois minutos ganhou um passo sobre a régua.',
    ],
  },
  {
    version: '0.2.0-beta.128',
    date: '2026-09-18',
    summary: 'Guia de dois minutos para quem chega agora.',
    added: [
      'Um guia de dois minutos, com seis passos que apontam para os ' +
        'sítios reais da aplicação: abrir um modelo, como o friso está ' +
        'agrupado, a pesquisa com "/", e a barra de estado. Aparece uma ' +
        'vez, num canto, a quem nunca o viu neste browser, e reabre-se ' +
        'pelo botão da bússola ao lado dos atalhos de teclado.',
      'Um passo cujo elemento não esteja no ecrã é saltado em vez de ' +
        'mostrado. Um guia que aponta para o vazio faz quem está a ' +
        'aprender concluir que a aplicação está partida.',
    ],
  },
  {
    version: '0.2.0-beta.127',
    date: '2026-09-18',
    summary: 'Pesquisa global e barra de estado.',
    added: [
      'Pesquisa global na barra de topo, com o atalho "/". Procura por ' +
        'GlobalId, nome ou classe IFC, e escolher um resultado ' +
        'selecciona e enquadra o elemento. Serve sobretudo para quem ' +
        'recebe um GlobalId num relatório de IDS, num tópico BCF ou num ' +
        'email e até aqui não tinha por onde começar.',
      'Barra de estado no fundo: modelos e entidades carregadas, quantos ' +
        'elementos estão seleccionados, triângulos e chamadas de desenho ' +
        'da última imagem, imagens por segundo, memória, motor e versão. ' +
        'São os números que respondem a "porque é que isto está lento", ' +
        'e estavam escondidos no painel de diagnóstico.',
    ],
    changed: [
      'A classificação de entidades IFC por família saiu do painel de ' +
        'Entidades para um módulo partilhado, porque a pesquisa precisa ' +
        'da mesma resposta. A pesquisa só indexa o que se consegue ' +
        'seleccionar: sem isso, procurar "wall" devolvia o ' +
        'Pset_WallCommon à frente das paredes.',
    ],
  },
  {
    version: '0.2.0-beta.126',
    date: '2026-09-18',
    summary:
      'A aplicação passa a dizer quando saiu versão nova, e a explicar ' +
      'o friso a quem o viu mudar.',
    added: [
      'Um aviso quando está publicada uma versão mais recente do que a ' +
        'que este separador está a correr. Um separador aberto há horas ' +
        'nunca volta a pedir o HTML, por isso continuava a correr o ' +
        'código do dia anterior sem nada que o dissesse. O aviso não ' +
        'recarrega sozinho: pode haver trabalho por gravar no ecrã.',
      'Uma nota "o que mudou de sítio", com a lista dos comandos que ' +
        'trocaram de separador na beta.123 e a razão de cada um. Só ' +
        'aparece a quem usou o friso antigo neste browser, e desaparece ' +
        'para sempre ao ser dispensada.',
    ],
    fixed: [
      'O vigia de versão travava o seu próprio primeiro pedido quando o ' +
        'relógio devolvia números pequenos. Apanhado por um teste antes ' +
        'de sair.',
    ],
  },
  {
    version: '0.2.0-beta.125',
    date: '2026-09-18',
    summary: 'A plataforma passa a abrir E57.',
    added: [
      'Nuvens de pontos em E57, a norma aberta ASTM E2807, com cor. ' +
        'É o formato que sai da maior parte dos scanners e do ReCap, e ' +
        'até aqui obrigava a converter antes de abrir.',
      'O leitor é o web-e57 (Apache-2.0, Prolincur Technologies), a ' +
        'biblioteca Rust e57 compilada para WebAssembly. São 283 kB que ' +
        'só se carregam a quem abre mesmo um E57, e o ficheiro vem da ' +
        'nossa origem, nunca de um CDN. Está nos créditos, com a norma.',
    ],
    changed: [
      'A limitação que dizia que o E57 não era lido "porque não existe ' +
        'leitor para browser" estava errada e foi corrigida. O que há ' +
        'mesmo é um limite de tamanho: acima de 80 MB o ficheiro é ' +
        'recusado com essa explicação, porque a conversão precisa de ' +
        'ter a nuvem inteira em memória de uma só vez.',
    ],
  },
  {
    version: '0.2.0-beta.124',
    date: '2026-09-18',
    summary: 'Nuvens de pontos: a cor do PTS e os ficheiros que o ' +
      'browser não consegue ler.',
    fixed: [
      'Um PTS abria com as cores erradas. O ficheiro tem sete colunas ' +
        '(x y z intensidade r g b) e liam-se três colunas cedo demais, ' +
        'o que dava a intensidade do scanner como se fosse vermelho. ' +
        'Agora distingue-se o PTS do XYZ pelo número de colunas.',
      'A escala da cor passa a decidir-se olhando ao ficheiro inteiro. ' +
        'Antes era linha a linha, e um ponto escuro num ficheiro de 0 ' +
        'a 255 era confundido com um ponto vivo num ficheiro de 0 a 1.',
      'Escolher um ficheiro que o browser não consegue ler dava uma ' +
        'mensagem em inglês a falar de permissões. Passa a dizer, em ' +
        'português, o que costuma ser: um ficheiro do OneDrive ou do ' +
        'Google Drive que ainda não foi descarregado para o disco, ou ' +
        'movido depois de escolhido. Vale para o IFC e para a nuvem.',
    ],
  },
  {
    version: '0.2.0-beta.123',
    date: '2026-09-18',
    summary:
      'O friso passa a agrupar pelo que se quer fazer, e não pelo sítio ' +
      'onde a coisa aparece.',
    changed: [
      'O separador Cena chama-se Aparência e fica só com o modo de ' +
        'desenhar: cores, arestas, fundo, fantasma, e as referências ' +
        '(grelha, levels, grids IFC, origem). "Cena" e "Vista" queriam ' +
        'dizer a mesma coisa e ninguém adivinhava em qual estava o quê.',
      'As Vistas 2D, os Viewpoints e o Mapa voltaram para o separador ' +
        'Vista, que é como se lhes chama. Tinham ido parar ao outro.',
      'Os pins BCF passaram para junto dos Viewpoints: um pin é um ' +
        'tópico BCF com viewpoint, não uma forma de desenhar o modelo.',
      'A nuvem de pontos passou para Ficheiro, junto do Abrir modelo. O ' +
        'gesto é importar um ficheiro, como abrir um IFC.',
      'O botão Aparência, que abre o painel de pintura, saiu da ' +
        'Visibilidade e foi para o separador com o mesmo nome.',
    ],
  },
  {
    version: '0.2.0-beta.122',
    date: '2026-09-18',
    summary: 'O ecrã de entrada passa a dizer porque foi recusado.',
    fixed: [
      'Uma falha ao entrar que não fosse password errada nem sessão ' +
        'noutro dispositivo dizia apenas "Pedido recusado pelo ' +
        'servidor". Não distinguia um erro do servidor de um pedido mal ' +
        'formado, e obrigava a abrir as ferramentas do browser para ' +
        'saber o que se passou. Passa a mostrar a explicação que o ' +
        'servidor deu e, quando ele não dá nenhuma, o código de estado.',
    ],
  },
  {
    version: '0.2.0-beta.121',
    date: '2026-09-18',
    summary: 'As nuvens de pontos passam a abrir LAS e LAZ.',
    added: [
      'LAS e LAZ nas nuvens de pontos, a juntar ao PLY, XYZ e PTS. É ' +
        'onde os levantamentos chegam, e deixa de ser preciso converter ' +
        'antes de abrir. O leitor entra por um pedaço à parte: quem ' +
        'nunca abre uma nuvem não paga o descompressor.',
      'Um LAS 1.4 é recusado com a explicação e o que fazer, em vez de ' +
        'deixar sair a mensagem em inglês de dentro da biblioteca. O ' +
        'leitor vai até à versão 1.3 do formato.',
    ],
    fixed: [
      'O painel das nuvens dizia "Importar PLY, XYZ ou PTS" num sítio e ' +
        'aceitava outra coisa noutro. A lista de formatos passa a viver ' +
        'num só sítio, e o selector de ficheiros e as mensagens de erro ' +
        'lêem-na de lá.',
    ],
  },
  {
    version: '0.2.0-beta.120',
    date: '2026-09-18',
    summary:
      'O friso deixa de obrigar a deslizar de lado, e os menus deixam ' +
      'de ficar presos dentro dele.',
    added: [
      'Separador Cena, novo: o que está na cena e como se apresenta. ' +
        'Apresentação (cores, arestas, fantasma, fundo), Referências ' +
        '(grelha, levels, grids IFC, origem), Camadas (nuvens de pontos, ' +
        'pins BCF) e Vistas e mapa (vistas 2D, viewpoints, mapa, ' +
        'federação).',
    ],
    changed: [
      'O separador Vista tinha 27 comandos em sete painéis, mais do ' +
        'dobro de qualquer outro, e não cabia num ecrã de portátil. ' +
        'Fica com doze: navegar, seleccionar e esconder. Nenhum ' +
        'separador passa a transbordar a 1280 px de largura.',
      'O "Esconder spaces" passou de Cena para Visibilidade, que é onde ' +
        'estão as outras maneiras de tirar coisas da frente.',
    ],
    fixed: [
      'Os menus dos comandos com seta (modelos de exemplo, anotações, ' +
        'plano de trabalho, unidades, opacidade do fantasma, filtros de ' +
        'grelhas e de origens) abriam DENTRO da barra, que tem scroll ' +
        'horizontal. Eram recortados por ela e obrigavam a deslizar a ' +
        'barra para os ler. Passam a ser desenhados por cima da área do ' +
        'modelo, onde há espaço, e viram-se para cima quando não cabem ' +
        'em baixo.',
    ],
  },
  {
    version: '0.2.0-beta.119',
    date: '2026-09-17',
    summary: 'A Ajuda passa a cobrir as extensões que a aplicação já tinha.',
    added: [
      'Seis entradas novas na Ajuda: rever desenhos PDF (incluindo a ' +
        'calibração das medições e a janela em ecrã separado), os ' +
        'marcadores dos tópicos BCF sobre o modelo e como os filtrar, ' +
        'guardar o modelo em Fragments e marcar versões, nuvens de ' +
        'pontos e desvio, o grafo e o índice de documentação, e o ' +
        'snapshot MCP.',
      'Cada uma diz também o que a funcionalidade NÃO faz: o que um ' +
        '.frag não traz, que o desvio às nuvens não é metrologia, que o ' +
        'índice não prova circularidade, e que um marcador aparece ' +
        'mesmo com o elemento tapado.',
    ],
    changed: [
      'A descrição do repositório e a árvore de pastas no CLAUDE.md ' +
        'ainda listavam módulos que nunca existiram (qto, excel-to-bcf) ' +
        'e ignoravam metade dos que existem. Passam a descrever o que ' +
        'lá está.',
    ],
  },
  {
    version: '0.2.0-beta.118',
    date: '2026-09-09',
    summary: 'Backlog técnico: o que faltava, e o que já estava feito.',
    changed: [
      'O motor de testes sobe para o vitest 5, que fecha os dois últimos ' +
        'avisos de segurança abertos. Os 2832 testes passam sem uma ' +
        'alteração. A plataforma não muda: o vitest só corre em ' +
        'desenvolvimento.',
      'Os testes passam a guardar as transformações entre corridas. Não ' +
        'muda o relógio, mas corta o trabalho repetido de transformar ' +
        'módulos que não mudaram, que era metade do tempo contabilizado.',
    ],
    fixed: [
      'Sete dos trinta avisos de lint pendentes. Dois eram reais, nas ' +
        'janelas destacadas, e passaram a usar o padrão que a casa já ' +
        'tinha. Cinco eram falsos positivos: a regra não vê para lá do ' +
        '`await` e nenhum deles escreve estado antes dele. Ficam com a ' +
        'razão escrita ao lado, para quem passar poder reverificar.',
    ],
  },
  {
    version: '0.2.0-beta.117',
    date: '2026-09-09',
    summary:
      'Revisão do código: créditos em falta, textos que já não ' +
      'descreviam a aplicação, e ficheiros mortos.',
    added: [
      'Créditos para o motor de revisão de PDF (Massing PDF, MIT), o ' +
        'PDF.js e o pdf-lib. O primeiro é redistribuído dentro do ' +
        'repositório, por isso é o que mais obrigava a estar creditado.',
      'A lista de Funcionalidades passa a incluir as extensões que ' +
        'entraram nas últimas versões, e o Assistente deixa de aparecer ' +
        'com quatro consultas quando tem sete.',
      'Seis limitações que a aplicação conhecia e não admitia: um .frag ' +
        'aberto directamente não traz o IFC, os pins BCF não testam ' +
        'oclusão, a revisão de PDF tem a interface em inglês, o desvio ' +
        'às nuvens de pontos é aproximado, o índice de documentação não ' +
        'prova circularidade, e o snapshot MCP é uma fotografia que ' +
        'leva propriedades.',
    ],
    fixed: [
      'Tudo o que lê o IFC (IDS, BoQ, Tabela, IFCEdit, escrita de ' +
        'georreferenciação) falhava sobre um modelo .frag com uma ' +
        'mensagem em inglês vinda de dentro do parser. Passa a dizer o ' +
        'que se passa e o que fazer, uma vez só, no sítio por onde ' +
        'todos passam.',
      'Cinco sítios mandavam carregar num botão "Abrir IFC" que passou ' +
        'a chamar-se "Abrir modelo".',
    ],
    changed: [
      'O fflate, que descompacta os ficheiros de sessão, os .ifczip e os ' +
        '.bcfzip, sobe para 0.8.3: a versão anterior podia entrar em ' +
        'ciclo infinito com um ZIP64 malformado, e quem abre um ' +
        'ficheiro estragado merece um erro e não a aba pendurada.',
    ],
    removed: [
      'Um componente reformado que continuava no código, três atalhos ' +
        'de reposição para testes que nunca chegaram a ser usados, e ' +
        'três ficheiros de reencaminhamento que sobraram da unificação ' +
        'do motor IDS.',
    ],
  },
  {
    version: '0.2.0-beta.116',
    date: '2026-08-20',
    summary:
      'Os pins BCF sobre o modelo passam a ter interruptor e filtros.',
    added: [
      'Painel "Pins BCF" (Vista > Cena): liga e desliga os marcadores ' +
        'sobre o modelo, e escolhe quais aparecem, por estado e por ' +
        'ficheiro BCF, com a contagem de cada um ao lado. Um botão ' +
        'próprio para esconder os fechados e resolvidos, que é o corte ' +
        'que mais vezes se quer.',
      'Os pins dos tópicos fechados passam a ser verdes, e o que o pin ' +
        'diz ao passar o rato traz o estado e o ficheiro de onde vem.',
    ],
    fixed: [
      'Uma detecção de conflitos triada em BCF punha um marcador por ' +
        'conflito sobre o modelo, e não havia por onde os desligar nem ' +
        'escolher quais mostrar. Um marcador que não se consegue tirar ' +
        'deixa de ser informação e passa a ser estorvo.',
    ],
  },
  {
    version: '0.2.0-beta.115',
    date: '2026-08-20',
    summary:
      'Nas vistas 2D: o Esc fecha o comando, e a janela de recorte ' +
      'ajusta-se depois de traçada.',
    added: [
      'A janela de recorte passa a ter pegas nos quatro lados, além dos ' +
        'cantos, e arrasta-se inteira pela moldura. Alargar só para um ' +
        'lado deixa de obrigar a mexer noutro, que era o que deixava a ' +
        'janela sempre torta.',
    ],
    fixed: [
      'Traçar a janela de recorte não fechava o comando. Como as pegas ' +
        'de ajuste só aparecem com a ferramenta desligada, a janela ' +
        'ficava impossível de redimensionar, e o arrasto seguinte ' +
        'começava uma janela nova em cima da anterior. Recortar e ' +
        'alinhar passam a terminar quando acabam, como qualquer comando.',
      'O Esc era roubado por coisas que não eram o comando a correr: uma ' +
        'anotação seleccionada minutos antes engolia o primeiro Esc, e ' +
        'parecia que o comando não fechava. Agora, com um comando a ' +
        'correr, o primeiro Esc larga o que está a meio e o segundo sai ' +
        'do comando; sem comando nenhum, o Esc desfaz selecções.',
      'Se a captura do ponteiro falhasse, a pega deixava de responder ' +
        'sem nada a explicar porquê. O arrasto passa a funcionar na ' +
        'mesma.',
    ],
  },
  {
    version: '0.2.0-beta.114',
    date: '2026-08-20',
    summary: 'A revisão de PDF abre numa janela do browser à parte.',
    added: [
      'Botão "Janela própria" na revisão de PDF: o desenho passa para ' +
        'uma janela do browser separada, para ficar num monitor com o ' +
        'modelo no outro. É o mesmo mecanismo das Vistas 2D e dos ' +
        'Conflitos, e a janela partilha o estado da aplicação, não é uma ' +
        'segunda instância.',
    ],
    fixed: [
      'Destacar destruía e recriava o motor de PDF, e por isso a janela ' +
        'nova abria vazia. O desenho que estava aberto é reposto do ' +
        'outro lado; as anotações já sobreviviam sozinhas. Fechar a ' +
        'revisão continua a esquecer o desenho, que é o que esse gesto ' +
        'quer dizer.',
    ],
  },
  {
    version: '0.2.0-beta.113',
    date: '2026-08-20',
    summary:
      'Correcção no servidor: duas migrações a correr ao mesmo tempo ' +
      'deitavam o deploy abaixo.',
    fixed: [
      'O deploy do servidor falhava com "expected to match one row when ' +
        'updating 0017 to 0018", que é o que acontece quando dois ' +
        'processos correm as migrações ao mesmo tempo: ambos leem a ' +
        'mesma versão, ambos migram, e o segundo já não encontra a linha ' +
        'que esperava. As migrações passam a correr uma de cada vez. Não ' +
        'há alteração nenhuma na aplicação em si.',
    ],
  },
  {
    version: '0.2.0-beta.112',
    date: '2026-08-20',
    summary:
      'As extensões que estavam à espera de decisão ficam ligadas, com o ' +
      'que foi preciso corrigir para poderem estar.',
    added: [
      'Revisão de PDF: redlines, medições calibradas, pins, comparação ' +
        'de revisões e intercâmbio BCF/XFDF, tudo dentro do browser. A ' +
        'interface do motor está em inglês e isso está dito no cabeçalho ' +
        'do módulo.',
      'Nuvens de pontos (PLY, XYZ, PTS) na cena, com desvio aproximado ' +
        'ao IFC por distância às caixas dos elementos.',
      'Grafo local do modelo, com procura por classe, nome, propriedade ' +
        'ou GlobalId, e um índice de quanta documentação existe para ' +
        'reutilização.',
      'Snapshot MCP: exportar uma fotografia do modelo, só de leitura, ' +
        'para o Claude Code ou outro cliente MCP.',
      'Abrir Fragments (.frag) directamente, com o aviso de que esses ' +
        'modelos não trazem o IFC.',
    ],
    changed: [
      'O friso deixa de ter nove botões no painel Modelo. As Versões vão ' +
        'para a Comparação, o Point cloud para a Cena, e a revisão de ' +
        'PDF, o grafo e o snapshot MCP para um painel Ferramentas.',
      'Os painéis novos passam a mostrar-se premidos no friso e a fechar ' +
        'com a sessão, como o Comparar IFC e o IDS.',
      'O motor de PDF passa a vir construído dentro do repositório. Vinha ' +
        'de um repositório GitHub e reconstruía-se do zero em cada ' +
        'instalação, incluindo no build de produção.',
    ],
    fixed: [
      'O índice de documentação contava palavras encontradas no meio dos ' +
        'valores em vez de relações declaradas: dizia que nenhum elemento ' +
        'tinha identificador num modelo em que todos têm, e que nenhum ' +
        'tinha tipo num modelo com 170 relações de tipo. Agora conta o ' +
        'que está mesmo declarado, e não conta unidades nem valores de ' +
        'propriedade como elementos por documentar.',
      'A procura no grafo cortava aos 50 resultados sem o dizer.',
      'O servidor MCP registava-se com o mesmo nome do servidor que já ' +
        'existe e lê os IFC do disco, substituindo-o na configuração. ' +
        'Passou a ter nome próprio.',
      'Um modelo aberto em .frag respondia "zero" a quem perguntasse ao ' +
        'assistente. Passa a dizer que não traz o IFC, e a lista de ' +
        'modelos marca-o.',
    ],
  },
  {
    version: '0.2.0-beta.111',
    date: '2026-08-20',
    summary:
      'Pacote de extensões da comunidade, com o motor a ficar onde ' +
      'estava: a versão nova continua a devolver caixas erradas.',
    added: [
      'Guardar o modelo activo em Fragments (.frag), e reutilizar a ' +
        'derivação do próprio IFC em cache: reabrir o mesmo ficheiro ' +
        'passa de cerca de cinco segundos para menos de um.',
      'Histórico de versões: guardar marcos do modelo e reabri-los ao ' +
        'lado do actual para comparar. Cada marco guarda o IFC, não a ' +
        'sua derivação, porque é o IFC que o Comparar IFC lê.',
      'Pins sobre o modelo para os viewpoints associados a tópicos BCF.',
      'Verificação de linha de visão entre dois elementos, no painel ' +
        'de conflitos.',
      'Zoom com inércia e com o pivô debaixo do cursor.',
      'Transparência que conserva os materiais de cada elemento em vez ' +
        'de os achatar todos no mesmo cinzento.',
      'Revisão de PDF, servidor MCP local, nuvens de pontos e grafo do ' +
        'modelo ficam no código mas desligados, à espera de decisão.',
    ],
    fixed: [
      'A subida do motor ThatOpen para fragments 3.4.7 foi revertida ' +
        'antes de sair: medido no browser, o 3.4.7 devolve uma caixa ' +
        'de 282844 x 200003 x 282844 metros onde o 3.4.3 devolve os ' +
        '64,80 x 2,57 x 24,45 certos, e todas as caixas por elemento ' +
        'vêm a mais de cem mil metros do sítio. Isso parte a pegada no ' +
        'mapa, o assentar no terreno, a federação e o enquadramento da ' +
        'câmara. O aviso de segurança que motivava a subida foi ' +
        'fechado sem tocar no motor.',
      'A cache de Fragments servia geometria derivada por outra versão ' +
        'do motor, sem nada o denunciar. Passa a guardar a versão que a ' +
        'produziu e a descartar o que não bate.',
      'Abrir um .frag ficou desligado: o ficheiro não traz o IFC, e o ' +
        'IDS, o BoQ, a Tabela, o IFCEdit e o assistente leem o IFC. ' +
        'Responderiam zero, que é pior do que não responder.',
      'O nome de um marco perdia os acentos pelo meio: "revisão" saía ' +
        '"revisa-o".',
    ],
  },
  {
    version: '0.2.0-beta.110',
    date: '2026-08-14',
    summary:
      'O assistente passa a saber agrupar por tipo, e o "como cheguei ' +
      'aqui" deixa de aparecer em branco.',
    added: [
      'Nova consulta que agrupa os elementos de uma classe pelos seus ' +
        'TIPOS, com a contagem de cada um e um exemplo por tipo. ' +
        'Responde a "que tipos de luminárias existem" com dois tipos em ' +
        'vez de vinte e cinco elementos, e é o que permite isolar um ' +
        'exemplo de cada.',
    ],
    fixed: [
      'Pedir para isolar um exemplo de cada tipo isolava a classe ' +
        'inteira, porque não havia consulta que soubesse o que é um ' +
        'tipo. O nome de um elemento traz o identificador da instância ' +
        'no fim, e sem o cortar cada elemento parecia um tipo diferente.',
      'A lista "Como cheguei aqui" aparecia com linhas em branco quando ' +
        'o servidor do modelo põe o nome da consulta noutro sítio da ' +
        'resposta. Passam a ser aceites as duas formas.',
    ],
  },
  {
    version: '0.2.0-beta.109',
    date: '2026-08-14',
    summary:
      'O assistente deixa de negar classes que o modelo tem, e passa a ' +
      'conseguir isolá-las.',
    fixed: [
      'Perguntar por uma classe que a barra lateral mostra podia dar ' +
        '"não há nenhum", e o assistente chegava a contradizer-se na ' +
        'mesma conversa. A causa era usar duas fontes diferentes: a ' +
        'lista de classes vinha de um lado e a procura de elementos de ' +
        'outro, e quando discordavam ganhava a que negava.',
      'O resumo do modelo passa a listar as classes do MESMO catálogo ' +
        'que a barra lateral mostra, com as contagens, em vez de só as ' +
        'classes agrupadoras.',
      'Isolar uma classe passa a funcionar mesmo para elementos que o ' +
        'leitor detalhado não consegue abrir: a selecção não precisa ' +
        'dele. É o que fazia "isola as tubagens" falhar num modelo com ' +
        'tubagens.',
    ],
  },
  {
    version: '0.2.0-beta.108',
    date: '2026-08-14',
    summary:
      'Alinhar a vista passa a ser um clique numa fachada, e o cubo ' +
      'fica com uma face alinhada com ela.',
    changed: [
      'Deixa de ser preciso traçar uma linha com dois pontos: basta ' +
        'clicar na fachada. A direcção vem da própria face, portanto é ' +
        'exacta e não depende de onde se clicou nem de haver snap. Dois ' +
        'pontos apanhados à mão davam a mesma direcção com erro, tanto ' +
        'maior quanto mais perto estivessem.',
      'Depois de alinhar, o cubo de navegação mostra a face alinhada ' +
        'com a fachada escolhida, e as vistas padrão passam a contar a ' +
        'partir dela.',
      'Clicar numa superfície horizontal (um pavimento, um tecto) é ' +
        'recusado com uma explicação: não tem direcção horizontal que ' +
        'sirva para alinhar.',
    ],
  },
  {
    version: '0.2.0-beta.107',
    date: '2026-08-14',
    summary:
      'Ao traçar a linha de alinhamento, os elementos deixam de acender ' +
      'sob o cursor.',
    fixed: [
      'Enquanto se traçava a linha, os elementos continuavam a acender ' +
        'sob o cursor, o que fazia o gesto parecer uma selecção em vez ' +
        'de um traçado. Já havia essa guarda para as ferramentas de ' +
        'medição e faltava aqui: quem está a traçar uma direcção não ' +
        'está a escolher elementos.',
    ],
  },
  {
    version: '0.2.0-beta.106',
    date: '2026-08-14',
    summary:
      'O "Alinhar vista" passa a estar no friso, com nome, em vez de ' +
      'escondido num símbolo do cubo de navegação.',
    fixed: [
      'A opção de traçar a linha que fica horizontal estava só num botão ' +
        'de 20 píxeis do cubo de navegação, identificado por um símbolo. ' +
        'Ninguém a encontrava. Passa a ser um comando com nome no friso ' +
        'Vista, ao lado do Focar e do Section box.',
    ],
  },
  {
    version: '0.2.0-beta.105',
    date: '2026-08-14',
    summary:
      'Alinhar pela linha no 3D passa a ser só uma acção de câmara: o ' +
      'cubo deixa de marcar uma orientação que não correspondia a nada.',
    fixed: [
      'Ao traçar a linha no 3D, além de rodar a câmara era também ' +
        'fixado o "norte do projeto". São duas grandezas com convenções ' +
        'diferentes, e o cubo de navegação somava-as: ficava a apontar ' +
        'uma orientação sem significado. Alinhar pela linha passa a ' +
        'mexer só na câmara; redefinir o que Frente e Trás querem dizer ' +
        'continua a ser o outro botão.',
      'Ao entrar no modo de traçar, e ao picar o primeiro ponto, a ' +
        'selecção é limpa: estava-se a traçar uma linha e viam-se ' +
        'elementos a acender, o que fazia parecer que se estava a ' +
        'seleccionar em vez de a traçar.',
    ],
  },
  {
    version: '0.2.0-beta.104',
    date: '2026-08-14',
    summary:
      'A janela de recorte deixa de se agarrar ao modelo e passa a ' +
      'poder ser ajustada pelos cantos.',
    fixed: [
      'Traçar a janela de recorte obrigava a apanhar pontos do modelo ' +
        '(snap), o que impedia de a pôr onde se queria. Uma janela de ' +
        'vista não é geometria: passa a seguir o cursor livremente.',
      'Depois de traçada, a janela não se podia alterar: era preciso ' +
        'traçá-la de novo. Passa a ter pegas nos quatro cantos, com o ' +
        'rectângulo a acompanhar enquanto se arrasta.',
    ],
  },
  {
    version: '0.2.0-beta.103',
    date: '2026-08-14',
    summary:
      'Alinhar a vista 2D estava a rodar para o lado errado: a fachada ' +
      'picada ficava com o dobro da inclinação em vez de horizontal.',
    fixed: [
      'O "Alinhar a" das vistas 2D rodava o desenho no sentido contrário ' +
        'ao devido. Picar dois pontos numa fachada deixava-a mais torta ' +
        'do que estava, com uma mensagem a dizer que a vista tinha sido ' +
        'alinhada. Agora a fachada picada fica mesmo horizontal.',
    ],
  },
  {
    version: '0.2.0-beta.102',
    date: '2026-08-14',
    summary:
      'A janela de recorte passa a ver-se, e o alinhar pela linha ' +
      'traçada roda a câmara para uma posição calculada em vez de um ' +
      'ângulo relativo.',
    fixed: [
      'A janela de recorte era invisível: o desenho aparecia cortado e ' +
        'não havia como saber onde estava a janela nem que existia. ' +
        'Passa a ser desenhada a tracejado, e o rectângulo aparece já ' +
        'enquanto se arrasta.',
      'Rodar a vista removia a janela de recorte em silêncio, o que ' +
        'parecia um comportamento estranho. Continua a ser removida (foi ' +
        'desenhada nos eixos antigos e deixaria de querer dizer o ' +
        'mesmo), mas agora é dito.',
      'Traçar a linha para alinhar podia rodar a vista para o lado ' +
        'errado: era pedida uma rotação relativa, cuja convenção de ' +
        'sinal depende da biblioteca de controlo da câmara. Passa a ser ' +
        'calculada a posição da câmara e a câmara é posta lá, o que não ' +
        'depende dessa convenção. A distância ao alvo e a inclinação ' +
        'mantêm-se: só o rumo muda.',
    ],
  },
  {
    version: '0.2.0-beta.101',
    date: '2026-08-14',
    summary:
      'Traçar a linha passa a rodar a vista na hora, em vez de só ' +
      'declarar que alinhou.',
    fixed: [
      'Ao traçar a linha, a mensagem dizia que a vista tinha sido ' +
        'alinhada e nada mudava no ecrã: era definido o que Frente, ' +
        'Trás, Dir e Esq querem dizer, mas a câmara ficava onde estava. ' +
        'Só se via o efeito ao clicar numa vista padrão. Agora a vista ' +
        'roda no momento, e a linha traçada fica horizontal onde se ' +
        'está.',
      'A rotação escolhe sempre o caminho curto: nunca mais de 90°, ' +
        'porque a direcção oposta deixa a linha igualmente horizontal. ' +
        'Antes, alinhar uma fachada podia dar meia volta ao edifício.',
    ],
  },
  {
    version: '0.2.0-beta.100',
    date: '2026-08-14',
    summary:
      'Traçar no 3D a linha que fica horizontal: dois cliques sobre o ' +
      'modelo e a vista alinha por essa direcção.',
    added: [
      'Botão de traçar no cubo de navegação. Clicas dois pontos sobre o ' +
        'modelo (uma fachada, um eixo, um alinhamento de pilares) e essa ' +
        'direcção passa a ser a horizontal do ecrã nas vistas padrão. É ' +
        'a forma precisa de alinhar, contra os saltos de 90°.',
      'Só conta a componente horizontal da linha: uma fachada inclinada ' +
        'ou o beirado de uma cobertura alinham pela sua projecção em ' +
        'planta, que é o que se espera.',
    ],
  },
  {
    version: '0.2.0-beta.99',
    date: '2026-08-14',
    summary:
      'Rotação de apresentação no 3D: alinhar o edifício com o ecrã sem ' +
      'lhe tocar.',
    added: [
      'Botão no cubo de navegação que roda a apresentação. Muda o que ' +
        'Frente, Trás, Dir e Esq querem dizer, e fica: é o "norte do ' +
        'projeto". Um segundo botão volta à orientação original.',
    ],
    changed: [
      'A bússola passa a ser sempre o mundo real: carregar no N leva ao ' +
        'norte verdadeiro, esteja a apresentação rodada como estiver. ' +
        'Antes seguia as vistas padrão.',
      'A rotação não toca no modelo, na georreferenciação nem no norte ' +
        'verdadeiro, e é uma só para a vista em vez de uma por modelo, ' +
        'portanto não pode desalinhar uma federação de modelos ' +
        'georreferenciados. Há testes que o prendem.',
    ],
  },
  {
    version: '0.2.0-beta.98',
    date: '2026-08-14',
    summary:
      'Vistas 2D: janela de recorte e rotação da vista, incluindo ' +
      'alinhar por dois pontos picados no desenho.',
    added: [
      'Recortar: dois cliques definem uma janela e o desenho passa a ' +
        'mostrar só o que lá está dentro. Afecta também o PDF e o DXF, ' +
        'como a crop region de qualquer CAD. Há um botão para a remover.',
      'Alinhar a: dois cliques sobre uma direcção do desenho (uma ' +
        'fachada, um eixo, uma parede) e a vista roda para essa direcção ' +
        'ficar horizontal.',
      'Rodar a vista 90° em qualquer sentido, para o caso simples.',
    ],
    changed: [
      'A rotação mexe nos eixos do desenho e em mais nada. A geometria, ' +
        'a georreferenciação e as posições relativas entre modelos ' +
        'federados ficam intactas, porque nenhuma matriz de modelo é ' +
        'tocada. Uma vista rodada é uma maneira de olhar, não uma ' +
        'alteração ao edifício.',
    ],
  },
  {
    version: '0.2.0-beta.97',
    date: '2026-08-14',
    summary:
      'Cada resposta passa a mostrar como lá chegou: que consultas fez e ' +
      'com que critérios.',
    added: [
      'Debaixo de cada resposta há um "Como cheguei aqui" que lista as ' +
        'consultas feitas ao modelo COM os argumentos: que classe, que ' +
        'quantidade, que filtro, que piso. É lido do que aconteceu de ' +
        'facto, não do que o assistente diz ter feito, portanto serve ' +
        'para o confrontar.',
      'O assistente é obrigado a explicar o método sem que lho peçam: ' +
        'que consultas fez, com que critérios, e o que ficou de fora. ' +
        'Quando o resultado depende de uma escolha dele, essa escolha faz ' +
        'parte da resposta.',
    ],
  },
  {
    version: '0.2.0-beta.96',
    date: '2026-08-14',
    summary:
      'Somas de área passam a dizer que âmbito cobrem, e há filtro por ' +
      'piso. Uma soma de todos os pisos não pode passar por área de ' +
      'implantação.',
    added: [
      'As somas aceitam um piso: a área de implantação é a das lajes do ' +
        'piso térreo, não a soma das lajes do edifício inteiro.',
    ],
    fixed: [
      'Perguntar a área de implantação somava as lajes de todos os ' +
        'pisos, o que num edifício de vários pisos dá um número várias ' +
        'vezes maior. A soma de áreas passa a avisar, no próprio ' +
        'resultado, quando atravessa mais do que um piso, e a dizer ' +
        'quais. O aviso está nos dados e não só nas instruções, para não ' +
        'depender de o modelo se lembrar.',
      'Grandezas com definição legal ou técnica (implantação, área bruta ' +
        'de construção, área útil, índice de utilização) deixam de poder ' +
        'ser inventadas a partir do que houver à mão: o assistente é ' +
        'obrigado a dizer o que somou e a avisar quando o que calculou ' +
        'não é a grandeza pedida.',
    ],
  },
  {
    version: '0.2.0-beta.95',
    date: '2026-08-14',
    summary:
      'O nome da classe deixa de ter de ser escrito ao milímetro, e ' +
      'quando não existe a resposta diz quais existem.',
    fixed: [
      'Perguntar pelos "buildingelementproxy" dava zero num modelo com ' +
        '131 deles, porque a classe se chama IFCBUILDINGELEMENTPROXY e a ' +
        'consulta procurava à letra o que lhe davam. O nome passa a ser ' +
        'resolvido contra as classes que o modelo tem mesmo: sem ' +
        'prefixo, em minúsculas ou por correspondência parcial única, ' +
        'tudo chega ao mesmo sítio.',
      'Quando a classe não existe de todo, a resposta lista as classes ' +
        'presentes no modelo com as suas contagens, em vez de um "não há" ' +
        'que parecia um facto sobre o modelo e era um erro de escrita.',
    ],
  },
  {
    version: '0.2.0-beta.94',
    date: '2026-08-14',
    summary:
      'A conversa do assistente deixa de se perder ao sair do módulo, e ' +
      'os erros passam a poder ser copiados.',
    fixed: [
      'Sair do assistente e voltar deitava a conversa inteira fora, ' +
        'incluindo o contexto que o modelo já tinha sobre o modelo ' +
        'aberto. Passa a viver no estado da sessão, como o IDS e a ' +
        'Tabela. Continua a ser limpa pelo "Fechar sessão": uma conversa ' +
        'sobre o modelo de um cliente é trabalho da sessão, não uma ' +
        'preferência.',
      'Uma mensagem de erro que não se consegue copiar é um erro que ' +
        'ninguém consegue diagnosticar. Passa a ter botão para copiar, ' +
        'a ser selecionável, e a ir também para a consola.',
    ],
  },
  {
    version: '0.2.0-beta.93',
    date: '2026-08-14',
    summary:
      'Áreas por piso, selecção por classe, e o fim de um erro em que o ' +
      'assistente dizia ter isolado uma coisa tendo isolado outra.',
    added: [
      'As somas podem ser repartidas por piso: área de construção de ' +
        'cada piso passa a ter resposta directa.',
      'A selecção pode ser pedida por classe, sem depender de GlobalId ' +
        'de uma pergunta anterior. "Isola as lajes" é agora uma chamada ' +
        'só, com a classe dita na altura.',
    ],
    fixed: [
      'O assistente afirmou ter isolado as lajes tendo isolado paredes: ' +
        'reutilizou os identificadores da pergunta anterior. A selecção ' +
        'passa a devolver as CLASSES que ficaram mesmo seleccionadas, o ' +
        'que lhe tira a hipótese de afirmar o contrário e mostra ao ' +
        'utilizador o que aconteceu.',
      'Quando a quantidade pedida não existe, a resposta passa a listar ' +
        'as que existem naqueles elementos, em vez de mandar procurar ' +
        'noutro sítio. O engano corrige-se na volta seguinte.',
    ],
  },
  {
    version: '0.2.0-beta.92',
    date: '2026-08-14',
    summary:
      'O assistente passa a seleccionar e isolar elementos no 3D, e a ' +
      'encontrar os que NÃO têm uma propriedade.',
    added: [
      'Consulta que selecciona elementos no 3D pelos seus GlobalId, com ' +
        'opção de isolar (esconder tudo o resto). Pedir "mostra-me essas ' +
        'paredes" passa a fazer alguma coisa em vez de descrever.',
      'A procura aceita o inverso: os elementos que NÃO têm a ' +
        'propriedade. É o que responde a "que paredes não têm área".',
    ],
    fixed: [
      'Uma soma parcial era apresentada como se fosse o total. Passa a ' +
        'vir marcada como incompleta, com o número de elementos que ' +
        'ficaram de fora, e o assistente é obrigado a dizê-lo.',
      'Os números saíam no formato inglês: 423.791 lê-se em Portugal ' +
        'como quatrocentos e vinte e três mil, um erro de três ordens de ' +
        'grandeza numa frase bem escrita. Passam a sair em português.',
      'A unidade era inferida pelo modelo a partir do nome da ' +
        'quantidade. Passa a ser lida das unidades declaradas no ' +
        'projeto, que é o que evita reportar milímetros como metros.',
      'O assistente respondia por vezes em português do Brasil e com ' +
        'discursos longos quando não conseguia fazer o que se lhe pedia. ' +
        'As regras passam a exigir português de Portugal e uma frase só ' +
        'nesses casos.',
    ],
  },
  {
    version: '0.2.0-beta.91',
    date: '2026-08-14',
    summary:
      'O assistente diz em que passo vai, e passa a saber filtrar por ' +
      'propriedade e somar quantidades.',
    added: [
      'Enquanto pensa, mostra o que está a fazer e há quantos segundos: ' +
        '"a pensar", "a consultar o modelo: georreferenciacao". Com um ' +
        'modelo local cada volta demora, e sem isto não se distinguia ' +
        'trabalho de encravanço.',
      'A procura de elementos aceita filtro por propriedade. As paredes ' +
        'exteriores são classe IFCWALL com IsExternal igual a true.',
      'Nova consulta que soma quantidades sobre um conjunto filtrado, ' +
        'para perguntas como a área total das paredes exteriores. Diz ' +
        'sempre quantos elementos entraram na conta e quantos ficaram de ' +
        'fora por não terem a quantidade, em vez de dar um número sem ' +
        'contexto.',
    ],
    fixed: [
      'O assistente não conseguia identificar paredes exteriores. A ' +
        'propriedade existia no modelo; faltava-lhe uma consulta que ' +
        'soubesse filtrar por ela. A comparação é tolerante às formas ' +
        'que os exportadores usam para verdadeiro (true, T, .T., 1), ' +
        'porque comparar à letra daria zero paredes num modelo cheio ' +
        'delas.',
    ],
  },
  {
    version: '0.2.0-beta.90',
    date: '2026-08-14',
    summary:
      'Instruções passo a passo no próprio painel, e um teste que diz o ' +
      'que está mal quando o modelo local não responde.',
    added: [
      'Ao escolher "Modelo na minha máquina", o painel mostra os três ' +
        'passos com os comandos prontos a copiar, incluindo a linha da ' +
        'autorização já com o endereço desta instalação preenchido. Sem ' +
        'ter de o adivinhar nem de o escrever à mão.',
      'Botão "Testar ligação" que distingue as duas falhas prováveis: o ' +
        'servidor não estar a correr, e o servidor estar a correr mas ' +
        'não autorizar esta página. O browser apresenta as duas de forma ' +
        'idêntica, e mandar procurar no sítio errado custa uma tarde.',
      'Quando a ligação funciona, o teste mostra que modelos estão ' +
        'disponíveis na máquina, para se poder escrever o nome certo.',
    ],
  },
  {
    version: '0.2.0-beta.89',
    date: '2026-08-14',
    summary:
      'O Assistente passa a poder usar um modelo na máquina do próprio ' +
      'utilizador, sem custo nenhum por pergunta.',
    added: [
      'Nova opção de serviço: "Modelo na minha máquina". O browser fala ' +
        'directamente com o Ollama ou o LM Studio de quem pergunta, sem ' +
        'passar pelo servidor da plataforma. Nem a pergunta nem o modelo ' +
        'saem do computador, e não há custo por pergunta.',
      'A Ajuda passa a explicar a instalação passo a passo, incluindo a ' +
        'autorização de origem que o Ollama exige.',
    ],
    changed: [
      'As regras que impedem o assistente de opinar continuam a vir do ' +
        'servidor, mesmo quando o modelo é local: são servidas pelo ' +
        'backend e o browser leva-as ao modelo. Assim ficam escritas uma ' +
        'vez só e apertá-las aperta para todos.',
    ],
  },
  {
    version: '0.2.0-beta.88',
    date: '2026-08-14',
    summary:
      'Cada utilizador pode ter o seu próprio assistente: serviço, ' +
      'modelo e chave, na mesma instalação.',
    added: [
      'O modelo passa a ser escolha de cada utilizador, e não uma ' +
        'definição igual para todos. A escolha fica no browser de quem a ' +
        'faz, vale tanto com a chave da instalação como com chave ' +
        'própria, e não afeta mais ninguém: dois utilizadores da mesma ' +
        'instalação podem estar a usar modelos diferentes ao mesmo ' +
        'tempo.',
      'Quem traz a sua chave escolhe também o serviço, entre Anthropic e ' +
        'OpenAI.',
    ],
    changed: [
      'A escolha é do serviço, nunca do endereço, e isso é uma decisão ' +
        'de segurança: o nome escolhido mapeia para um de dois endereços ' +
        'escritos no código do servidor. Deixar o utilizador indicar o ' +
        'endereço faria o servidor emitir pedidos para onde ele ' +
        'quisesse, incluindo a rede interna, o que o tornaria um meio de ' +
        'sondar a própria infraestrutura.',
      'A troca de serviço exige chave própria, porque a chave da ' +
        'instalação é de um serviço e não serve noutro. Remover a chave ' +
        'leva o serviço atrás.',
    ],
  },
  {
    version: '0.2.0-beta.87',
    date: '2026-08-12',
    summary:
      'A ajuda da plataforma passa a explicar o Assistente e como usar ' +
      'um modelo de IA local.',
    added: [
      'Duas entradas novas na Ajuda: o que o Assistente faz e de onde ' +
        'vêm as respostas, e como configurar um modelo local (Ollama, LM ' +
        'Studio) com recomendação de qual escolher e o que esperar dele.',
      'O Assistente passa a aparecer na lista de Funcionalidades, para ' +
        'quem tem o módulo activo.',
    ],
  },
  {
    version: '0.2.0-beta.86',
    date: '2026-08-12',
    summary:
      'O Assistente deixa de estar preso a um fornecedor de IA.',
    added: [
      'Além da Anthropic, o assistente passa a falar o formato da ' +
        'OpenAI, que é o mesmo que a Azure, a Groq, o OpenRouter, a ' +
        'Mistral e a DeepSeek usam. Com um endereço alternativo, funciona ' +
        'também com um modelo a correr na própria máquina, via Ollama ou ' +
        'LM Studio, e nesse caso sem custo por pergunta.',
      'A tradução entre formatos vive no servidor. O browser continua a ' +
        'falar uma linguagem só e nunca sabe que fornecedores existem, ' +
        'pela mesma razão por que não aloja as guardas: o que é decisão ' +
        'de quem instala não deve estar do lado que se pode adulterar.',
    ],
    changed: [
      'O campo da chave passa a dizer de que serviço é a chave que está ' +
        'a pedir, em vez de assumir sempre a Anthropic.',
    ],
  },
  {
    version: '0.2.0-beta.85',
    date: '2026-08-12',
    summary:
      'O Assistente passa a funcionar com a chave de API do próprio ' +
      'utilizador, sem depender de a instalação pagar.',
    added: [
      'Cada utilizador pode trazer a sua própria chave de API. Fica ' +
        'guardada apenas no browser dele: é enviada em cada pergunta, ' +
        'usada, e deitada fora. Nunca é gravada na base de dados, nem no ' +
        'registo de auditoria, nem nos registos do servidor. A plataforma ' +
        'não fica com a chave de ninguém.',
      'Quem traz a sua chave não está sujeito ao limite diário de ' +
        'pedidos: esse limite existe para proteger o saldo da instalação, ' +
        'e quem paga o seu consumo não tem de ser racionado. O consumo ' +
        'com chave própria também não conta para esse limite.',
      'Mensagens de erro que distinguem os casos: chave recusada, conta ' +
        'sem saldo, ou demasiados pedidos seguidos. Antes era tudo o ' +
        'mesmo erro genérico.',
    ],
    fixed: [
      'O aviso de "falta a chave" aparecia mesmo quando o problema era ' +
        'outro, e continuava a aparecer depois de o utilizador ter posto ' +
        'a sua. Passa a dizer o que se passa de facto.',
    ],
  },
  {
    version: '0.2.0-beta.84',
    date: '2026-08-12',
    summary:
      'Novo módulo Assistente: perguntas em linguagem natural sobre o ' +
      'modelo aberto, com as consultas a correr no browser.',
    added: [
      'Módulo Assistente. Perguntas como "este modelo está ' +
        'georreferenciado e em que sistema de coordenadas", "que pisos tem ' +
        'e a que cotas" ou "quantas portas existem". Responde consultando ' +
        'o modelo com quatro ferramentas: resumo, georreferenciação, ' +
        'procura de elementos por classe, e propriedades de um elemento ' +
        'pelo GlobalId.',
      'As consultas correm no browser, sobre o modelo já carregado. O ' +
        'servidor só reencaminha a pergunta e o resultado: o IFC nunca ' +
        'sai da máquina de quem pergunta. As guardas ficam no servidor, ' +
        'não no cliente, para não poderem ser contornadas.',
      'O assistente responde a partir do que as ferramentas devolvem e ' +
        'diz que não sabe quando não há ferramenta que responda. Não ' +
        'opina sobre o modelo nem sobre decisões de engenharia, e isso é ' +
        'uma restrição do desenho, não uma afinação de texto.',
    ],
    changed: [
      'A leitura do modelo (georreferenciação, unidades, estrutura ' +
        'espacial, o que conta como elemento físico) passou para um ' +
        'segundo motor partilhado, usado pelo assistente e pelas ' +
        'ferramentas de linha de comando. Foi verificado que o relatório ' +
        'produzido não muda um único campo.',
    ],
  },
  {
    version: '0.2.0-beta.83',
    date: '2026-08-12',
    summary:
      'A leitura do modelo para validação IDS também deixa de estar ' +
      'escrita duas vezes.',
    changed: [
      'Na versão anterior ficou por unificar a camada que lê o modelo e ' +
        'monta a vista de cada elemento: como se resolve um property set, ' +
        'como se convertem unidades, o que se herda do tipo. Eram 937 ' +
        'linhas escritas duas vezes, e essas decisões contam para o ' +
        'resultado tanto como as regras. Passam a existir uma só vez. O ' +
        'módulo da plataforma ficou com 118 linhas: dar ao motor acesso ' +
        'ao modelo e pedir-lhe as mensagens em português.',
      'Os 318 casos oficiais da buildingSMART passam agora pelo mesmo ' +
        'código nos dois sítios, medidos por caminhos diferentes: na ' +
        'plataforma através do parser do browser, na linha de comando ' +
        'através do parser de Node. Antes provavam duas implementações ' +
        'parecidas; agora provam a mesma.',
    ],
  },
  {
    version: '0.2.0-beta.82',
    date: '2026-08-12',
    summary:
      'O motor de validação IDS passa a existir uma só vez, partilhado ' +
      'entre o módulo da plataforma e as ferramentas de linha de comando.',
    changed: [
      'A validação IDS estava implementada duas vezes: uma no módulo da ' +
        'plataforma e outra no motor autónomo que serve as ferramentas ' +
        'fora do browser. As duas passavam os 318 casos oficiais da ' +
        'buildingSMART, mas qualquer correção tinha de ser feita nos dois ' +
        'sítios, e o que não estivesse coberto por esses casos podia ' +
        'divergir sem ninguém dar por isso. Passa a haver um só motor, em ' +
        'frontend/packages/ids, e o módulo é a camada que o liga ao ' +
        'modelo carregado. São menos 8.700 linhas duplicadas.',
      'As razões de falha deixaram de estar escritas dentro do motor e ' +
        'passaram a uma tabela de mensagens. O texto que a plataforma ' +
        'mostra é exatamente o mesmo de antes, palavra por palavra; a ' +
        'diferença é que a decisão de passar ou falhar deixa de depender ' +
        'da língua em que se explica.',
      'Os ficheiros .ids passam a ser lidos pelo leitor XML do próprio ' +
        'motor em vez do DOMParser do browser, que é o que a suite de ' +
        'conformidade mede. Um ficheiro XML mal formado passa também a ' +
        'dizer onde falhou, em vez de só "malformado".',
    ],
  },
  {
    version: '0.2.0-beta.81',
    date: '2026-08-10',
    summary:
      'As tubagens passam a desenhar-se em planta, os conflitos são um ' +
      'módulo, e a georreferenciação IFC2x3 segue o guia da buildingSMART.',
    fixed: [
      'Em planta, a tubagem aparecia praticamente invisível: só se viam ' +
        'os aros dos topos. Faltava o contorno aparente, ou seja, as duas ' +
        'linhas laterais à distância do diâmetro, que num tubo não são ' +
        'arestas do modelo mas sim silhueta vista de cima. Passam a ser ' +
        'desenhadas, e a largura corresponde ao diâmetro real do IFC (sem ' +
        'erro nos tubos medidos, de 28 mm a 600 mm). As plantas de ' +
        'arquitetura ficam exatamente como estavam.',
      'A escrita de georreferenciação em IFC2x3 seguia convenções ' +
        'erradas: gravava nos property sets do IfcSite em vez do ' +
        'IfcProject, com a capitalização trocada, e escrevia as ' +
        'coordenadas na unidade do projeto em vez de metros. Num ficheiro ' +
        'em milímetros isso dava uma coordenada mil vezes errada. ' +
        'Corrigido segundo o guia oficial da buildingSMART; regravar um ' +
        'ficheiro antigo migra-o sem duplicar. Se georreferenciaste algum ' +
        'IFC2x3 antes desta versão, vale a pena voltar a gravá-lo.',
    ],
    added: [
      'Controlo da projeção nas Vistas 2D (automática, leve, média, ' +
        'forte): numa vista sem elementos cortados, como é típico em ' +
        'redes de tubagem, a projeção deixa de ser desenhada como ' +
        'secundária e passa a ler-se bem. Fica gravado com a vista.',
      'Remoção de linhas ocultas nas Vistas 2D, opcional: esconde o que ' +
        'está tapado por outros elementos. Numa planta de estrutura, as ' +
        'armaduras dentro do betão passam de mais de mil linhas para ' +
        'duas. Vem desligada, e o custo em tempo é mostrado.',
      'Deteção de conflitos promovida a módulo, com painel acoplável por ' +
        'baixo do modelo, redimensionável e destacável para outro ecrã. ' +
        'Ao escolher um conflito, os dois elementos ficam com cores ' +
        'distintas e o resto do modelo em fantasma. Os conjuntos a ' +
        'testar podem agora definir-se por regra, com contagem em vivo ' +
        'dos elementos abrangidos.',
    ],
  },
  {
    version: '0.2.0-beta.80',
    date: '2026-08-04',
    summary:
      'A documentação dentro da aplicação passa a refletir tudo o que ' +
      'ela faz hoje.',
    changed: [
      'As Funcionalidades ganharam os grupos que faltavam: Vistas 2D, ' +
        'Deteção de conflitos, Tabela, e Medições e anotações no 3D. O ' +
        'grupo do IDS passou a cobrir a criação de IDS e as regras, e a ' +
        'secção de sessões foi reescrita para a forma como a aplicação ' +
        'trabalha hoje (o trabalho só fica se gravares).',
      'Limitações corrigidas: dizia que não havia vistas em planta, o ' +
        'que deixou de ser verdade. Ficou explicado o que o desenho 2D ' +
        'não faz, e os limites da deteção de conflitos.',
      'Ajuda com entradas novas para gravar a sessão, criar plantas e ' +
        'cortes, detetar conflitos e criar um IDS, e com correções onde ' +
        'os nomes dos botões já não batiam certo com a aplicação.',
    ],
  },
  {
    version: '0.2.0-beta.79',
    date: '2026-08-04',
    summary:
      'Deteção de conflitos (clash detection): correr, triar e exportar ' +
      'para BCF.',
    added: [
      'Novo painel "Conflitos" no viewer: defines dois conjuntos (por ' +
        'modelos e categorias IFC, com as contagens reais do ficheiro, ' +
        'ou pela seleção no 3D) e corres a verificação. Presets para ' +
        'Estrutura × MEP e Modelo A × Modelo B. Deteta conflitos duros ' +
        '(elementos que se atravessam) e de folga (elementos a menos de ' +
        'X da distância que definires), com tolerância configurável ' +
        'para ignorar contactos de construção.',
      'A verificação corre em segundo plano com progresso e cancelamento ' +
        'e não bloqueia a aplicação. Num modelo estrutural de 1526 ' +
        'elementos, vigas contra pilares levou cerca de um segundo.',
      'Triagem: cada conflito pode ser marcado como aceite ou resolvido, ' +
        'com nota. Ao correr de novo, os pareceres mantêm-se e os ' +
        'conflitos que deixaram de existir ficam assinalados em vez de ' +
        'desaparecerem. Clicar num conflito leva a câmara ao sítio, ' +
        'seleciona os dois elementos e permite isolá-los.',
      'Export para BCF: um tópico por conflito, com o ponto de vista já ' +
        'apontado ao problema e os dois elementos selecionados.',
    ],
  },
  {
    version: '0.2.0-beta.78',
    date: '2026-08-04',
    summary:
      'Medir áreas escolhendo a geometria do desenho, com valores ' +
      'exatos em superfícies curvas.',
    added: [
      'Novo "Área do elemento": clicas numa parede, pilar ou laje no ' +
        'desenho e tens a área da secção, já com as aberturas ' +
        'subtraídas.',
      'Novo "Escolher linhas" (à maneira do Pick Lines): defines a área ' +
        'escolhendo contornos que já existem no desenho. Um círculo ou ' +
        'contorno fechado dá o valor de imediato; troços soltos vão-se ' +
        'encadeando até fechar, com desfazer.',
      'As áreas e perímetros de superfícies curvas passam a ser ' +
        'exatos: um círculo dá π·r² e os perímetros contam o ' +
        'comprimento do arco, em vez da aproximação por polígono (numa ' +
        'laje circular de 2 m de raio isso valia 0,7% de erro). As ' +
        'medições continuam a poder ficar gravadas como cota e a sair ' +
        'no PDF e no DXF.',
    ],
  },
  {
    version: '0.2.0-beta.77',
    date: '2026-08-04',
    summary:
      'Guardar sessão em modo local, snap nos cruzamentos, e o ' +
      'relatório do IDS deixa de se perder.',
    added: [
      'Guardar, abrir e fechar sessão passam a existir também em modo ' +
        'local, e não só no modo em nuvem. Um ficheiro de sessão gravado ' +
        'num modo abre no outro; o que não fizer sentido no destino é ' +
        'ignorado sem estragar nada.',
      'O snap das Vistas 2D passa a agarrar também os cruzamentos entre ' +
        'linhas, com uma marca em X. Onde há canto e cruzamento no mesmo ' +
        'sítio ganha o canto, para não haver duas marcas no mesmo ponto.',
    ],
    fixed: [
      'O relatório de validação do IDS perdia-se ao sair do módulo e ' +
        'obrigava a validar outra vez. Agora fica, e o painel do viewer ' +
        'e a página do módulo passam a mostrar o mesmo relatório (antes, ' +
        'validar num deixava o outro em branco). Se trocares de modelo, ' +
        'o relatório é limpo com aviso, em vez de mostrar resultados de ' +
        'um ficheiro que já não está aberto.',
    ],
  },
  {
    version: '0.2.0-beta.76',
    date: '2026-08-02',
    summary:
      'A Tabela deixa de se perder ao sair do módulo, e fechar sessão ' +
      'limpa mesmo tudo.',
    fixed: [
      'No módulo Tabela, sair e voltar obrigava a carregar a tabela ' +
        'outra vez: era o único módulo cujo estado morria ao fechar. ' +
        'Agora a tabela, as colunas escolhidas, o filtro, a ordenação e ' +
        'os grupos mantêm-se enquanto a sessão durar, mesmo trocando de ' +
        'módulo no painel. Se trocares ou descarregares os modelos, a ' +
        'tabela é limpa com aviso, para nunca mostrares linhas de um ' +
        'ficheiro que já não está aberto.',
      'Fechar sessão passa a limpar também o BoQ e as edições do ' +
        'IFCEdit, que ficavam pendurados de um modelo já descartado.',
    ],
  },
  {
    version: '0.2.0-beta.75',
    date: '2026-08-02',
    summary:
      'O trabalho deixa de reaparecer sozinho: só fica se gravares a ' +
      'sessão, com rede de recuperação se algo correr mal.',
    changed: [
      'BCFs, vistas 2D, viewpoints, rascunhos IDS e os IFC carregados ' +
        'deixaram de ser repostos em silêncio a cada visita. Passam a ' +
        'viver na sessão, e é o "Guardar sessão" que os torna ' +
        'permanentes. Se saíres com trabalho por gravar, a app avisa; ' +
        'se voltares, PERGUNTA se queres recuperar a sessão anterior ' +
        '(dizendo o que lá está) e podes descartá-la. Um ponto na barra ' +
        'assinala trabalho por gravar. As preferências (tema, tamanho ' +
        'do texto, barras, snap, modos dos painéis) continuam guardadas ' +
        'como antes, mesmo que descartes o trabalho.',
    ],
    fixed: [
      'O "Guardar sessão" não estava a incluir as vistas 2D (plantas, ' +
        'cortes, anotações e cotas): passam a ir no ficheiro de sessão.',
    ],
  },
  {
    version: '0.2.0-beta.74',
    date: '2026-08-02',
    summary:
      'Vistas 2D: o cursor agarra à geometria (snap) e as anotações ' +
      'voltam a funcionar.',
    added: [
      'Snap nos modos Medir, Anotar e Desenhar corte: o cursor agarra a ' +
        'vértices, pontos médios e centros de círculos e arcos, com uma ' +
        'marca a indicar o tipo. Medir entre dois cantos passa a dar a ' +
        'dimensão exata do elemento (num pilar de 12": 0,30 m com snap, ' +
        '0,36 m sem). Alt desliga momentaneamente, Shift tranca na ' +
        'horizontal ou vertical, e há um botão Snap na barra.',
    ],
    fixed: [
      'O modo Anotar não fazia nada: a caixa de texto era criada e ' +
        'perdia o foco no mesmo clique, apagando-se antes de se poder ' +
        'escrever. Corrigido, e com ele a edição por duplo clique, que ' +
        'nunca chegara a funcionar.',
    ],
  },
  {
    version: '0.2.0-beta.73',
    date: '2026-08-02',
    summary:
      'Módulos de volta ao painel acoplado junto ao 3D, e cotagem ' +
      'persistente nas Vistas 2D.',
    added: [
      'Cotagem nas Vistas 2D: as medições passam a ficar desenhadas ' +
        'como cotas de arquitetura (linha com traços de extensão e o ' +
        'valor alinhado; encadeadas com total; áreas com A= e perímetro ' +
        'no centro), gravadas com a vista, apagáveis uma a uma ou todas, ' +
        'e incluídas nos exports SVG, PDF e DXF (camada COTAS).',
    ],
    fixed: [
      'Os módulos (BCF, IDS, BoQ, Tabela) voltaram a poder ficar ' +
        'abertos em simultâneo com o 3D: o painel acoplado em baixo ou ' +
        'ao lado sempre existiu, mas bastava carregar uma vez em "Tela ' +
        'cheia" para os controlos de modo desaparecerem e a escolha ' +
        'ficar presa para sempre. A barra de modos passa a estar ' +
        'presente em todos os estados (acoplado em baixo, ao lado, ' +
        'flutuante, janela do browser, ecrã inteiro), com "Acoplar ao ' +
        'viewer" sempre à mão, e a app lembra o modo por módulo. De ' +
        'caminho: o 3D passa a redimensionar-se corretamente quando o ' +
        'painel abre, e corrigiu-se um bloqueio da página escondido no ' +
        'aviso de restauro de sessão.',
    ],
  },
  {
    version: '0.2.0-beta.72',
    date: '2026-08-02',
    summary:
      'Vistas 2D: fim dos arcos falsos em armaduras, perfis e MEP; o ' +
      'reconhecimento de curvas é agora estritamente geométrico.',
    fixed: [
      'O reconhecimento de arcos criava artefactos em forma de lente em ' +
        'geometria fina ou curva (armaduras, fillets de perfis, curvas ' +
        'em S): o arco ajustado passa a ser verificado contra todos os ' +
        'pontos originais, pela ordem e dentro da amplitude, e na ' +
        'dúvida ficam os segmentos. Medido no modelo de exemplo: 90 ' +
        'cruzamentos do traçado antes, zero depois; desvio máximo de ' +
        '32 mm para 0,08 mm. Os critérios são puramente geométricos, ' +
        'sem casos especiais por tipo de elemento: tubagens MEP curvas, ' +
        'paredes curvas ou corrimãos passam pelo mesmo caminho. Novo ' +
        'interruptor "Arcos suaves" nas Camadas para veres a tesselação ' +
        'pura quando quiseres. Raios muito pequenos (fillets com 2-4 ' +
        'vértices na origem) mantêm a tesselação do ficheiro: não se ' +
        'inventa geometria sem evidência.',
    ],
  },
  {
    version: '0.2.0-beta.71',
    date: '2026-08-02',
    summary:
      'Círculos a sério nas Vistas 2D e o modo 2D pode destacar-se para ' +
      'outro ecrã.',
    added: [
      'Botão "Destacar" no modo 2D: o desenho abre numa janela própria ' +
        'do browser, arrastável para um segundo monitor, com o 3D a ' +
        'ficar na janela principal. A seleção continua sincronizada nos ' +
        'dois sentidos e todas as ferramentas funcionam na janela ' +
        'destacada. Fechar a janela (ou "Trazer de volta") devolve o ' +
        'desenho ao modo embutido tal como estava. Se o browser ' +
        'bloquear a janela, a app avisa e nada se perde.',
    ],
    fixed: [
      'Elementos redondos apareciam como polígonos nas Vistas 2D: o ' +
        'desenho passa a reconhecer arcos e círculos verdadeiros na ' +
        'geometria cortada e projetada. No ecrã e no PDF saem curvas ' +
        'suaves, e no DXF saem entidades CIRCLE e ARC nativas, como um ' +
        'CAD espera. Elementos genuinamente facetados (um pilar ' +
        'hexagonal) ficam como são: só vira círculo o que é mesmo ' +
        'circular.',
    ],
  },
  {
    version: '0.2.0-beta.70',
    date: '2026-08-02',
    summary:
      'As Vistas 2D passam a ser um modo da plataforma, lado a lado com ' +
      'o 3D.',
    changed: [
      'Novo comutador 3D | 2D junto à área do modelo: o modo 2D ocupa o ' +
        'ecrã de trabalho com o navegador de vistas à esquerda (plantas ' +
        'e cortes agrupados), o desenho ao centro com todas as ' +
        'ferramentas, e o painel de propriedades à direita a responder ' +
        'à seleção feita no desenho. Alternar entre modos preserva ' +
        'tudo: a câmara 3D, a vista aberta, anotações e cores. O botão ' +
        '"Vistas 2D" do ribbon e o novo botão "2D" no telemóvel entram ' +
        'diretamente no modo (a antiga janela flutuante foi retirada). ' +
        'No telemóvel: escolher vista, arrastar com um dedo e zoom com ' +
        'dois (pinch).',
    ],
  },
  {
    version: '0.2.0-beta.69',
    date: '2026-08-02',
    summary:
      'Vistas 2D: anotações, medição de áreas, janela maximizável, e os ' +
      'elementos redondos no sítio certo.',
    added: [
      'Modo "Anotar": etiquetas de texto colocadas por clique no ' +
        'desenho, arrastáveis e editáveis, gravadas com a vista e ' +
        'incluídas nos exports (SVG, PDF e DXF em camada própria).',
      'Modo "Medir": distâncias entre dois pontos e áreas por polígono ' +
        '(com comprimento em vivo, área e perímetro ao fechar), com ' +
        'opção de guardar o resultado como anotação no desenho.',
      'Botão maximizar/restaurar na janela das Vistas 2D (também por ' +
        'duplo clique na barra de título).',
    ],
    fixed: [
      'Em modelos com coordenadas grandes (georreferenciados), os ' +
        'elementos redondos apareciam dentados e ligeiramente fora do ' +
        'sítio: perda de precisão numérica (até 5 cm de desvio). O ' +
        'cálculo passa a precisão dupla num referencial local e o ' +
        'desvio ficou abaixo do micrómetro. O número de lados dos ' +
        'círculos vem da tesselação do próprio IFC e não muda.',
    ],
  },
  {
    version: '0.2.0-beta.68',
    date: '2026-08-02',
    summary:
      'Vistas 2D: cores por camada, preenchimento dos cortes e seleção ' +
      'de elementos pelo desenho.',
    added: [
      'Cores das linhas configuráveis por camada (com repor), gravadas ' +
        'com a vista e aplicadas ao preview e aos exports (SVG, PDF, ' +
        'DXF).',
      'Preenchimento das zonas cortadas (poché), com furos respeitados: ' +
        'uma abertura dentro de uma parede fica vazia. Toggle na barra e ' +
        'cor editável por camada; sai igual no SVG e no PDF. No DXF os ' +
        'contornos vão fechados, prontos a tramar no CAD (o formato ' +
        'antigo não suporta tramas nativas).',
      'Selecionar pelo desenho: clicar numa linha ou mancha da planta ou ' +
        'do corte seleciona esse elemento no viewer 3D e realça-o no ' +
        'desenho; a seleção feita no 3D, na árvore ou na tabela também ' +
        'aparece realçada no desenho. Clique em vazio limpa.',
    ],
  },
  {
    version: '0.2.0-beta.67',
    date: '2026-08-02',
    summary:
      'Vistas 2D: o desenho passa a aparecer enquadrado, com zoom e ' +
      'janela redimensionável.',
    fixed: [
      'A pré-visualização das Vistas 2D abria em branco: o desenho era ' +
        'gerado mas aparecia sem enquadramento (via-se só um canto da ' +
        'folha, que era margem) e, ao afastar, os traços ficavam finos ' +
        'demais para se verem. Agora o desenho abre enquadrado e ' +
        'centrado, os traços têm espessura fixa no ecrã em qualquer ' +
        'zoom, o zoom com a roda centra-se no cursor e o desenho ' +
        'arrasta-se (também com o dedo). A janela abre maior (80% do ' +
        'ecrã) e redimensiona-se pelo canto. O modo "Desenhar corte" ' +
        'coloca a linha exatamente onde clicas, mesmo com zoom e ' +
        'arrasto aplicados.',
    ],
  },
  {
    version: '0.2.0-beta.66',
    date: '2026-07-28',
    summary:
      'Vistas 2D: plantas e cortes com view range, gravação e export ' +
      'PDF/DXF. E o export Google Earth passa a levar o modelo inteiro.',
    added: [
      'Novo painel "Vistas 2D" (ribbon, separador Vista): plantas por ' +
        'piso com um clique (corte a 1,20 m, profundidade editável) ou ' +
        'planta livre com a cota e a profundidade que quiseres; cortes ' +
        'criados da caixa de corte, por azimute, ou desenhados ' +
        'diretamente sobre a planta com dois cliques (vários seguidos, ' +
        'nomes Corte A-A, B-B, direção invertível, marcas com setas na ' +
        'planta). As vistas gravam-se por modelo e sobrevivem a reloads. ' +
        'Pré-visualização com camadas por categoria IFC nas cores do ' +
        'viewer, e export para PDF vetorial (papel e escala reais, 1:50 a ' +
        '1:500), DXF (abre em qualquer CAD; para DWG basta gravar-como no ' +
        'CAD) e SVG.',
    ],
    fixed: [
      'O export Google Earth (KMZ) cortava os modelos grandes num limite ' +
        'de 400 mil triângulos, por ordem arbitrária: num modelo real de ' +
        '1,8 milhões só saíam 22% (a "fachada e pouco mais"). Passa a ' +
        'usar geometria instanciada (cada peça repetida escreve-se uma ' +
        'vez), pelo que o modelo sai INTEIRO num ficheiro mais pequeno e ' +
        'mais rápido, com barra de progresso e uma nota honesta com os ' +
        'números do que foi exportado.',
    ],
  },
  {
    version: '0.2.0-beta.65',
    date: '2026-07-26',
    summary: 'Modo telemóvel corrigido: scroll nas propriedades, largura ' +
      'do ecrã estável e layout certo na horizontal.',
    fixed: [
      'No telemóvel, o painel de propriedades não fazia scroll: o painel ' +
        'ficava com altura livre dentro da gaveta e o conteúdo além do ' +
        'ecrã era simplesmente inalcançável. Corrigido: o painel ocupa a ' +
        'altura da gaveta e o interior desliza normalmente.',
      'A página esticava na horizontal: o grupo de ações da barra ' +
        'superior media mais 59 px do que o ecrã. Em ecrãs pequenos as ' +
        'peças largas saem da faixa (o "Info do modelo" muda para o menu ' +
        'de reticências) e a largura fica fixa.',
      'Telemóvel deitado caía no layout de computador, deixando a vista ' +
        '3D com um canto minúsculo entre as barras. O modo compacto passa ' +
        'a ativar-se também pela altura do ecrã.',
      'Desativado o "puxar para atualizar" e o ressalto elástico do ' +
        'browser, que atrapalhavam os gestos de órbita.',
    ],
  },
  {
    version: '0.2.0-beta.64',
    date: '2026-07-26',
    summary: 'A aplicação arranca mais leve: o mapa só é descarregado ' +
      'quando o abres.',
    changed: [
      'A biblioteca do mapa e a de conversão de coordenadas deixaram de ' +
        'vir no arranque da aplicação e passam a ser descarregadas na ' +
        'primeira vez que abres o painel de mapa. Quem só usa o viewer ' +
        'poupa cerca de 100 kB comprimidos no primeiro carregamento ' +
        '(de 1079 para 980 kB). O mapa, a vista do relevo em 3D, a ' +
        'verificação de elevação e o export Google Earth funcionam ' +
        'exatamente como antes.',
    ],
  },
  {
    version: '0.2.0-beta.63',
    date: '2026-07-24',
    summary:
      'O editor de IDS sugere agora as hipóteses do esquema IFC, e ' +
      'chega um verificador de regras condicionais.',
    added: [
      'Sugestões vindas do esquema IFC em todos os campos onde antes ' +
        'era preciso saber a norma de cor: classes, tipos predefinidos ' +
        'da classe escolhida, atributos, property sets standard da ' +
        'buildingSMART (filtrados pelas classes a que se aplicam) e as ' +
        'suas propriedades, com o tipo de dados a preencher-se sozinho. ' +
        'Funciona sem modelo carregado; com um modelo aberto, o que ' +
        'existe nele aparece primeiro e assinalado. Cobertura: 903 ' +
        'classes, 320 com tipos predefinidos e 760 property sets no ' +
        'IFC4X3, e o equivalente em IFC4 e IFC2X3.',
      'Novo separador "Regras (extensão)": verificações condicionais que ' +
        'o IDS não cobre, do género "para os pilares de betão com tipo ' +
        'predefinido USERDEFINED, reportar o sistema de classificação". ' +
        'Condições SE/ENTÃO/SENÃO com grupos TODOS/ALGUM/NENHUM ' +
        'encaixáveis, ações de exigir ou de reportar (tabela de valores ' +
        'encontrados), falhas clicáveis que selecionam no modelo, e ' +
        'export para BCF. Fica em ficheiro próprio (.idsx) e está ' +
        'marcado como extensão VN2R: não se disfarça de IDS, para não ' +
        'confundir quem receber os ficheiros.',
    ],
  },
  {
    version: '0.2.0-beta.62',
    date: '2026-07-24',
    summary: 'Criar IDS: o módulo IDS passa a permitir criar e editar ' +
      'ficheiros de requisitos, não só validar.',
    added: [
      'Editor "Criar IDS" no módulo IDS: especificações com os seis ' +
        'tipos de facet (applicability e requirements), valores exatos, ' +
        'listas, padrões, intervalos e comprimentos; sugestões vindas do ' +
        'modelo carregado (classes, property sets, materiais, ' +
        'classificações); avisos de schema em tempo real (atributo ' +
        'inexistente, decimal em campo inteiro, padrão em campo ' +
        'numérico) para impedir IDS inválidos à nascença; validar já ' +
        'contra o modelo sem sair da página; importar um .ids existente ' +
        'para editar; descarregar o .ids conforme a norma; rascunho ' +
        'que sobrevive a reloads. O ficheiro escrito reconstrói-se ' +
        'fielmente ao ser relido (testado contra a suite oficial ' +
        'buildingSMART).',
    ],
  },
  {
    version: '0.2.0-beta.61',
    date: '2026-07-24',
    summary:
      'O IDS passa a módulo de primeira classe da aplicação, com ' +
      'separador próprio.',
    added: [
      'Novo módulo "IDS" na barra superior (modo cloud, ativado por ' +
        'workspace como os restantes módulos): página de trabalho com ' +
        'validação completa (abrir .ids, resultados por especificação, ' +
        'falhas clicáveis que selecionam o elemento no viewer, resumo, ' +
        'export CSV e export das falhas para BCF) e a área "Criar IDS" ' +
        'preparada para o futuro editor. O atalho rápido "Validar IDS" ' +
        'no ribbon do viewer mantém-se, e continua a servir o modo local.',
    ],
  },
  {
    version: '0.2.0-beta.60',
    date: '2026-07-24',
    summary:
      'Validador IDS a 100% de conformidade com a suite oficial da ' +
      'buildingSMART (318/318 casos).',
    changed: [
      'O validador IDS passa a detetar ficheiros IDS insatisfazíveis ' +
        'pelo schema IFC: um valor decimal exigido num atributo que o ' +
        'standard declara como inteiro, ou um padrão de texto aplicado a ' +
        'um atributo numérico, são agora assinalados como inválidos. Com ' +
        'isto, a conformidade com a suite oficial da buildingSMART chega ' +
        'aos 318/318 (100%). A tabela de tipos do schema (IFC2X3, IFC4, ' +
        'IFC4X3) é gerada da mesma fonte que o motor usa em runtime e ' +
        'servirá também o futuro editor de criação de IDS.',
    ],
  },
  {
    version: '0.2.0-beta.59',
    date: '2026-07-24',
    summary:
      'Validador IDS a 98,7% de conformidade com a suite oficial ' +
      'buildingSMART, e créditos a terceiros no Sobre.',
    added: [
      'Nova tab "Créditos" no Sobre/Diagnóstico: reconhecimento do ' +
        'trabalho de terceiros que a app usa (ThatOpen, web-ifc, ' +
        'three.js, Leaflet, OpenStreetMap, buildingSMART, entre outros), ' +
        'com as licenças verificadas nos próprios pacotes.',
    ],
    changed: [
      'O validador IDS sobe de 75,2% para 98,7% de conformidade com a ' +
        'suite oficial buildingSMART (314/318 casos): tolerância numérica ' +
        'conforme a especificação, todos os tipos de valor de propriedade ' +
        'com conversão de unidades, herança do tipo (materiais, ' +
        'propriedades, classificações, PredefinedType), partOf com grupos ' +
        'e cadeias, cardinalidades e restrições XSD completas. Os 4 casos ' +
        'restantes exigem introspeção do schema IFC e ficam documentados.',
    ],
  },
  {
    version: '0.2.0-beta.58',
    date: '2026-07-24',
    summary:
      'O validador IDS passa a ser medido contra a suite oficial de ' +
      'testes da buildingSMART: 75% de conformidade, lacunas mapeadas.',
    added: [
      'Suite oficial de casos de teste IDS da buildingSMART (318 casos, ' +
        '9 áreas) integrada nos testes automáticos do projeto. O motor ' +
        'valida corretamente 239/318 (75,2%); os 79 casos por cobrir ' +
        'estão documentados um a um com o motivo (ver CONFORMIDADE.md ' +
        'no módulo IDS), e o maior bloco são tipos de propriedade menos ' +
        'comuns e comparações numéricas com tolerância. Qualquer ' +
        'melhoria futura ao motor é agora medida contra a norma, não ' +
        'contra a nossa própria interpretação dela.',
    ],
  },
  {
    version: '0.2.0-beta.57',
    date: '2026-07-24',
    summary:
      'Pegada e centro do mapa pela extensão real da geometria, e o ' +
      'export Google Earth ganha cores por categoria IFC.',
    added: [
      'As malhas do export Google Earth (KMZ) passam a ir coloridas pela ' +
        'categoria IFC de cada elemento, com a mesma paleta do modo ' +
        '"cores por classe" do viewer. Categorias sem cor na paleta ' +
        'continuam a cinza.',
    ],
    fixed: [
      'A pegada e o centro do modelo no mapa, o chão do basemap 3D, a ' +
        'verificação de elevação e o alinhamento de federação usavam a ' +
        'caixa que o motor devolve para o modelo inteiro, que vem inflada ' +
        '(medido: 4,5x mais larga que a geometria real). Passam todos a ' +
        'usar a extensão real, somando as caixas de cada elemento, ' +
        'calculada uma vez após o carregamento e mantida em cache. A ' +
        'pegada desenhada no mapa passa a ter o tamanho do edifício.',
    ],
    changed: [
      'As versões do motor ThatOpen ficam fixadas exatamente ' +
        '(3.4.3/3.4.2): a versão mais recente publicada (3.4.7) ainda ' +
        'devolve caixas de dimensão errada (±100 km) e uma instalação ' +
        'fresca podia apanhá-la sem querer.',
    ],
  },
  {
    version: '0.2.0-beta.56',
    date: '2026-07-16',
    summary:
      'O export Google Earth passa a ancorar o modelo pela extensão real ' +
      'da geometria.',
    fixed: [
      'O export Google Earth (KMZ) ancorava o modelo pela caixa que o ' +
        'motor devolve para o modelo inteiro, que vem inflada (medido: ' +
        '4,5x mais larga que a geometria real). A âncora passa a ser a ' +
        'extensão real, somando as caixas de cada elemento, pelo que o ' +
        'modelo assenta no sítio certo. Modelos com um elemento muito ' +
        'afastado do resto (marco de federação, anotações) eram os mais ' +
        'afetados.',
    ],
    changed: [
      'Limpeza técnica: eliminados os tipos `any` explícitos no módulo de ' +
        'medições e reduzidos os avisos de estado em efeitos (renders em ' +
        'cascata). Sem alterações de comportamento.',
    ],
  },
  {
    version: '0.2.0-beta.55',
    date: '2026-07-16',
    summary:
      'Aviso quando o Scale do MapConversion não bate certo com a unidade ' +
      'do ficheiro (o modelo aparece no sítio errado no mapa e no KMZ).',
    added: [
      'A Info do modelo (CRS / Origem) passa a avisar quando o Scale ' +
        'declarado no IfcMapConversion é incompatível com a unidade do ' +
        'projeto. O caso típico: ficheiro em milímetros com Scale=1 (devia ' +
        'ser 0.001). Nesses ficheiros o modelo desenha bem, mas o mundo ' +
        'fica em milímetros em vez de metros, e tudo o que assume metros ' +
        'mente em silêncio: a posição no mapa e o export Google Earth ' +
        'saem no sítio errado e com o tamanho errado. O aviso diz a escala ' +
        'que sai, o Scale que o ficheiro devia ter, e que se corrige no ' +
        'exportador. Não corrigimos por adivinhação: o ficheiro é ambíguo.',
    ],
  },
  {
    version: '0.2.0-beta.54',
    date: '2026-07-16',
    summary:
      'O export Google Earth (KMZ) estava a produzir um modelo vazio. ' +
      'Passa a exportar a geometria toda.',
    fixed: [
      'O ficheiro COLLADA dentro do KMZ saía sem geometria nenhuma (só o ' +
        'esqueleto XML), ou dava "Sem malhas trianguladas para exportar". ' +
        'A recolha percorria a cena 3D à procura de malhas, mas nesta ' +
        'versão do motor a geometria não vive na cena: é desenhada por um ' +
        'sistema próprio. A recolha passa a ler a geometria diretamente do ' +
        'motor, pelo que exporta o modelo todo e é robusta a modelos ' +
        'grandes em streaming (já não depende do que está carregado na ' +
        'vista). Os triângulos exportados são agora os do modelo real, ' +
        'verificados contra as caixas de referência do motor.',
    ],
    changed: [
      'As malhas exportadas para KMZ vão com a cor por omissão: a API de ' +
        'geometria do motor não expõe os materiais. A posição, a escala e ' +
        'a orientação mantêm-se corretas.',
    ],
  },
  {
    version: '0.2.0-beta.53',
    date: '2026-07-13',
    summary:
      'A resolução de componentes por GlobalId passa a ser a mesma para ' +
      'os viewpoints BCF e para a integração FireBIM.',
    changed: [
      'Os viewpoints BCF e a integração FireBIM (abrir modelo e focar um ' +
        'elemento vindo do FireBIM) passam a resolver os GlobalIds da mesma ' +
        'maneira, num único sítio. Na prática, a integração FireBIM fica ' +
        'igualmente robusta a receber diretamente um compartimento sem ' +
        'geometria (IfcZone) ou um sistema: expande-os para os membros e ' +
        'foca-os, em vez de depender de o emissor já mandar os espaços.',
    ],
  },
  {
    version: '0.2.0-beta.52',
    date: '2026-07-13',
    summary:
      'Viewpoints BCF que apontam para entidades sem geometria (IfcZone, ' +
      'IfcSystem, piso) passam a selecionar essas entidades e os seus ' +
      'membros.',
    fixed: [
      'Abrir um viewpoint BCF que referencia um IfcZone (ou IfcSystem, ' +
        'IfcGroup) não selecionava nada: o Fragments só mapeia GlobalIds ' +
        'de entidades com geometria, e essas eram descartadas em silêncio. ' +
        'Agora resolvem-se pelo parser paralelo, e como a própria entidade ' +
        'não tem geometria para realçar, selecionam-se também os seus ' +
        'membros (os elementos do sistema, os espaços da zona, os ' +
        'elementos contidos num piso), que aparecem realçados em 3D. A ' +
        'entidade agrupadora continua na seleção, visível no painel.',
    ],
  },
  {
    version: '0.2.0-beta.51',
    date: '2026-07-13',
    summary:
      'Os menus da barra superior deixam de ficar presos e cortados ' +
      'dentro da própria barra.',
    fixed: [
      'Ao abrir um menu da barra superior (Sessão, Mais opções, menu do ' +
        'utilizador, Histórico, Diagnóstico), o conteúdo ficava espremido ' +
        'dentro da faixa de 40px da barra, ilegível mesmo com scroll. ' +
        'Causa: a barra tinha scroll horizontal (overflow) que também ' +
        'recortava na vertical e prendia os menus. O scroll horizontal ' +
        'passou para um grupo interno (só o nome do workspace e os ' +
        'separadores), e a barra deixa os menus expandir para baixo, por ' +
        'cima do modelo, como devia.',
    ],
  },
  {
    version: '0.2.0-beta.50',
    date: '2026-07-13',
    summary:
      'Os atributos do elemento (Name, Tag, ObjectType, classe) voltam à ' +
      'vista simples, sempre visíveis.',
    changed: [
      'A secção Atributos deixou de estar escondida atrás do modo ' +
        'Avançado: é informação básica e de uso diário (Name, Tag, ' +
        'ObjectType, LongName, classe), por isso fica sempre visível ao ' +
        'lado dos property sets. O modo Avançado passa a mostrar apenas o ' +
        'que é mesmo técnico: relações inversas, cadeia de herança, tipo ' +
        'IFC e geometria.',
    ],
  },
  {
    version: '0.2.0-beta.49',
    date: '2026-07-13',
    summary:
      'Property sets e quantity sets voltam a mostrar valores em todos ' +
      'os elementos, e a barra superior foi reorganizada.',
    fixed: [
      'Os property sets e quantity sets apareciam vazios ("Sem property ' +
        'sets") em todos os elementos: o painel só ia buscar os nomes dos ' +
        'sets, não os valores das propriedades. A leitura passa a expandir ' +
        'em profundidade apenas a relação que carrega os psets/qsets ' +
        '(IsDefinedBy), pelo que os valores (IsExternal, FireRating, ' +
        'NetVolume, etc.) voltam a aparecer, sem inchar o payload dos ' +
        'elementos muito ligados como os pisos.',
      'Deixou de aparecer uma linha em branco no topo dos property sets ' +
        'quando o motor devolvia uma propriedade sem nome.',
    ],
    changed: [
      'Barra superior reorganizada por grupos: chip do modelo (nome + ' +
        'schema + estado da sessão), ações (Capturar, Exportar, menu ' +
        'Sessão, Info do modelo), e os acessos menos frequentes recolhidos ' +
        'em menus (Sessão, Mais opções e menu do utilizador). Fica mais ' +
        'legível e prática, sem scroll horizontal.',
    ],
  },
  {
    version: '0.2.0-beta.48',
    date: '2026-07-11',
    summary:
      'Enquadramento da câmara robusto: os modelos deixam de aparecer ' +
      'pequenos e a parecer inclinados por causa de elementos dispersos.',
    fixed: [
      'Modelos com um elemento muito afastado do resto (marco de ' +
        'federação à cota 0, textos 3D, anotações) apareciam pequenos e ' +
        'de esquina, a parecer inclinados: a câmara enquadrava a caixa ' +
        'inteira, esticada por esse elemento, e apontava para espaço ' +
        'vazio. O enquadramento (no arranque e no Focar/Centrar) passa a ' +
        'usar uma caixa robusta que ignora esses outliers e aponta ao ' +
        'grosso da geometria. O modelo em si nunca esteve inclinado.',
    ],
  },
  {
    version: '0.2.0-beta.47',
    date: '2026-07-11',
    summary:
      'Correção: o export Google Earth (KMZ) já não estoura em modelos ' +
      'com geometria em streaming.',
    fixed: [
      'O export "Exportar Google Earth (KMZ)" rebentava ("Cannot read ' +
        'properties of undefined") em modelos maiores cujas malhas ' +
        'não estavam totalmente residentes em memória. Agora salta as ' +
        'malhas ilegíveis e exporta o resto, indicando quantas ignorou; ' +
        'se nenhuma for legível, dá uma mensagem clara (carrega o modelo ' +
        'todo na vista antes de exportar).',
    ],
  },
  {
    version: '0.2.0-beta.46',
    date: '2026-07-11',
    summary:
      'Cadeia de referenciais: cada nível passa a dizer a que está ' +
      'ancorado.',
    changed: [
      'Na "Cadeia de referenciais (exata)" (painel Coordenadas), cada ' +
        'nível mostra agora "ancorado a:" com o nome do contentor pai ' +
        '(ex.: um piso ancorado ao edifício), em vez de só dizer "rel. ao ' +
        'pai" sem identificar o pai. O nível mais externo indica "origem ' +
        'do ficheiro".',
    ],
  },
  {
    version: '0.2.0-beta.45',
    date: '2026-07-11',
    summary:
      'UI mais limpa: vista simples por omissão no painel de ' +
      'propriedades, duplicações removidas e ferramentas do Mapa ' +
      'recolhidas.',
    changed: [
      'O painel de propriedades abre em vista SIMPLES: property sets, ' +
        'quantity sets, materiais, classificações e coordenadas. O ' +
        'interruptor "Avançado" (junto à pesquisa, persistido) traz de ' +
        'volta atributos, relações inversas, tipo IFC, geometria e JSON ' +
        'cru.',
      'Duplicações removidas: a Localização entrou na secção Coordenadas ' +
        'e a bounding box passa a existir num só sítio (Coordenadas, ' +
        'agora com o volume).',
      'As ferramentas do painel Mapa (elevação, Google Earth, federação) ' +
        'ficam num acordeão "Ferramentas" recolhido por omissão; o botão ' +
        'Federação do ribbon abre-o automaticamente.',
    ],
  },
  {
    version: '0.2.0-beta.44',
    date: '2026-07-11',
    summary:
      'Ligações partilháveis com validação IDS incluída, e um terceiro ' +
      'exemplo (arquitetura).',
    added: [
      'O parâmetro ?ids=https://… nas ligações partilháveis abre o ' +
        'painel de validação IDS já com o ficheiro de regras carregado, ' +
        'pronto a validar, depois de o modelo (?sample= ou ?ifc=) ' +
        'carregar. Um único link demonstra modelo + validação.',
      'Novo exemplo "Clínica (arquitetura)": IFC4 da cena de ' +
        'certificação PCERT da buildingSMART, pequeno e ideal para ' +
        'demonstrar a validação IDS.',
    ],
  },
  {
    version: '0.2.0-beta.43',
    date: '2026-07-11',
    summary:
      'Ligações partilháveis que abrem o viewer já com um modelo, e app ' +
      'instalável (PWA).',
    added: [
      'Ligações partilháveis: ?sample=house ou ?sample=school abre o ' +
        'viewer já com o exemplo carregado; ?ifc=https://… carrega um IFC ' +
        'remoto (CORS permitindo). Botão "copiar ligação" junto a cada ' +
        'exemplo no menu do ribbon. O carregamento verifica e repete ' +
        'automaticamente (até 3 vezes) para sobreviver a corridas do ' +
        'arranque e a falhas transitórias do download.',
      'App instalável (PWA): manifest + service worker leve. Instala no ' +
        'telemóvel ou desktop; a interface abre offline com a última ' +
        'versão vista (os modelos IFC não são guardados offline).',
    ],
  },
  {
    version: '0.2.0-beta.42',
    date: '2026-07-11',
    summary:
      'Exportar o modelo para o Google Earth (KMZ), já georreferenciado.',
    added: [
      'Botão "Exportar Google Earth (KMZ)" no painel Mapa: gera um KMZ ' +
        '(COLLADA + KML) com o modelo na posição real, derivada ' +
        'automaticamente do CRS + MapConversion/posição manual do viewer ' +
        '(sem digitar coordenadas). A base assenta no terreno do Google ' +
        'Earth; a rotação da georreferenciação vai aplicada à geometria.',
    ],
    changed: [
      'Deteção de coordenadas embutidas ganha um teto de sanidade ' +
        '(50 000 km): valores corruptos deixam de mandar o modelo "para ' +
        'o infinito" no mapa.',
    ],
  },
  {
    version: '0.2.0-beta.41',
    date: '2026-07-11',
    summary:
      'Manutenção interna: menos dívida de lint e uma correção de ordem ' +
      'de hooks. Sem alterações visíveis.',
    changed: [
      'Limpeza de código sem mudança de comportamento: atribuições ' +
        'redundantes removidas, helpers e constantes movidos para ' +
        'ficheiros próprios (auth, ribbon, BCF), e as subscrições do ' +
        'ribbon convertidas para useSyncExternalStore.',
    ],
    fixed: [
      'Correção de ordem de hooks no Info do modelo: o cálculo do ' +
        'TrueNorth (beta.40) corria depois de um return antecipado, o ' +
        'que podia estourar ao remover o modelo com o painel aberto.',
    ],
  },
  {
    version: '0.2.0-beta.40',
    date: '2026-07-11',
    summary:
      'Georreferenciação: gravar em ficheiros IFC2X3 (ePsets no IfcSite) ' +
      'e mostrar o TrueNorth declarado.',
    added: [
      'Gravar georreferenciação em IFC2X3: como o esquema não tem ' +
        'IfcMapConversion nativo, o viewer escreve a convenção da ' +
        'comunidade (property sets ePset_ProjectedCRS e ' +
        'ePset_MapConversion no IfcSite), compatível com o ' +
        'ifc-georeferencer. Coordenadas na unidade do projeto; remove ' +
        'ePsets antigos sem duplicar e preserva o datum vertical.',
      'Info do modelo (CRS/Origem) passa a mostrar o TrueNorth declarado ' +
        'no IfcGeometricRepresentationContext, quando existe. Meramente ' +
        'informativo: por spec não roda a geometria.',
    ],
  },
  {
    version: '0.2.0-beta.39',
    date: '2026-07-09',
    summary:
      'Desfazer a última alteração de posição de um modelo, no editor de ' +
      'posição.',
    added: [
      'Botão "Desfazer" no editor de posição (ao lado do Reset): reverte ' +
        'a última alteração de posição (alinhar, assentar no terreno, ' +
        'reposicionar no mapa ou editar um campo) para o passo anterior. ' +
        'Um nível. O Reset continua a voltar de vez ao MapConversion / ' +
        'origem do modelo.',
    ],
  },
  {
    version: '0.2.0-beta.38',
    date: '2026-07-09',
    summary:
      'Confirmações consistentes: gravações, exportações e ajustes de ' +
      'posição passam a mostrar um aviso curto no canto do ecrã.',
    added: [
      'Avisos de confirmação (toasts) unificados: gravar georreferenciação ' +
        'no IFC, gravar origem de placement, gravar alterações do IFCEdit, ' +
        'exportar o relatório IDS em CSV e o mapa de quantidades do BoQ, e ' +
        'os ajustes de posição no mapa (alinhar federação, assentar no ' +
        'terreno, reposicionar) mostram agora um aviso curto e coerente no ' +
        'canto inferior, que desaparece sozinho. As mensagens detalhadas ' +
        'que já existiam (editor de posição, alinhamento) mantêm-se.',
    ],
  },
  {
    version: '0.2.0-beta.37',
    date: '2026-07-09',
    summary:
      'Comparar propriedades de vários elementos numa tabela lado a lado.',
    added: [
      'Com 2+ elementos selecionados, o painel passa a mostrar uma matriz ' +
        'de comparação: uma coluna por elemento (atributos, property sets ' +
        'e quantity sets), com as linhas que variam realçadas e "-" onde ' +
        'um elemento não tem a propriedade. Compara até 8 elementos lado ' +
        'a lado.',
    ],
  },
  {
    version: '0.2.0-beta.36',
    date: '2026-07-09',
    summary:
      'Dicionário de propriedades: ver que Property Sets e propriedades ' +
      'existem no modelo, e saltar daí para o Filtro.',
    added: [
      'Nova secção "Propriedades presentes" no Info do modelo: lista os ' +
        'Property Sets e as suas propriedades, com o nº de elementos que ' +
        'têm cada uma (amostra dos elementos com geometria). Clicar numa ' +
        'propriedade abre o Filtro (Shift+F) já apontado a essa ' +
        'Pset.Propriedade, com os valores possíveis sugeridos.',
    ],
  },
  {
    version: '0.2.0-beta.35',
    date: '2026-07-09',
    summary:
      'Manutenção interna: os três maiores ficheiros da interface foram ' +
      'divididos em módulos mais pequenos. Sem alterações visíveis.',
    changed: [
      'Refactor sem mudança de comportamento: o painel Mapa, o painel de ' +
        'propriedades e o ribbon deixaram de ser ficheiros monolíticos. ' +
        'Componentes auto-contidos (verificação de elevação, alinhamento ' +
        'de federação, vista de coordenadas, vista de geometria, filtros ' +
        'de origens e de pisos) passaram a ficheiros próprios. Facilita a ' +
        'manutenção e reduz o risco de erros; a interface é idêntica.',
    ],
  },
  {
    version: '0.2.0-beta.34',
    date: '2026-07-09',
    summary:
      'Coordenadas mais claras: uma só cadeia de referenciais por ' +
      'elemento, e a aproximação por contentores passa a ficar recolhida.',
    changed: [
      'Deixou de haver duas secções sobrepostas para a mesma coisa. A ' +
        'antiga "IfcLocalPlacement (chain)" foi absorvida pela "Cadeia de ' +
        'referenciais (exata)", que agora mostra também os eixos (Axis Z / ' +
        'RefDirection X) de cada nível quando o ficheiro os declara.',
      'No painel Coordenadas, o bloco "Contentores (centro geométrico)" ' +
        'passa a estar recolhido e rotulado como aproximação, para não ' +
        'competir com a cadeia de referenciais exata (as origens reais).',
    ],
  },
  {
    version: '0.2.0-beta.33',
    date: '2026-07-09',
    summary:
      'Correção de posicionamento: modelos em milímetros deixam de ' +
      'aparecer 1000x mais pequenos no mapa.',
    fixed: [
      'IFC em milímetros com georreferenciação (IfcMapConversion com ' +
        'Scale=0.001, a convenção de unidade mm→m) deixava o modelo 1000x ' +
        'mais pequeno: o viewer aplicava esse 0.001 como escala geométrica ' +
        'sobre a geometria que o motor já normaliza para metros. Agora o ' +
        'fator de unidade do projeto é dividido antes de o usar, e o ' +
        'modelo aparece no tamanho e posição corretos. Ficheiros em metros ' +
        'não são afetados.',
    ],
  },
  {
    version: '0.2.0-beta.32',
    date: '2026-07-09',
    summary:
      'Trabalhabilidade: as preferências de visualização passam a ' +
      'persistir entre sessões, e a verificação de federação ganha um ' +
      'botão direto no ribbon.',
    added: [
      'Botão "Federação" (separador Vista > Mapa): abre o painel Mapa já ' +
        'na verificação de alinhamento entre modelos. Ativo com 2+ ' +
        'modelos carregados.',
    ],
    changed: [
      'As preferências do marcador de origem (que categorias mostrar, ' +
        'etiquetas), a extensão do chão OSM (1×/2×/3×) e o relevo (DEM) ' +
        'passam a ficar guardadas entre sessões (localStorage), como já ' +
        'acontecia com o tamanho da interface.',
      'Estado vazio do painel Mapa mais claro: indica como abrir um IFC.',
    ],
  },
  {
    version: '0.2.0-beta.31',
    date: '2026-07-09',
    summary:
      'Painel de propriedades: pesquisa dentro das propriedades e ' +
      'proveniência dos property sets mais clara.',
    added: [
      'Pesquisa no painel de propriedades: caixa no topo que filtra os ' +
        'atributos, property sets e quantity sets por nome de set, nome ' +
        'de propriedade ou valor. Um set cujo nome corresponde mostra ' +
        'tudo; senão só as linhas que correspondem. As secções ficam ' +
        'abertas enquanto procuras, e o termo mantém-se ao trocar de ' +
        'elemento (útil para comparar).',
      'Etiqueta de origem em cada property/quantity set: "instância" ' +
        '(lido do elemento) ou "via BoQ" (escrito pelo módulo BoQ nesta ' +
        'sessão), para se perceber de onde vem cada conjunto.',
    ],
  },
  {
    version: '0.2.0-beta.30',
    date: '2026-07-09',
    summary:
      'Validação IDS: exportar as falhas para BCF. Nos workspaces com o ' +
      'módulo BCF ativo, o relatório de validação gera um BCF novo com ' +
      'um tópico por especificação falhada.',
    added: [
      'Exportar para BCF (painel Validar IDS): cria um BCF com um tópico ' +
        'por especificação com falhas, com os elementos falhados (classe, ' +
        'nome, GlobalId e razão) na descrição e a etiqueta "IDS". Abre ' +
        'depois o módulo BCF para os rever e capturar viewpoints. Só ' +
        'aparece nos workspaces com o módulo BCF ativo.',
    ],
  },
  {
    version: '0.2.0-beta.29',
    date: '2026-07-05',
    summary:
      'Validação IDS mais completa: os requisitos de classificação, ' +
      'material e "faz parte de" (partOf) passam a ser avaliados, além ' +
      'da verificação de tipo de dados (dataType) nas propriedades.',
    added: [
      'Facet classification: valida o sistema de classificação e o ' +
        'código (ex.: Uniclass 2015 / EF_25_10) associados ao elemento ' +
        '(IfcRelAssociatesClassification).',
      'Facet material: valida o material associado ao elemento por nome ' +
        '(IfcRelAssociatesMaterial, incluindo camadas, constituintes e ' +
        'perfis).',
      'Facet partOf: valida que o elemento faz parte de um contentor da ' +
        'classe esperada (ex.: parede num IfcBuildingStorey), por ' +
        'IfcRelAggregates, IfcRelNests ou ' +
        'IfcRelContainedInSpatialStructure.',
      'Verificação de dataType nas propriedades (ex.: IFCLABEL), ' +
        'tolerante: só falha quando o tipo real é conhecido e difere.',
    ],
  },
  {
    version: '0.2.0-beta.28',
    date: '2026-07-05',
    summary:
      'Validação IDS (buildingSMART): abre um ficheiro .ids e valida o ' +
      'modelo contra os requisitos de informação (entidades, atributos, ' +
      'propriedades), com relatório navegável, cores no 3D e export CSV. ' +
      'Tudo client-side.',
    added: [
      'Validar IDS (separador Ficheiro > IDS): abre um .ids ' +
        '(Information Delivery Specification 1.0), escolhe o modelo e ' +
        'valida. Por especificação, mostra quantos elementos se aplicam ' +
        'e quantos passam, com a lista de falhas e a razão de cada uma; ' +
        'clica numa falha para a selecionar no 3D.',
      'Cores no 3D: elementos que cumprem a verde, que falham a ' +
        'vermelho (toggle "Ver no 3D"). Export do relatório de falhas ' +
        'para CSV (abre no Excel).',
      'Facets suportados nesta versão: entity (classe + PredefinedType), ' +
        'attribute e property (propertySet + baseName + valor), com ' +
        'restrições simpleValue, enumeração, padrão (regex), limites ' +
        'numéricos e comprimento, e cardinalidade required / prohibited ' +
        '/ optional. Os restantes (classification, material, partOf) são ' +
        'marcados como "não avaliados" em vez de falhar.',
    ],
  },
  {
    version: '0.2.0-beta.27',
    date: '2026-07-05',
    summary:
      'Painel Entidades: materiais, unidades e estilos deixam de aparecer ' +
      'como "elementos físicos": novo grupo "Recursos", e só herdeiros ' +
      'reais de IfcElement contam como elementos.',
    fixed: [
      'Painel Entidades: IfcMaterial*, IfcSIUnit, IfcUnitAssignment e ' +
        'afins caíam no grupo "IfcElement (elementos físicos)" porque o ' +
        'classificador usava esse grupo como fallback. Agora há um grupo ' +
        '"Recursos (materiais, unidades, estilos)" para a camada de ' +
        'recursos do esquema, os elementos são validados contra a tabela ' +
        'de herança IFC (mais sufixos das famílias MEP/estrutura), e o ' +
        'que não for reconhecido cai em "Outros" em vez de inflacionar ' +
        'os elementos.',
    ],
  },
  {
    version: '0.2.0-beta.26',
    date: '2026-07-05',
    summary:
      'O mapa funciona sempre que houver CRS: um IfcMapConversion a ' +
      'zeros (com CRS declarado) é legítimo (as coordenadas do ficheiro ' +
      'valem como coordenadas de mapa) e um modelo sem CRS nenhum pode ' +
      'ser posto no mapa definindo o EPSG manualmente e reposicionando ' +
      'com um clique.',
    changed: [
      'Painel Mapa: deixou de exigir MapConversion/posição manual. Com ' +
        'CRS conhecido (lido do IFC ou definido no editor de posição) o ' +
        'mapa abre sempre, centrado na PEGADA do modelo (não na origem). ' +
        'Modelos sem georreferenciação mostram um aviso e o caminho: ' +
        'reposicionar com clique e gravar a georref no ficheiro.',
      'MapConversion a zeros: tratado como "coordenadas do ficheiro já ' +
        'são de mapa" (padrão Revit/LoGeoRef): a deteção de coordenadas ' +
        'embutidas passou a comparar por valor, e o chão OSM 3D também ' +
        'só exige CRS.',
      'Marcador de origem no Leaflet só aparece quando o placement diz ' +
        'alguma coisa (com placement identidade cairia na origem do CRS, ' +
        'longe do modelo e sem significado).',
    ],
    fixed: [
      'O mapa Leaflet ficava em branco quando o painel passava por um ' +
        'estado sem mapa e voltava (ex.: trocar de modelo): a instância ' +
        'antiga ficava presa a um div desmontado. Agora é recriada ' +
        'quando o contentor muda.',
    ],
  },
  {
    version: '0.2.0-beta.25',
    date: '2026-07-05',
    summary:
      'Legibilidade do posicionamento: por defeito só a origem do ' +
      'ficheiro tem representação 3D (as origens da hierarquia são ' +
      'informação textual, cada uma a dizer a que referencial estão as ' +
      'suas coordenadas), e a verificação de alinhamento da federação ' +
      'ganhou destaque e passos guiados no painel Mapa.',
    changed: [
      'Marcador de origem: por defeito desenha APENAS a origem do ' +
        'ficheiro IFC. Os tripés de Site/Building/Storey/Space passam a ' +
        'opt-in no filtro de origens, e a cena deixou de ficar carregada ' +
        'com símbolos sobrepostos.',
      'Origens da hierarquia espacial (Info do modelo): cada nó mostra ' +
        'agora, por extenso, o REFERENCIAL das suas coordenadas (ex.: ' +
        '"IfcBuildingStorey · Piso 4, referencial: IfcBuilding · ' +
        'Edifício X, coordenadas: E/N/h") e a absoluta no ficheiro ' +
        'quando difere. Fim dos valores só em tooltip.',
      'Federação: o bloco de alinhamento no painel Mapa ganhou título ' +
        'destacado ("Federação: verificar alinhamento") e três passos ' +
        'guiados (A = referência, Diagnosticar, Alinhar B a A) antes do ' +
        'primeiro diagnóstico. O tooltip do botão Mapa (Vista) refere a ' +
        'verificação de federação.',
    ],
  },
  {
    version: '0.2.0-beta.24',
    date: '2026-07-05',
    summary:
      'Chão OSM 3D com extensão ajustável (1×/2×/3×) e "Assentar no ' +
      'terreno" com escolha da referência: um piso do modelo (dropdown ' +
      'com todas as cotas) ou a base do bounding box. Os marcos de ' +
      'federação e níveis espúrios deixavam a compensação automática ' +
      'errada.',
    added: [
      'Extensão do chão OSM no 3D (painel Mapa): seletor 1×/2×/3× para ' +
        'ver mais mapa à volta do modelo (16, 64 ou 144 tiles, mesma ' +
        'resolução).',
      'Assentar no terreno com referência escolhida: a verificação de ' +
        'elevação lista todos os pisos do modelo com as suas cotas ' +
        '(lidas do STEP e projetadas com o placement) e deixa escolher ' +
        'qual deve assentar no terreno. A referência por defeito é a ' +
        'base do modelo (bbox), objetiva e sem interpretação; se o ' +
        'ficheiro tem elementos espúrios em baixo (marcos de federação ' +
        'à cota 0), a escolha manual de um piso resolve, mas a ' +
        'responsabilidade do conteúdo é de quem criou o IFC.',
    ],
  },
  {
    version: '0.2.0-beta.23',
    date: '2026-07-05',
    summary:
      'P3 do sprint de posicionamento: diagnóstico de alinhamento entre ' +
      'dois IFCs carregados (painel Mapa) com três indicadores (' +
      'elementos com o mesmo GlobalId, cotas de pisos com o mesmo nome, ' +
      'e bounding boxes) e correção a um clique (alinhar B a A).',
    added: [
      'Alinhamento entre modelos (painel Mapa, com 2+ modelos): escolhe ' +
        'a referência (A) e o modelo a alinhar (B) e diagnostica: CRS ' +
        'iguais/diferentes, estado de georreferenciação de cada um, Δ ' +
        'mediano das posições de elementos com o mesmo GlobalId ' +
        '(indicador mais fiável), Δ das cotas de pisos com o mesmo nome ' +
        '(desalinhamento vertical entre disciplinas), e Δ do centro dos ' +
        'bounding boxes (grosseiro).',
      'Correção a um clique: "Alinhar B a A" (elementos comuns, só a ' +
        'cota, ou bbox) aplica o -Δ ao placement de B como override de ' +
        'sessão; para persistir no ficheiro usa depois o "Gravar georref ' +
        'no IFC" do B. Verificado: desalinhamento plantado de ' +
        '(20, 5, 2) m detetado por todos os indicadores e corrigido ' +
        'para Δ = 0.000000 m.',
    ],
  },
  {
    version: '0.2.0-beta.22',
    date: '2026-07-05',
    summary:
      'Editar a origem dos placements espaciais (IfcSite / Building / ' +
      'Storey / Space) no próprio IFC, com opção de manter as posições ' +
      'absolutas, a forma de re-referenciar a hierarquia ao IfcSite ' +
      '(o site passa a carregar as coordenadas e o resto fica com ' +
      'offsets pequenos).',
    added: [
      'Editar origem de placements (Info do modelo > Origens da ' +
        'hierarquia espacial > editar): novos campos E/N/h (metros, no ' +
        'referencial do pai) por nó espacial, com write-back textual ao ' +
        'STEP e download de <nome>_placement.ifc. Nunca reescreve ' +
        'pontos/eixos partilhados: cunha entidades novas e re-aponta ' +
        'só a IfcLocalPlacement em causa.',
      'Opção "Manter posições absolutas (compensar filhos)": os ' +
        'placements diretamente abaixo do nó são ajustados por -Δ ' +
        '(respeitando a rotação do placement) e nada muda de posição, ' +
        'só a repartição das coordenadas pela hierarquia. É o ' +
        '"re-referenciar tudo ao IfcSite": pôr as coordenadas do ' +
        'terreno no site e zerar os intermédios, ou o inverso. ' +
        'Verificado com desvio 0.000000 m em fixture e em modelo real ' +
        'de 25 MB (diff de 168 bytes).',
    ],
  },
  {
    version: '0.2.0-beta.21',
    date: '2026-07-04',
    summary:
      'P2 do sprint de posicionamento: gravar a georreferenciação no ' +
      'próprio IFC a partir do editor de posição (IfcProjectedCRS + ' +
      'IfcMapConversion, download _georef.ifc), e verificação de ' +
      'elevação contra o terreno real (DEM) no painel Mapa com ' +
      'compensação a um clique.',
    added: [
      'Gravar georref no IFC (editor de posição): escreve o placement ' +
        'efetivo (E/N/h/heading/escala) e o CRS como IfcProjectedCRS + ' +
        'IfcMapConversion nos bytes originais do ficheiro e descarrega ' +
        'um <nome>_georef.ifc. Write-back textual ao STEP: só as linhas ' +
        'de georreferenciação mudam, todo o resto fica byte a byte ' +
        'igual. Remove entidades antigas (MapConversion/Scaled, ' +
        'RigidOperation, ProjectedCRS), preserva o datum vertical ' +
        'existente, e escreve o Scale com o fator de unidade do projeto ' +
        '(mm → 0.001). Só IFC4+; IFC2X3 é recusado com indicação do ' +
        'ifc-georeferencer.',
      'Verificar elevação (terreno) no painel Mapa: compara a base do ' +
        'modelo com a altitude real do terreno no centro da pegada (DEM ' +
        'aberto Mapzen/AWS, ~30 m) e mostra o Δ; botão "Assentar no ' +
        'terreno" soma o Δ ao h do placement com um clique.',
    ],
  },
  {
    version: '0.2.0-beta.20',
    date: '2026-07-04',
    summary:
      'Posicionamento rigoroso: corrigido o desvio do modelo no mapa ' +
      '(o recentramento interno do conversor não era compensado e o ' +
      'modelo aparecia deslocado dezenas de metros face à posição ' +
      'verdadeira), corrigidas as coordenadas do painel a 2×, e nova ' +
      '"Cadeia de referenciais (exata)" no painel Coordenadas: a que ' +
      'está ancorado cada nível (elemento → piso → edifício → site → ' +
      'origem do ficheiro), com valores do STEP e projeção no mapa.',
    added: [
      'Cadeia de referenciais (exata) no painel Coordenadas: para o ' +
        'elemento selecionado, cada IfcLocalPlacement da cadeia (do ' +
        'IfcSite ao elemento) com a origem relativa ao pai e a absoluta ' +
        'no referencial do ficheiro, em metros, lidas do próprio STEP; ' +
        'com o modelo georreferenciado mostra também cada origem no ' +
        'CRS do mapa (E/N/h). Responde a "as coordenadas desta parede ' +
        'são face ao piso ou a outra referência?".',
      'Modelos com coordenadas embutidas (LoGeoRef 30, sem ' +
        'MapConversion) passam a ficar automaticamente na posição ' +
        'verdadeira do ficheiro, e o painel Mapa e o chão OSM 3D ' +
        'funcionam para eles (basta indicar o CRS).',
    ],
    fixed: [
      'Modelo deslocado no mapa: o conversor Fragments recentra a ' +
        'geometria para perto da origem local e o placement não ' +
        'compensava essa translação: o modelo ficava deslocado por ' +
        'R(θ)·t (dezenas de metros, com aparência de rodado) face a ' +
        'plataformas que trabalham nos bytes originais, como o ' +
        'ifc-georeferencer. O placement agora compõe ' +
        'T(E,h,-N)·R(θ)·S·T(-t) e aplica-se às coordenadas do FICHEIRO. ' +
        'Verificado contra valores calculados à mão (erro zero).',
      'Painel Coordenadas com valores a 2×: a versão atual do Fragments ' +
        'devolve posições/caixas per-item já em coordenadas de mundo; ' +
        'aplicar o matrixWorld em cima duplicava o transform geo ' +
        '(posição world, CRS global e Focar/Centrar da seleção em ' +
        'modelos georreferenciados). Deteção automática local/mundo ' +
        'partilhada por todos os consumidores.',
      'Reposicionar com clique no mapa passa a mover o EDIFÍCIO para o ' +
        'clique (delta sobre o centro da pegada) em vez da origem do ' +
        'ficheiro, que pode estar longe da geometria.',
    ],
  },
  {
    version: '0.2.0-beta.19',
    date: '2026-07-04',
    summary:
      'Sprint de georreferenciação, parte 2: o modelo em cima do mapa ' +
      'dentro do viewport 3D (chão de tiles OpenStreetMap, opcionalmente ' +
      'com relevo real via DEM), posicionamento rigoroso lido do próprio ' +
      'STEP (origens exatas da hierarquia espacial, deteção de ' +
      'coordenadas baked LoGeoRef 30), e marcadores de origem ' +
      'simplificados com filtro por categoria e etiquetas opcionais.',
    added: [
      'Chão OSM no 3D ("Ver no 3D" no painel Mapa): tiles OpenStreetMap ' +
        'reprojetados do WGS84 para o CRS do modelo (proj4) e desenhados ' +
        'por baixo do modelo georreferenciado no viewport. A elevação do ' +
        'chão é automática (base do modelo) ou manual ("Chão h").',
      'Relevo (DEM): checkbox "Relevo" no painel Mapa dá alturas reais de ' +
        'terreno ao chão OSM (grelha por tile com elevações dos tiles ' +
        'terrarium Mapzen/AWS, ~30 m de resolução, amostragem bilinear). ' +
        'Com relevo ligado, o campo passa a "Offset h" (ajuste vertical ' +
        'sobre o terreno).',
      'Origens exatas da hierarquia espacial: nova secção "Origens da ' +
        'hierarquia espacial" no Info do modelo lê os IfcLocalPlacement ' +
        'do próprio STEP (parser paralelo) e mostra a origem absoluta e ' +
        'relativa de Site / Building / Storey / Space com precisão total; ' +
        'clique numa linha seleciona o nó no 3D.',
      'Deteção de coordenadas baked (LoGeoRef 30): quando o IFC não tem ' +
        'MapConversion mas a geometria vive em coordenadas de mapa, o ' +
        'viewer deteta o recentramento do conversor e propõe "Usar como ' +
        'posição" no editor e no painel Mapa.',
      'Filtro de origens no marcador 3D: escolher que categorias aparecem ' +
        '(ficheiro IFC, IfcSite, IfcBuilding, IfcBuildingStorey, IfcSpace, ' +
        'origem da cena) e ligar/desligar as etiquetas de texto. Os nós ' +
        'espaciais têm tripé de eixos próprio; a origem do ficheiro é ' +
        'clicável e abre o editor de posição.',
    ],
    changed: [
      'Marcador de origem: a antiga "origem do mundo (0,0,0)" passou a ' +
        '"Origem da cena" e vem desligada por defeito (é um conceito ' +
        'interno do conversor, confundia-se com a origem do IFC). A ' +
        'origem marcada de cada modelo é a origem VERDADEIRA do ficheiro, ' +
        'incluindo o recentramento que o conversor aplica a coordenadas ' +
        'grandes.',
    ],
    fixed: [
      'Focar / Centrar em modelos georreferenciados: o bbox do modelo era ' +
        'transformado duas vezes (model.box já vinha em coordenadas world ' +
        'nalguns casos) e a câmara voava para longe do modelo.',
      'Modo "Colorir por...": o pré-selecionar e selecionar voltam a ' +
        'ser visíveis sobre elementos pintados (os materiais de highlight ' +
        'do Fragments eram apanhados pelo motor de pintura).',
      'Os overlays 3D (Levels, Grids, origem, chão OSM) só reagiam aos ' +
        'toggles com o separador Vista do ribbon ativo - ligar "Ver no ' +
        '3D" no painel Mapa com outro separador à frente não fazia nada. ' +
        'O efeito passou para um host sempre montado no viewer.',
    ],
  },
  {
    version: '0.2.0-beta.18',
    date: '2026-07-03',
    summary:
      'Sprint de georreferenciação: painel Mapa (OpenStreetMap) com a ' +
      'origem e a pegada do modelo e reposicionamento por clique, marcador ' +
      '3D de origem, CRS (EPSG) visível e editável, atributos do ' +
      'IfcProject no Info do modelo, e posições dos contentores nas ' +
      'Coordenadas. Motor ThatOpen atualizado para 3.4.6 e instância ' +
      'própria do ifc-georeferencer para georreferenciar ficheiros.',
    added: [
      'Painel Mapa (Vista > Mapa): o modelo ativo num OpenStreetMap via ' +
        'Leaflet, com a origem (E/N do IfcMapConversion ou override) e a ' +
        'pegada convertidas do CRS para lat/lon via proj4. Modo ' +
        '"Reposicionar com clique no mapa" grava E/N como posição manual; ' +
        'o 3D, o editor e o mapa atualizam em direto. Resolução de EPSG ' +
        'por dicionário local + epsg.io.',
      'Marcador de origem (Vista > Cena > Origem): eixos E/N/h e etiqueta ' +
        'na origem do mundo (0,0,0) e na origem local de cada modelo ' +
        'georreferenciado, com E/N/h/heading no tooltip. Segue as edições ' +
        'de posição em direto.',
      'CRS por modelo: o nome do IfcProjectedCRS (ex.: EPSG:3763) é lido ' +
        'no load e aparece num novo campo "CRS" do editor de posição, ' +
        'onde pode ser sobrescrito para a sessão (badge auto/manual/n-d). ' +
        'Usado pelo painel Mapa.',
      'Info do modelo: nova secção "IfcProject" com os atributos do ' +
        'projeto e botão para o abrir no painel Propriedades.',
      'Coordenadas (painel Propriedades): novo bloco "Contentores" com a ' +
        'posição world (centro geométrico) de cada contentor espacial ' +
        'acima do elemento selecionado.',
      'Link para o ifc-georeferencer (instância VN2R da ferramenta open ' +
        'source do buildingSMART NL) no editor de posição e na ajuda, ' +
        'para escrever IfcMapConversion/IfcProjectedCRS no próprio IFC.',
    ],
    changed: [
      'Motor ThatOpen: a atualização para 3.4.6 foi revertida no próprio ' +
        'dia: a partir do 3.4.4 os tiles do modelo deixavam de renderizar ' +
        '(seleção funcionava, geometria invisível). O motor fica em ' +
        'components 3.4.2 / fragments 3.4.3 até o problema estar ' +
        'compreendido. web-ifc mantém-se em 0.0.77 (limitação IFC 4.3 ' +
        'continua).',
    ],
  },
  {
    version: '0.2.0-beta.17',
    date: '2026-06-21',
    summary:
      'Diff de versões IFC (Fase K), modo mobile dedicado e abrir IFC de ' +
      'exemplo. O painel Comparar IFC passa a colorir o resultado no 3D, ' +
      'comparar Psets/Qsets, detetar elementos movidos e mudanças de piso, ' +
      'e exportar CSV. No telemóvel a interface usa uma barra inferior com ' +
      'drawers, e há um botão para descarregar um modelo de exemplo.',
    added: [
      'Diff de versões: cores no 3D (removidos a vermelho no modelo A, ' +
        'novos a verde e alterados a amarelo no modelo B), legenda e ' +
        'toggle "Ver no 3D". A camada de diff vive no classColors (acima ' +
        'de palette, critério e pinturas manuais) e sobrevive a repaints.',
      'Diff compara propriedades de Psets e quantidades de Qsets por ' +
        'GlobalId, com as mudanças marcadas "pset" na linha alterada. ' +
        'Extração por uma passagem sobre IfcRelDefinesByProperties (o ' +
        'getEntity usa inverse=false, por isso o IsDefinedBy não vem).',
      'Diff deteta elementos movidos (deslocamento do centro do bounding ' +
        'box acima de 5 mm, mostrado em mm) e mudança de contentor ' +
        'espacial (mudou de IfcBuildingStorey / IfcSpace).',
      'Diff: cabeçalho de resumo (nome e schema dos dois modelos, total de ' +
        'alterações) e export do resultado para CSV, que abre no Excel.',
      'Modo mobile dedicado (abaixo de 768px): o ribbon dá lugar a uma ' +
        'barra de ação inferior (Modelos, Abrir, Exemplo, Focar, Medir, ' +
        'Propriedades) e as sidebars passam a drawers deslizantes. ' +
        'Controlo por toque: 1 dedo orbita, toque simples seleciona, 2 ' +
        'dedos pan e zoom.',
      'Abrir IFC de exemplo: botão que descarrega um modelo público para ' +
        'testar o viewer sem ter um ficheiro à mão (casa pequena ~0,4 MB ' +
        'ou estrutura de escola ~8,6 MB). No ribbon, na barra mobile e no ' +
        'ecrã de boas-vindas sobre o canvas vazio.',
      'Arrastar-e-largar: arrasta um ou mais ficheiros .ifc / .ifczip para ' +
        'o viewport para os abrir (adiciona à federação).',
    ],
    fixed: [
      'Georreferenciação automática: o baseline lido do IfcMapConversion ' +
        'era apagado logo a seguir pelo dedup do loader (removeModel limpa ' +
        'geoBaselines/geoOverrides do id), e o modelo voltava à origem com ' +
        'o badge "origem" em vez de "auto · MapConversion". O dedup passou ' +
        'para antes do bloco geo. Afetava todos os modelos georreferenciados ' +
        'desde a Onda A.',
    ],
  },
  {
    version: '0.2.0-beta.16',
    date: '2026-05-13',
    summary:
      'Onda C da federação georreferenciada: os overrides de placement ' +
      'manual passam a viver no `.vn2r-session`. Guardar sessão escreve ' +
      'os 5 valores por modelo (Eastings, Northings, Altura, Heading, ' +
      'Escala) no manifest; abrir sessão restaura-os automaticamente ao ' +
      'recarregar cada IFC.',
    added: [
      'Schema da sessão `.vn2r-session` v4: cada VnSessionModel ganha ' +
        '`placement_override?: GeoPlacement | null`. v2 e v3 continuam ' +
        'aceites em leitura: modelos sem o campo retomam o ' +
        'IfcMapConversion baseline como antes.',
      'Save Session: o topbar lê `geoOverrides` e `geoBaselines` do ' +
        'store e embebe no manifest só os overrides que diferem do ' +
        'baseline auto-derivado. Reset não polui o ficheiro.',
      'Open Session: o loader cruza o sha256 do IFC carregado com a ' +
        'lista `sessionRestoreModels`, e quando encontra um ' +
        '`placement_override` injecta-o via `setGeoOverride(modelId, ...)`. ' +
        'O `useEffect` da Onda B re-aplica o transform ao model.object ' +
        'logo de seguida.',
    ],
    fixed: [
      'Match de override por sha256 (não por filename), tolerante a ' +
        'modelos cujo ficheiro mudou de nome entre save e load.',
    ],
  },
  {
    version: '0.2.0-beta.15',
    date: '2026-05-13',
    summary:
      'Onda B da federação georreferenciada: editor de posição por ' +
      'modelo no sidebar. Permite introduzir manualmente Eastings, ' +
      'Northings, OrthogonalHeight, Heading e Escala para modelos ' +
      'cujo IFC não declara IfcMapConversion (típico em exports Revit ' +
      'sem survey point), ou sobrescrever o que o MapConversion ' +
      'definiu.',
    added: [
      'Botão "Compass" em cada linha de modelo no sidebar abre o ' +
        'editor "Posição" inline. 5 campos numéricos com unidades ' +
        '(metros e graus) + botão Reset. Edição commit por Enter ou ' +
        'blur; Esc cancela.',
      'Badge per-row indica o estado: "auto · MapConversion" (verde) ' +
        'quando o modelo trouxe um IfcMapConversion; "manual" (âmbar) ' +
        'quando o user sobrescreveu; "origem" (cinzento) quando o ' +
        'modelo não tem MapConversion e fica na origem por defeito.',
      'O placement override (per-modelId) é absoluto: substitui ' +
        'completamente o baseline. Reset volta a aplicar o baseline ' +
        '(auto-derivado ou identidade).',
      'useEffect no useViewer re-aplica o placement ao model.object ' +
        'sempre que geoOverrides ou geoBaselines mudam. Bbox / camera ' +
        'fit / section box / inspector lêem matrixWorld a cada uso, ' +
        'portanto seguem o novo placement sem refrescos adicionais.',
    ],
    changed: [
      'geoTransform.ts foi reescrito à volta de um novo tipo ' +
        '`GeoPlacement` (Eastings/Northings/h/Heading/Scale, em ' +
        'convenção IFC). `applyGeoPlacement(model, placement)` é a ' +
        'função imperativa única; `applyGeoTransform(model, crs)` ' +
        'continua disponível como conveniência para o load path.',
      '`placementFromMapConversion` extrai um GeoPlacement a partir ' +
        'do CRSData do Fragments. `placementsDiffer` compara dois ' +
        'placements com tolerâncias razoáveis (1mm / 1e-6).',
    ],
    fixed: [
      'Remover um modelo limpa também a sua baseline e override do ' +
        'store, para evitar drift quando o user re-carrega o mesmo IFC.',
    ],
  },
  {
    version: '0.2.0-beta.14',
    date: '2026-05-13',
    summary:
      'Federação georreferenciada: ao carregar um IFC, o IfcMapConversion ' +
      'é lido e aplicado como matriz de mundo do modelo. Modelos do mesmo ' +
      'projecto exportados de ferramentas diferentes (Revit + Civil 3D, ' +
      'arquitectura + estrutura, ...) caem agora nas posições reais ' +
      'relativas em vez de empilhados todos na origem.',
    added: [
      'Onda A da federação geo: novo `viewer/lib/geoTransform.ts` que lê ' +
        'o IfcMapConversion via `model.getCRS()` e baka Eastings/Northings/' +
        'OrthogonalHeight + rotação do X-axis + scale como transform do ' +
        'root do modelo no Three.js. Modelos sem MapConversion ficam na ' +
        'origem (comportamento histórico, sem regressão).',
      'Camera fit (loader inicial + fitToAllModels + focusOnSelection + ' +
        'setOrbitToElement + setOrbitAndLookAtElement) agora transforma o ' +
        'bbox local pela matrixWorld do modelo antes de unir / focar. ' +
        'Sem isto, dois modelos com origens diferentes apareceriam todos ' +
        'fit-to-bbox sobrepostos.',
      'Inspector mostra badge verde "geo · APLICADO" na secção CRS/Origem ' +
        'do painel ModelInfo quando o IfcMapConversion foi aplicado.',
      'Section box AABB inicial passa a usar as bboxes em world coords ' +
        '(matrixWorld), seguindo o conjunto federado geo-referenciado.',
    ],
    changed: [
      'Posição world e bbox surface no painel de propriedades reflectem ' +
        'agora a transformação geo aplicada; antes mostravam coords ' +
        'locais ao modelo (que coincidiam com world só porque o root ' +
        'estava em (0,0,0)). Para modelos com MapConversion, os números ' +
        'são grandes (eastings/northings reais).',
      'A derivação CRS global (E/N/h) passa a ser um axis remap directo ' +
        'em vez de aplicar `localToGlobal()`: a transformação já foi ' +
        'aplicada no load. A helper `localToGlobal()` deixou de ser ' +
        'usada e foi removida.',
    ],
    fixed: [
      'O helper `worldBoxesFor(model, ids)` em viewerApi.ts encapsula a ' +
        'transformação local→world para todas as chamadas getBoxes, ' +
        'eliminando duplicação e garantindo que qualquer consumidor de ' +
        'bbox (camera fit, orbit centre, focus selection, ...) trabalha ' +
        'em world coords coerentes.',
    ],
  },
  {
    version: '0.2.0-beta.13',
    date: '2026-05-13',
    summary:
      'Painel de propriedades agora vê e edita atributos para entidades ' +
      'não-geométricas (IfcSystem, IfcDistributionSystem, IfcBuildingSystem, ' +
      'IfcZone, IfcGroup, IfcElementAssembly, IfcSpatialZone, ' +
      'IfcStructuralAnalysisModel). PredefinedType de cada uma destas ' +
      'classes ganhou dropdown próprio.',
    added: [
      'Augmentação automática do payload do painel com o parser rawIfc ' +
        'para entidades que Fragments devolve quase vazias (typicamente ' +
        'systems, groups, zones, assemblies). Atributos como Name, ' +
        'Description, ObjectType, LongName e PredefinedType passam a ' +
        'aparecer mesmo quando Fragments não os surface, e portanto ' +
        'tornam-se editáveis inline.',
      'Dropdowns PredefinedType schema-aware para: IfcDistributionSystem ' +
        '(IfcDistributionSystemEnum: HEATING, COOLING, DOMESTICCOLDWATER, ' +
        'ELECTRICAL, ...), IfcBuildingSystem (IfcBuildingSystemTypeEnum), ' +
        'IfcSpatialZone (IfcSpatialZoneTypeEnum), IfcElementAssembly ' +
        '(IfcElementAssemblyTypeEnum) e IfcStructuralAnalysisModel ' +
        '(IfcAnalysisModelTypeEnum).',
      'Per-class LongName overrides no patcher: IfcDistributionSystem ' +
        'tem LongName ao índice 5, IfcBuildingSystem ao 6 e IfcZone (IFC4+) ' +
        'ao 5, diferentes da posição 7 dos IfcSpatialElement.',
    ],
    fixed: [
      'Gerador de enums (`scripts/regen-ifc-enums.mjs`) agora apanha ' +
        'classes que terminam em `Enum` (não só em `TypeEnum`). Isso ' +
        'expande a tabela de 445 para 580 enums e inclui ' +
        'IfcDistributionSystemEnum, IfcStateEnum, IfcChangeActionEnum, ' +
        'etc.',
    ],
  },
  {
    version: '0.2.0-beta.12',
    date: '2026-05-13',
    summary:
      'IFCEdit ganha edição inline de valores em Psets e Qsets ' +
      '(IfcPropertySingleValue.NominalValue + IfcQuantity*.<Value>). ' +
      'O patcher navega IfcRelDefinesByProperties → IfcPropertySet → ' +
      'IfcPropertySingleValue e reescreve só o valor, preservando o ' +
      'type wrapper (IFCREAL / IFCTEXT / IFCBOOLEAN / ...). ' +
      'Inspector ganha cobertura completa de inversos IFC (Covers, ' +
      'Projects, Interferes, Nests, Sequence, ServicesBuildings, ' +
      'SpaceBoundary, ...) e calcula a área das space boundaries 1st ' +
      'e 2nd level a partir do ConnectionGeometry.',
    added: [
      'Edição inline de valores em property sets e quantity sets no ' +
        'painel de propriedades. Cada row ganha lápis; strings abrem ' +
        'editor de texto, booleans (true/false) abrem dropdown ' +
        '(T/F/U), numerics aceitam texto e são validados como número ' +
        'no export. O type wrapper original (IFCREAL, IFCLENGTHMEASURE, ' +
        'IFCTEXT, IFCLABEL, IFCBOOLEAN, IFCLOGICAL, ...) é preservado ' +
        'pelo patcher.',
      'Edição de valores de Qsets (IfcQuantityLength / Area / Volume / ' +
        'Count / Weight). O valor numérico é reescrito em-place; o ' +
        'entity class garante o tipo.',
      'Indexer STEP one-pass para o patcher: constrói lineByExpressId, ' +
        'expressIdByGuid, psetsByEntity e propsByPset. Para edits em ' +
        'massa o custo de navegação é O(1) por edit.',
      'Inspector mostra todas as relações inversas IFC, não só as que ' +
        'o IfcLoader do Fragments popula. O parser paralelo varre os ' +
        'IfcRel* uma vez por modelo (IfcRelContainedInSpatialStructure, ' +
        'IfcRelReferencedInSpatialStructure, IfcRelAggregates, ' +
        'IfcRelNests, IfcRelDeclares, IfcRelConnectsElements/' +
        'WithRealizingElements/PathElements, IfcRelCoversBldgElements, ' +
        'IfcRelCoversSpaces, IfcRelVoidsElement, IfcRelFillsElement, ' +
        'IfcRelProjectsElement, IfcRelInterferesElements, IfcRelSequence, ' +
        'IfcRelServicesBuildings, IfcRelSpaceBoundary*) e funde os ' +
        'inversos na secção "Relações inversas" com dedup contra o ' +
        'que o Fragments já trouxe. Cada elemento mostra agora ' +
        'ContainsElements / ReferencesElements / IsDecomposedBy / ' +
        'Decomposes / IsNestedBy / Nests / Declares / HasContext / ' +
        'ConnectedTo / ConnectedFrom / HasCoverings / CoversElements / ' +
        'CoversSpaces / HasOpenings / VoidsElements / HasFillings / ' +
        'FillsVoids / HasProjections / ProjectsElements / ' +
        'InterferesElements / IsInterferedByElements / IsPredecessorTo / ' +
        'IsSuccessorFrom / ServicesBuildings / ServicedBySystems / ' +
        'BoundedBy / ProvidesBoundaries onde aplicável.',
      'Área da superfície das space boundaries calculada a partir do ' +
        'ConnectionGeometry. Cada entry em BoundedBy / ProvidesBoundaries ' +
        'mostra agora "[PHYSICAL, INTERNAL, 12.34 m²]" quando a ' +
        'ConnectionGeometry é IfcConnectionSurfaceGeometry com ' +
        'IfcFaceSurface (PolyLoop) ou IfcCurveBoundedPlane (Polyline). ' +
        'Cálculo via Newell em 3D, robusto para faces oblíquas, ' +
        'subtrai inner boundaries (vãos de janela/porta). Composite ' +
        'curves, parametric/swept surfaces e arcos ficam sem área (em ' +
        'vez de chutar um valor).',
    ],
    fixed: [
      'Limpar edições do painel home limpa também o pset overlay e o ' +
        'class overlay (resetIfcEdit agora cobre as três overlays).',
    ],
  },
  {
    version: '0.2.0-beta.11',
    date: '2026-05-13',
    summary:
      'IFCEdit ganha mudança de classe IFC dentro de subclasses de ' +
      'IfcBuildingElement com layout STEP idêntico (Wall, Slab, Beam, ' +
      'Column, Roof, Covering, Stair, Ramp, Railing, Footing, Pile, ' +
      'Chimney, ShadingDevice, BuildingElementProxy, ...). Quando a ' +
      'classe muda, PredefinedType é auto-reset para NOTDEFINED se o ' +
      'valor actual não pertencer ao enum da nova classe.',
    added: [
      'Editor de "Classe" no painel de propriedades (dropdown com as ' +
        'subclasses de IfcBuildingElement compatíveis). Aparece como linha ' +
        'sintetizada na secção Atributos, ordenada alfabeticamente. Só ' +
        'mostra a opção para elementos cujo class actual está na ' +
        'compatibility set: Doors, Windows, Spaces e MEP elements ficam ' +
        'protegidos (têm layouts diferentes).',
      'Auto-fallback do PredefinedType quando muda a classe: se o valor ' +
        'existente não estiver no enum da nova classe, o overlay regista ' +
        'NOTDEFINED para evitar produzir um IFC com PredefinedType inválido.',
      'Pipeline de export agora aplica class edits ANTES de attribute ' +
        'edits, para que qualquer PredefinedType subsequente leia a classe ' +
        'já reescrita e aponta para o slot correcto.',
      'Painel home do IFCEdit mostra resumo das mudanças de classe ' +
        'pendentes ao lado dos atributos editados.',
    ],
    fixed: [
      'O patcher STEP de class change exige que tanto a classe origem ' +
        'como destino estejam na compatibility set; pedidos fora dela ' +
        'são recusados antes de tocarem nos bytes.',
    ],
  },
  {
    version: '0.2.0-beta.10',
    date: '2026-05-13',
    summary:
      'IFCEdit ganha edição de PredefinedType com dropdown das opções ' +
      'válidas para o esquema IFC do modelo. Os valores são extraídos ' +
      'directamente do schema do web-ifc (IFC2X3, IFC4, IFC4X3) e ' +
      'aparecem por classe (IfcWall mostra WallTypeEnum, IfcSlab mostra ' +
      'SlabTypeEnum, etc.). Classes que só ganharam PredefinedType em ' +
      'IFC4+ ficam read-only em IFC2X3 (o atributo nem existe).',
    added: [
      'Editor de PredefinedType como dropdown schema-aware no painel de ' +
        'propriedades. Lista as opções legais para a combinação ' +
        '(classe, esquema), extraídas do ifc-schema.d.ts do web-ifc, ' +
        'mais "(vazio)" para limpar. Mantém-se o valor actual mesmo que ' +
        'não esteja na lista (caso de USERDEFINED com ObjectType ' +
        'definido), por isso editar+cancelar nunca destrói dados.',
      'Suporte do patcher STEP a slots de enumeração (`.X.`). Mantém o ' +
        'mesmo nível de safety: o argumento existente tem de ter o ' +
        'shape correcto (string para Name/Description/..., enum para ' +
        'PredefinedType) ou a edição é recusada e marcada como "não ' +
        'correspondida".',
      'Script `scripts/regen-ifc-enums.mjs` para regenerar a tabela de ' +
        'valores de enums (445 enums, 3517 valores) a partir do ' +
        '`node_modules/web-ifc/ifc-schema.d.ts`. Correr quando se actualizar ' +
        'a versão do web-ifc.',
    ],
  },
  {
    version: '0.2.0-beta.9',
    date: '2026-05-13',
    summary:
      'IFCEdit ganha mais três atributos editáveis no painel de ' +
      'propriedades: Tag, ObjectType e LongName, ao lado de Name e ' +
      'Description. Cada atributo só aparece onde faz sentido pela ' +
      'inheritance IFC, e o exportador valida antes de reescrever para ' +
      'nunca corromper o ficheiro.',
    added: [
      'Edição inline de Tag (qualquer IfcElement), ObjectType (qualquer ' +
        'IfcObject) e LongName (qualquer IfcSpatialElement / IfcSpatial' +
        'StructureElement) no painel de propriedades. Se o elemento ainda ' +
        'não tinha o valor populado, o atributo aparece como "vazio" com o ' +
        'lápis para o user preencher.',
      'Header chips reactivos: as chips Name / Tag / ObjectType / LongName ' +
        'no topo do painel reflectem agora as edições inline em tempo real ' +
        '(antes ficavam congeladas com o valor original).',
    ],
    fixed: [
      'Safety check no patcher STEP: antes de reescrever um argumento, ' +
        'verifica que o slot é string-shaped (single-quoted ou NULL). ' +
        'Recusa a edição se encontrar número, referência, enum ou wrapper ' +
        'tipado: garante que o ficheiro nunca pode ser corrompido por ' +
        'uma má inferência de posição.',
    ],
  },
  {
    version: '0.2.0-beta.8',
    date: '2026-05-11',
    summary:
      'Modo Ghost agora exposto na ribbon (Vista · Cena), com botão de ' +
      'opacidade ao lado. Antes só era acessível via atalho G ou pelo ' +
      'painel Aparência.',
    added: [
      'Toggle "Ghost" na ribbon (Vista · Cena) ao lado de "Esconder ' +
        'spaces". Atalho G continua a funcionar. Quando ligado, os ' +
        'elementos não-seleccionados ficam translúcidos para a selecção ' +
        'saltar à vista; sem nada seleccionado, é uma vista raio-X de ' +
        'todo o modelo.',
      'Botão de opacidade adjacente (visível quando o Ghost está ligado) ' +
        'com slider 2–50% num popover, para ajustar a translucidez sem abrir ' +
        'o painel Aparência.',
    ],
  },
  {
    version: '0.2.0-beta.7',
    date: '2026-05-11',
    summary:
      'Diagnóstico mostrou que algumas meshes (4 num modelo de teste) ' +
      'não têm sequer attribute `id` acessível: nem snapshot, nem ' +
      'live. Para essas, o `addEdges` gerava linhas com `id = 0` por ' +
      'defeito; o shader olhava para `texture[0]` (sempre visível) e ' +
      'desenhava sempre. Eram esses os wireframes fantasma que ' +
      'sobravam. Agora saltamos completamente a geração de arestas ' +
      'para meshes sem fonte de `id`.',
    fixed: [
      'Wireframes fantasma de meshes sem `id` attribute deixam de ' +
        'aparecer. Trade-off: se uma mesh legítima ficar sem `id` (raro, ' +
        'caminho de geometria auxiliar), perde os outlines. Preferível a ' +
        'ter wireframes que não correspondem a geometria visível.',
    ],
  },
  {
    version: '0.2.0-beta.6',
    date: '2026-05-11',
    summary:
      'Follow-up ao beta.5: alguns IfcSpaces continuavam com wireframes ' +
      'fantasma porque o snapshot do attribute `id` falhava em capturar ' +
      'certas meshes (Fragments por vezes popula o atributo via um caminho ' +
      'que não passa pelo monkey-patch de setAttribute). Tanto a detecção ' +
      'de contentores espaciais como o baking do `id` na geometria de ' +
      'arestas usam agora o atributo live como fallback.',
    fixed: [
      'Wireframes de IfcSpace deixam de aparecer mesmo quando o snapshot ' +
        'do `id` falha em capturar a mesh. Cleanup e addEdges usam ' +
        'getMeshIdArray que lê primeiro o snapshot e depois o atributo ' +
        'live; o baking de arestas também faz o mesmo fallback para ' +
        'garantir que o `id` per-vertex está sempre presente quando ' +
        'a fonte o tem.',
    ],
  },
  {
    version: '0.2.0-beta.5',
    date: '2026-05-11',
    summary:
      'Outlines de IfcSpace / contentores espaciais deixam de aparecer ' +
      'quando os shells estão escondidos. Antes, esconder uma space ' +
      'apagava a superfície mas mantinha o wireframe da bounding box ' +
      'porque o filtro per-item dependia do Fragments setVisible, e os ' +
      'spatial containers ficam permanentemente "visíveis" no Fragments ' +
      '(o shell é que é alpha=0). Agora marcamos esses items como ' +
      '"permanentemente escondidos" para o filtro de arestas, ' +
      'independentemente do estado de visibilidade do Fragments.',
    fixed: [
      'Edges de IfcSpace / IfcBuildingStorey / IfcZone / IfcSpatialZone / ' +
        'IfcBuilding / IfcSite deixam de aparecer como wireframes ' +
        'fantasmas. O conjunto permanente é marcado no ' +
        'edgeVisibility shader uma vez quando os contentores espaciais ' +
        'são detectados; refresh subsequentes preservam-no.',
    ],
  },
  {
    version: '0.2.0-beta.4',
    date: '2026-05-11',
    summary:
      'Arestas agora filtradas por-item via shader (descarta fragments ' +
      'de items escondidos) em vez de auto-toggle global. A janela de ' +
      'abrir IFC mostra .ifc e .ifczip como filtros separados no ' +
      'dropdown.',
    changed: [
      'Edges deixam de ter auto-toggle global ligado à visibilidade ' +
        'dos items. Cada LineSegments traz um attribute `id` (mapeado ' +
        'do per-vertex localId do mesh fonte); o material patched lê ' +
        'uma DataTexture R8 de visibilidade por modelo e descarta os ' +
        'fragments dos items hidden. Ao esconder/mostrar items a ' +
        'textura é actualizada, sem mexer no toggle "Arestas".',
      'Janela de abrir IFC mostra .ifc e .ifczip como filtros ' +
        'separados (showOpenFilePicker types agrupados).',
    ],
  },
  {
    version: '0.2.0-beta.3',
    date: '2026-05-11',
    summary:
      'Suporte a .ifczip (zip que envolve um .ifc) directamente do file ' +
      'picker: extrai o .ifc interno via fflate e segue o pipeline ' +
      'normal. Toggle de arestas volta a ser sincronizado com o estado ' +
      'de visibilidade dos elementos: auto-off ao esconder, auto-on ao ' +
      'mostrar tudo, sem perder a tua preferência manual.',
    added: [
      'File picker do viewer e do banner Open Session aceitam .ifczip ' +
        'além de .ifc. O ficheiro interno é extraído (fflate.unzipSync) ' +
        'e segue o pipeline normal: cache, schema, Fragments load, etc.',
    ],
    fixed: [
      'Arestas voltam a sincronizar com a visibilidade dos elementos. ' +
        'Auto-disable quando há items escondidos (evita wireframe ' +
        'fantasma) E auto-re-enable quando todos visíveis. Se mexeres no ' +
        'toggle manualmente, mantemos a tua escolha sem reverter.',
    ],
  },
  {
    version: '0.2.0-beta.2',
    date: '2026-05-11',
    summary:
      'Hotfix de produção: as cores não funcionavam na deploy porque ' +
      'Rolldown minifica o nome dos construtores, e o filtro de paint ' +
      'comparava `material.constructor.name === "MeshLambertMaterial"`. ' +
      'Passou a usar `material.type` (campo string sempre presente) e ' +
      'a flag `isMeshLambertMaterial` como fallbacks.',
    fixed: [
      'Pintura: Cores por classe / Colorir por… / Aparência funcionam ' +
        'em produção (não só em dev).',
    ],
  },
  {
    version: '0.2.0-beta.1',
    date: '2026-05-11',
    summary:
      'Reescrita do motor de pintura (bypass total ao Fragments setColor ' +
      'via shader + DataTexture) e nova ferramenta de Filtro multi-critério. ' +
      'Threshold de arestas configurável; outlines de IfcSpace / storey ' +
      'já não aparecem como "fantasmas" quando os spaces estão escondidos.',
    added: [
      'Filtro multi-critério (Shift+F): query builder com regras AND/OR sobre ' +
        'entidade, storey, sistema, material, tipo, PredefinedType e propriedades. ' +
        'Multi-select por regra (OR interno), operadores =, ≠, contém, começa por, ' +
        '>, ≥, <, ≤. Aplica como selecção; depois esconder/isolar/aparência.',
      'Threshold de arestas configurável em Diagnóstico (5°–80°, default 30°). ' +
        'Útil para HVAC: baixar vê tessellation seams, subir limpa.',
      'Atalhos C (Colorir por…) e Shift+F (Filtro).',
    ],
    changed: [
      'Motor de pintura completamente reescrito: paint engine com shader ' +
        'patch + DataTexture per-vertex, indexada pelo "id" attribute do ' +
        'Fragments. Sem chamadas a setColor / resetColor. Resolve o leak ' +
        'estrutural do bitmask do ItemConfigController em Fragments 3.4.',
      'Colorir por…: 6 critérios (entidade, PredefinedType, sistema/grupo, ' +
        'material, tipo, propriedade), federação multi-IFC, sem WIP flag.',
      'Sistemas/grupos no filtro mostram nomes legíveis (não mais "#123").',
      'Edges para IfcSpace / IfcBuildingStorey / IfcZone / etc. são ' +
        'suprimidas para evitar caixas wireframe a flutuar mesmo quando ' +
        'as spaces estão escondidas.',
    ],
    fixed: [
      'Painéis flutuantes (Aparência, Colorir por…, Filtro) abrem em modo ' +
        'local sem auth (antes só montavam em modo cloud via WorkspaceLayout).',
      'Tradução localId → itemIndex em todos os caminhos que tocam o ' +
        'attribute "id" do geometry (paint, edges) para evitar mapping errado.',
      'Ligar/desligar "Esconder spaces" já não desliga as arestas.',
    ],
  },
  {
    version: '0.1.0-beta.1',
    date: '2026-05-08',
    summary:
      'Primeira release pública (beta). O viewer base está estável; ' +
      'módulos opcionais (BCF, BoQ, IFCEdit) adicionam funcionalidade ' +
      'por workspace. Os bytes do IFC nunca saem do computador do ' +
      'utilizador; a cloud serve apenas como portão de acesso.',
    added: [
      'Viewer base completo: pipeline ThatOpen Fragments, federação de modelos, edges de contorno.',
      'Inspector: herança, atributos, property sets, quantity sets, relações, classificações, materiais.',
      'Spatial tree, view cube, section box, selecção custom (hover, marquee, Ctrl/Shift).',
      'Right sidebar com 3 modos: fixo, destacável, janela popup do browser.',
      'Save/Open Session: container .vn2r-session com bytes IFC, layout, BCFs e viewpoints embutidos.',
      'Cache local de IFCs (IndexedDB) para reabertura silenciosa entre sessões.',
      'Auth cloud por magic link e password opcional, sessões httpOnly, painel admin VN2R, audit log.',
      'Módulo BCF: import .bcfzip, multi-versão (time-machine), comentários, viewpoints, labels, status/priority/topic-type por workspace, watchers, capturar vista → tópico.',
      'Módulo BoQ: cálculo client-side via web-ifc, filtros por classe, decomposição IFC, export CSV/Excel, write-back para Qsets.',
      'Módulo IFCEdit (skeleton): export do IFC actual.',
      'Histórico hub-wide na topbar; popover Diagnóstico com indicador de build (commit + hora).',
    ],
  },
]

// Catálogo de funcionalidades visível na popover Sobre / Diagnóstico.
// Reflecte o ESTADO ACTUAL da plataforma: não muda a cada release, só
// quando funcionalidades entram ou saem. "module" liga o grupo à
// activação por workspace e usa os slugs reais de modules/registry.ts;
// a UI mostra sempre o grupo e marca-o "Activo" ou "Opcional" conforme
// o módulo esteja ligado neste workspace.
export interface FeatureGroup {
  title: string
  module?: 'bcf' | 'boq' | 'ifc-edit' | 'info-table' | 'ids' | 'assistant'
  items: string[]
}

export const FEATURES: FeatureGroup[] = [
  {
    title: 'Viewer 3D',
    items: [
      'Carregamento de IFC do disco (file picker ou arrastar-e-largar o ' +
        'ficheiro para o viewport) com pipeline ThatOpen Fragments.',
      'Suporte a IFC 2x3, 4, 4x1, 4x2 e 4x3.',
      'Federação de múltiplos IFCs no mesmo viewport com toggle de visibilidade e fit-to-all.',
      'Edges de contorno em todas as meshes (LineSegments) com threshold ' +
        'configurável (Diagnóstico, 5° a 80°). Auto-suprimidas em IfcSpace ' +
        'e contentores espaciais para não desenhar caixas wireframe.',
      'Cores por classe IFC opcional (palette estilo BIMvision/Solibri), ' +
        'aplicada via shader override (sem leak do Fragments setColor).',
      'Abrir IFC de exemplo: descarrega um modelo público (casa, escola ou ' +
        'clínica) para testar sem ter um ficheiro à mão. Disponível no ' +
        'ribbon e no ecrã de boas-vindas do canvas vazio.',
      'Layout responsivo: abaixo de 768px o ribbon dá lugar a uma barra ' +
        'de ação inferior e as sidebars passam a drawers, com controlo de ' +
        'câmara por toque (1 dedo orbita, 2 dedos pan e zoom).',
      'Mapa (Vista > Mapa): o modelo ativo num OpenStreetMap, com a origem ' +
        'e a pegada convertidas do CRS (EPSG, via proj4) para lat/lon, e ' +
        'modo de reposicionar a origem com um clique no mapa. "Ver no 3D" ' +
        'desenha o chão de tiles OSM por baixo do modelo no viewport, ' +
        'plano ou com relevo real (DEM terrarium Mapzen/AWS).',
      'Marcador de origem 3D (Aparência > Referências > Origem): tripé E/N/h na ' +
        'origem verdadeira do ficheiro de cada modelo (clicável, abre o ' +
        'editor de posição) e tripés pequenos nas origens exatas de ' +
        'Site / Building / Storey / Space lidas do STEP. Filtro por ' +
        'categoria e etiquetas opcionais.',
      'Posicionamento rigoroso e correção com write-back: cadeia de ' +
        'referenciais exata por elemento (painel Coordenadas), edição ' +
        'da origem de placements espaciais com opção de manter posições ' +
        'absolutas (re-referenciar ao IfcSite), gravação do ' +
        'IfcMapConversion/IfcProjectedCRS no próprio IFC (download), ' +
        'verificação de elevação contra o terreno real (DEM) e ' +
        'diagnóstico/correção de alinhamento entre dois modelos.',
      'Export do modelo para Google Earth (KMZ) já georreferenciado, com ' +
        'geometria instanciada (o modelo sai inteiro), cores por categoria ' +
        'IFC e barra de progresso.',
    ],
  },
  {
    title: 'Navegação e selecção',
    items: [
      'View cube interactivo com pré-sets ortogonais e isométricos.',
      'Section box: drag overlay, painel de coordenadas, AABB.',
      'Selecção: hover overlay, marquee, modificadores Ctrl/Shift, raycasting manual.',
      'Janelas de propriedades flutuantes arrastáveis (FloatingPropertiesWindow).',
      'Filtro multi-critério (Shift+F): query builder com AND/OR, multi-select ' +
        'por regra, operadores =, ≠, contém, começa por, >, ≥, <, ≤. ' +
        'Dimensões: entidade IFC, storey, sistema/grupo, material, tipo, ' +
        'PredefinedType, propriedade. Aplica como selecção.',
    ],
  },
  {
    title: 'Medições e anotações no 3D',
    items: [
      'Separador Medições do ribbon: distância entre dois pontos, ' +
        'comprimento de uma aresta (com snap à aresta), área por polígono ' +
        'e modo Coordenadas (HUD com X/Y/Z do cursor e etiqueta fixa em ' +
        'cada clique, absoluta e relativa a uma origem à escolha).',
      'Unidades configuráveis, plano de trabalho, mostrar/esconder todas ' +
        'as cotas sem as apagar, apagar uma a uma ou limpar tudo.',
      'Separador Anotações: etiquetas de texto, setas com texto, ' +
        'rectângulos de marcação e nuvens de revisão, desenhados sobre o ' +
        'modelo no plano de trabalho activo.',
    ],
  },
  {
    title: 'Vistas 2D (plantas e cortes)',
    items: [
      'Modo próprio, a par do 3D: comutador "3D | 2D" junto à área do ' +
        'modelo, botão "Vistas 2D" (ribbon, separador Vista) e botão "2D" ' +
        'no telemóvel. Trocar de modo preserva a câmara 3D, a vista ' +
        'aberta, as anotações e as cores. O modo 2D tem o navegador de ' +
        'vistas à esquerda, o desenho ao centro e as propriedades à direita.',
      'Plantas por piso com um clique (corte 1,20 m acima da cota do piso ' +
        'e profundidade de 4 m, ambos editáveis) ou "planta livre" com a ' +
        'cota absoluta e a profundidade que quiseres.',
      'Cortes por três caminhos: da caixa de corte, por azimute (N/E/S/O ' +
        'ou ângulo e ponto do plano), ou desenhados sobre a planta com ' +
        'dois cliques, com nomes automáticos Corte A-A, B-B, ..., marcas ' +
        'com setas na planta e "Inverter direção".',
      'Arcos e círculos verdadeiros reconhecidos na geometria cortada e ' +
        'projectada: curvas suaves no ecrã e no PDF, entidades CIRCLE e ' +
        'ARC nativas no DXF. O que é genuinamente facetado fica facetado, ' +
        'e o interruptor "Arcos suaves" mostra a tesselação do ficheiro.',
      'Poché: preenchimento das zonas cortadas com os vãos respeitados ' +
        'como furos. Cor do traço e cor do poché editáveis por camada ' +
        '(uma camada por categoria IFC), com repor.',
      'Selecção nos dois sentidos: clicar numa linha ou mancha do desenho ' +
        'selecciona o elemento no 3D, e a selecção feita no 3D, na árvore ' +
        'ou na tabela aparece realçada no desenho.',
      'Anotações de texto colocadas por clique, arrastáveis e editáveis ' +
        'por duplo clique.',
      'Cotagem persistente: as medições ficam desenhadas como cotas de ' +
        'arquitectura (linear, encadeada com total, e área com A= e ' +
        'perímetro), gravadas com a vista e apagáveis uma a uma ou todas.',
      'Snap com quatro tipos e marca própria: vértice (quadrado), ' +
        'interseção (X), ponto médio (triângulo) e centro de círculo ou ' +
        'arco (círculo). Alt desliga momentaneamente, Shift tranca na ' +
        'horizontal ou vertical.',
      'Três formas de medir área: por pontos (polígono), "Escolher ' +
        'linhas" (à maneira do Pick Lines, encadeando contornos que já ' +
        'existem no desenho) e "Área do elemento" (a secção cortada de ' +
        'uma parede, pilar ou laje, já com as aberturas subtraídas). Em ' +
        'geometria curva os valores são exactos: um círculo mede π·r², ' +
        'não o polígono da tesselação.',
      'Exports à escala: PDF vectorial (A4 ou A3, retrato ou paisagem, ' +
        '1:50 a 1:500 ou ajustar à folha, com rodapé), DXF R12 em metros ' +
        'com uma camada por categoria IFC (para DWG basta gravar-como no ' +
        'CAD) e SVG. Anotações e cotas saem em camadas próprias ' +
        '(ANOTACOES e COTAS).',
      '"Destacar": o desenho abre numa janela própria do browser, para um ' +
        'segundo monitor, com o 3D na janela principal e a selecção ' +
        'sincronizada nos dois sentidos. "Trazer de volta" devolve-o ao ' +
        'sítio, tal como estava.',
      '"Ver no 3D" leva a câmara e a caixa de corte exactamente à fatia ' +
        'desenhada.',
    ],
  },
  {
    title: 'Pintura por critério',
    items: [
      'Painel "Colorir por…" (atalho C): pinta cada bucket distinto com a ' +
        'sua cor. 6 critérios: entidade IFC, PredefinedType, sistema/grupo, ' +
        'material, tipo (IfcRelDefinesByType), valor de propriedade.',
      'Pinturas manuais elemento-a-elemento via painel Aparência (A).',
      'Federação inteiramente suportada: a mesma classe recebe a mesma cor ' +
        'em todos os IFCs carregados.',
      'Motor próprio (BufferAttribute + DataTexture + shader patch) que faz ' +
        'bypass total ao Fragments setColor: sem leaks, sem limite de ' +
        'aplicações sucessivas.',
    ],
  },
  {
    title: 'Inspector e estrutura',
    items: [
      'Property panel com herança, atributos directos, Psets, Qsets, relações IFC, classificações, materiais.',
      'Spatial tree completa com pré-cómputo de ancestor paths.',
      'Info do modelo: schema, unidades, modelos federados, categorias, sistemas/grupos/assemblies.',
      'Parser IFC paralelo (web-ifc) opcional para inspecção fina das representations originais.',
      'Cobertura completa de relações inversas IFC: o parser paralelo ' +
        'varre IfcRel* (Contains, References, Aggregates, Nests, Declares, ' +
        'Connects, Covers, Voids, Fills, Projects, Interferes, Sequence, ' +
        'ServicesBuildings, SpaceBoundary*) e funde os inversos não ' +
        'populados pelo Fragments na secção "Relações inversas".',
      'Boundaries de IfcSpace: BoundedBy / ProvidesBoundaries com ' +
        'classificação PHYSICAL/VIRTUAL + INTERNAL/EXTERNAL, e área da ' +
        'superfície em m² quando a ConnectionGeometry o permite ' +
        '(IfcFaceSurface / IfcCurveBoundedPlane). Útil para envelope ' +
        'e análise energética.',
    ],
  },
  {
    title: 'Deteção de conflitos',
    items: [
      'Painel "Conflitos" (ribbon, separador Ficheiro, painel Comparação): ' +
        'defines dois conjuntos, A e B, por modelos e categorias IFC (com ' +
        'as contagens reais do ficheiro) ou pela selecção no 3D. Só se ' +
        'testam pares com um elemento de cada lado.',
      'Presets: Estrutura × MEP, Modelo A × Modelo B e Tudo × Tudo (este ' +
        'último avisa que dá muito ruído).',
      'Conflitos duros (elementos que se atravessam, por interseção de ' +
        'triângulos ou por contenção de um dentro do outro) e de folga ' +
        '(elementos a menos da distância que definires). Configuração em ' +
        'milímetros: tolerância de penetração, folga, ignorar contactos ' +
        'encostados, e testar ou não elementos do mesmo modelo entre si.',
      'A verificação corre num worker, sem bloquear a aplicação, com ' +
        'fases e progresso e com cancelamento que devolve resultados ' +
        'parciais. No fim: elementos, pares candidatos, pares testados, ' +
        'duros, folgas e tempo.',
      'Lista agrupável por gravidade, categorias ou modelos. Clicar num ' +
        'conflito leva a câmara ao sítio, selecciona os dois elementos e ' +
        'permite isolá-los.',
      'Triagem: cada conflito fica Novo, Aceite ou Resolvido, com nota. ' +
        'Os pareceres sobrevivem a novas corridas (a chave não depende da ' +
        'tolerância) e os conflitos que deixaram de existir ficam ' +
        'assinalados em vez de desaparecerem.',
      'Export para BCF: um tópico por conflito, com o ponto de vista já ' +
        'apontado ao problema e os dois elementos seleccionados. Com o ' +
        'módulo BCF activo cria um BCF novo; em modo local descarrega um ' +
        '.bcfzip.',
      'A profundidade de penetração é uma estimativa por sondagem e serve ' +
        'para ordenar por gravidade; a distância de folga é exacta.',
    ],
  },
  {
    title: 'Diff de versões',
    items: [
      'Comparar dois IFC carregados por GlobalId (botão "Comparar IFC" no ' +
        'separador Ficheiro): elementos novos, removidos, alterados e ' +
        'iguais, com contagem por classe IFC e clique para selecionar no 3D.',
      'Cores no 3D: removidos a vermelho (modelo A), novos a verde e ' +
        'alterados a amarelo (modelo B), com legenda e toggle "Ver no 3D".',
      'Compara atributos (Nome, Tag, ObjectType, PredefinedType), ' +
        'propriedades de Psets/Qsets, contentor espacial (piso) e posição ' +
        '(elementos movidos mais de 5 mm).',
      'Cabeçalho com os dois modelos e schema, e export do resultado para ' +
        'CSV (abre no Excel).',
    ],
  },
  {
    title: 'IDS: validar e criar',
    items: [
      'Validar o modelo contra um ficheiro IDS (buildingSMART ' +
        'Information Delivery Specification 1.0): botão "IDS" no separador ' +
        'Ficheiro. Abre o .ids, escolhe o modelo e valida, tudo no ' +
        'browser. Funciona também em modo local.',
      'Relatório por especificação: elementos aplicáveis, quantos passam ' +
        'e a lista de falhas com a razão de cada uma; clique numa falha ' +
        'seleciona o elemento no 3D. Cores no 3D (cumpre a verde, falha ' +
        'a vermelho), "Exportar CSV" e, nos workspaces com o módulo BCF ' +
        'ativo, "Exportar para BCF" (um tópico por especificação).',
      'Facets: entity (classe + PredefinedType), attribute, property ' +
        '(propertySet + baseName + valor + dataType), classification ' +
        '(sistema + código), material (por nome) e partOf (faz parte de ' +
        'um contentor da classe esperada). Restrições simpleValue, ' +
        'enumeração, padrão, limites e comprimento, e cardinalidade ' +
        'required / prohibited / optional.',
      'Conformidade medida, não estimada: 318 de 318 casos (100%) da ' +
        'suite oficial de testes IDS da buildingSMART, a correr nos ' +
        'testes automáticos do projecto.',
      '"Criar IDS" (separador do módulo IDS): editor de autoria com as ' +
        'seis facets em applicability e requirements, valores exactos, ' +
        'listas, padrões, intervalos e comprimentos; sugestões vindas do ' +
        'esquema IFC (903 classes, 760 property sets no IFC4X3) e do ' +
        'modelo carregado; avisos de esquema em tempo real; importar um ' +
        '.ids para editar, validar sem sair da página e descarregar o ' +
        '.ids conforme a norma.',
      '"Regras (extensão)": verificações condicionais SE/ENTÃO/SENÃO que ' +
        'o IDS 1.0 não cobre (grupos TODOS/ALGUM/NENHUM encaixáveis, ' +
        'acções de exigir ou de reportar), com falhas clicáveis e export ' +
        'CSV/BCF. Ficam em ficheiro próprio .idsx, marcado como extensão ' +
        'VN2R: não se disfarça de IDS, para não confundir quem o receber.',
      'O painel "Validar IDS" do ribbon está sempre disponível; o ' +
        'separador IDS da barra superior, com o editor e as regras, é ' +
        'opt-in por workspace.',
    ],
  },
  {
    title: 'Sessões e ficheiros',
    items: [
      'Guardar/Abrir sessão: container .vn2r-session (zip) com IFCs, ' +
        'layout, rota, BCFs, viewpoints, vistas 2D (com anotações e ' +
        'cotas), rascunhos de IDS e triagem de conflitos.',
      'O trabalho vive na sessão e só fica permanente quando gravas ' +
        '(Guardar sessão, Ctrl+Shift+S). Nada é reposto em silêncio ao ' +
        'voltar à aplicação.',
      'Rede de recuperação: se saíres com trabalho por gravar, a app ' +
        'avisa; ao voltar, pergunta se queres recuperar a sessão anterior, ' +
        'dizendo o que lá está, e podes descartá-la. Um ponto na barra ' +
        'assinala trabalho por gravar.',
      'As preferências (tema, tamanho do texto, barras, snap, modos dos ' +
        'painéis, vistas guardadas da Tabela) ficam sempre, mesmo que ' +
        'descartes o trabalho.',
      'Guardar, abrir e fechar sessão existem nos dois modos, local e em ' +
        'nuvem; um ficheiro gravado num modo abre no outro, e o que não ' +
        'fizer sentido no destino é ignorado sem estragar nada.',
      'Cache local de IFCs em IndexedDB: ao abrir um ficheiro de sessão, ' +
        'os IFCs já vistos voltam sozinhos, sem os ir buscar ao disco.',
      'Histórico hub-wide na topbar para retomar sessões recentes.',
      'Os bytes do IFC ficam sempre no computador do utilizador.',
      'Entrega assinada: termo de entrega em PDF com o SHA-256 de cada ' +
        'ficheiro, para assinar no Autenticação.gov (Cartão de Cidadão ou ' +
        'Chave Móvel Digital), e verificação de uma entrega assinada (PDF ' +
        'ou contentor ASiC) contra os ficheiros recebidos. Os ficheiros não ' +
        'são alterados.',
    ],
  },
  {
    title: 'BCF (módulo opcional)',
    module: 'bcf',
    items: [
      'Import de .bcfzip multi-versão com time-machine entre versões importadas.',
      'Edição local de tópicos: status, prioridade, tipo, labels, watchers, comentários.',
      'Catálogos de status / priority / topic-type / labels customizáveis por workspace.',
      'Captura de viewpoint a partir do viewer com 1 clique: cria tópico ou anexa.',
      'Export .bcfzip para troca com BIMcollab, Solibri, Trimble Connect, etc.',
    ],
  },
  {
    title: 'Tabela (módulo opcional)',
    module: 'info-table',
    items: [
      'Tabela de Informação: uma linha por elemento, sobre todos os IFCs ' +
        'carregados ao mesmo tempo e com o ficheiro de origem em cada ' +
        'linha. Corre inteiramente no browser.',
      'Colunas configuráveis: atributos, property sets e quantity sets. ' +
        '"Descobrir tudo" varre todos os elementos das classes escolhidas ' +
        'para encontrar as colunas raramente preenchidas.',
      'Filtro de texto em qualquer coluna, ordenação por coluna e ' +
        'agrupamento (por IFC, por classe ou por qualquer coluna), com os ' +
        'grupos clicáveis para seleccionar no viewer.',
      'Vistas guardadas no browser (classes, colunas, ordenação, grupos e ' +
        'filtro) e export CSV.',
      'Esconder (H), Isolar (I) e Mostrar tudo (Alt+H) a partir da ' +
        'selecção feita na tabela.',
    ],
  },
  {
    title: 'BoQ (módulo opcional)',
    module: 'boq',
    items: [
      'Cálculo de quantidades 100% client-side via web-ifc (sem upload do IFC).',
      'Filtros por classe IFC, decomposição da árvore espacial.',
      'Aggregate fallback para entidades sem Qset directo.',
      'Export CSV / Excel; write-back de quantidades para Qsets via pipeline IFCEdit.',
    ],
  },
  {
    title: 'IFCEdit (módulo opcional)',
    module: 'ifc-edit',
    items: [
      'Edição inline no painel de propriedades para Name, Description, ' +
        'ObjectType, Tag, LongName, PredefinedType, Classe IFC e ' +
        'valores de Psets / Qsets (IfcPropertySingleValue + IfcQuantity*).',
      'PredefinedType com dropdown schema-aware: lista as opções legais ' +
        'do esquema do modelo (IFC2X3 / IFC4 / IFC4X3) para a classe do ' +
        'elemento, extraídas do schema do web-ifc.',
      'Classe IFC com dropdown das subclasses de IfcBuildingElement ' +
        'compatíveis (Wall, Slab, Beam e afins). Mudança de classe faz ' +
        'auto-fallback do PredefinedType para NOTDEFINED quando o valor ' +
        'existente não pertence ao enum da nova classe.',
      'Edição de valores em Psets / Qsets: navegação ' +
        'IfcRelDefinesByProperties > IfcPropertySet > IfcPropertySingleValue ' +
        '(ou IfcElementQuantity > IfcQuantity*). O type wrapper original ' +
        '(IFCREAL, IFCLENGTHMEASURE, IFCTEXT, IFCBOOLEAN, ...) é ' +
        'preservado. Adicionar novos psets / propriedades fica para ' +
        'sub-sprint seguinte.',
      'Export client-side: aplica todas as edições pendentes aos bytes ' +
        'do IFC carregado e descarrega um ficheiro `_edit.ifc` no disco do ' +
        'utilizador. O original mantém-se intacto.',
      'Safety check anti-corrupção: o patcher STEP recusa qualquer edição ' +
        'cujo slot existente não tenha o shape esperado, qualquer ' +
        'mudança de classe fora da compatibility set, e valores de pset ' +
        'cuja conversão para o tipo do wrapper original falhe.',
      'Edição de materiais / classificações / geometria: planeada para ' +
        'sub-sprints futuros.',
    ],
  },
  {
    title: 'Plataforma e administração',
    items: [
      'Auth cloud por magic link + password opcional, sessões httpOnly+SameSite=Lax.',
      'Acesso por workspace; módulos opt-in por workspace.',
      'Painel admin VN2R: organizações, workspaces, membros, módulos, archive.',
      'Audit log de eventos: login, password, imports, alterações de membros, módulos.',
      'Modo local-first: a app funciona sem rede, com IFCs do disco do utilizador.',
      'App instalável (PWA) no telemóvel ou no computador; a interface ' +
        'abre offline com a última versão vista (os IFC não ficam ' +
        'guardados offline).',
      'Ligações partilháveis: ?sample= abre o viewer já com um exemplo, ' +
        '?ifc= carrega um IFC remoto (CORS permitindo) e ?ids= abre a ' +
        'validação já com o ficheiro de regras pronto.',
      'Créditos de terceiros na tab "Créditos" desta popover, com as ' +
        'licenças verificadas nos próprios pacotes.',
    ],
  },
  {
    title: 'Extensões',
    items: [
      'Revisão de desenhos PDF dentro da plataforma: anotações a ' +
        'vermelho, medições calibradas, pins de problema, pesquisa no ' +
        'texto do desenho, comparação entre revisões e intercâmbio ' +
        'BCF/XFDF. Abre por cima do 3D ou numa janela do browser à ' +
        'parte, para ter o desenho num monitor e o modelo no outro.',
      'Guardar o modelo activo em Fragments (.frag) e reabri-lo. ' +
        'Reabrir o mesmo IFC reutiliza a derivação já feita e passa de ' +
        'segundos para menos de um.',
      'Histórico de versões: guardar marcos do modelo e reabri-los ao ' +
        'lado do actual para comparar. Cada marco guarda o IFC.',
      'Pins dos tópicos BCF sobre o modelo, com interruptor e filtros ' +
        'por estado e por ficheiro (Vista > Vistas).',
      'Nuvens de pontos na cena, com mapa de desvio aproximado ao IFC. ' +
        'Lê LAS e LAZ (até à versão 1.3 do formato), PLY, XYZ e PTS.',
      'Grafo local do modelo, com procura por classe, nome, propriedade ' +
        'ou GlobalId, e uma contagem de quanta informação está ' +
        'declarada por elemento.',
      'Verificação de linha de visão entre dois elementos, no painel de ' +
        'conflitos.',
      'Snapshot MCP: exportar uma fotografia do modelo, só de leitura, ' +
        'para o Claude Code ou outro cliente MCP.',
      'Transparência que conserva os materiais de cada elemento, e zoom ' +
        'com inércia e pivô debaixo do cursor.',
    ],
  },
  {
    title: 'Assistente',
    module: 'assistant',
    items: [
      'Perguntas em linguagem natural sobre o modelo aberto. Sete ' +
        'consultas: resumo, georreferenciação, procura de elementos ' +
        '(por classe, piso ou propriedade), propriedades de um elemento ' +
        'pelo GlobalId, soma de quantidades, selecção no 3D e ' +
        'agrupamento por tipo.',
      'Cada resposta traz "Como cheguei aqui": a lista das consultas ' +
        'que correu e o que cada uma devolveu, para se poder confrontar ' +
        'o que diz com o que fez.',
      'As consultas correm no browser, sobre o modelo já carregado. O ' +
        'ficheiro IFC não sai da máquina: só viajam a pergunta e o ' +
        'resultado da consulta.',
      'Responde a partir do resultado das consultas e diz que não sabe ' +
        'quando não há consulta que responda. Não emite juízos sobre o ' +
        'modelo nem sobre decisões de engenharia.',
      'Funciona com Anthropic ou com qualquer serviço que fale o formato ' +
        'da OpenAI (Azure, Groq, OpenRouter, Mistral, DeepSeek), ' +
        'incluindo um modelo local via Ollama ou LM Studio.',
      'Cada utilizador pode trazer a sua própria chave de API, guardada ' +
        'apenas no seu browser e nunca gravada na plataforma.',
    ],
  },
]

export type LimitationSeverity = 'info' | 'warn'

export interface KnownLimitation {
  title: string
  detail: string
  workaround?: string
  severity: LimitationSeverity
}

export const LIMITATIONS: KnownLimitation[] = [
  {
    title: 'Um Fragments (.frag) aberto directamente não traz o IFC',
    severity: 'warn',
    detail:
      'Um ficheiro .frag é geometria já derivada: vê-se, mede-se e ' +
      'navega-se, mas não tem o IFC por trás. Tudo o que lê o IFC fica ' +
      'sem chão: o IDS, o BoQ, a Tabela, o IFCEdit, a escrita de ' +
      'georreferenciação e o assistente. Nenhum deles inventa uma ' +
      'resposta; todos dizem que falta o IFC. Na lista de modelos, um ' +
      'modelo assim aparece marcado com "sem IFC".',
    workaround:
      'Abrir o .ifc original. Guardar em .frag continua a servir para ' +
      'trocar geometria depressa e para reabrir o mesmo modelo sem ' +
      'esperar pela conversão.',
  },
  {
    title: 'Pins BCF não são testados contra a geometria',
    severity: 'info',
    detail:
      'Um pin é projectado a partir do seu ponto no modelo, sem se ' +
      'verificar se há alguma coisa à frente. Um tópico dentro de uma ' +
      'sala fechada mostra o pin na mesma, visto de fora. Testar ' +
      'oclusão obrigava a lançar um raio por pin a cada movimento da ' +
      'câmara, e isso paga-se em fluidez.',
    workaround:
      'Os filtros do painel Pins BCF (Vista > Vistas) reduzem o que está ' +
      'no ecrã ao que interessa, por estado ou por ficheiro.',
  },
  {
    title: 'A revisão de PDF tem a interface em inglês',
    severity: 'info',
    detail:
      'O motor de revisão de desenhos é o Massing PDF, que não expõe ' +
      'tradução. A barra de ferramentas, os nomes das ferramentas e as ' +
      'mensagens dele estão em inglês, ao contrário do resto da ' +
      'plataforma. Traduzir obrigava a bifurcar o projecto de origem.',
  },
  {
    title: 'Acompanhar numa sessão: não é edição em conjunto',
    severity: 'info',
    detail:
      'Acompanhar alguém numa sessão partilhada traz a câmara e a ' +
      'selecção dessa pessoa. Não traz cortes, medições nem anotações, ' +
      'e duas pessoas não editam o mesmo modelo ao mesmo tempo.',
    workaround:
      'Para trabalho em conjunto sobre alterações, usar o BCF da ' +
      'sessão: comentários, estados e atribuições sincronizam ao vivo.',
  },
  {
    title: 'Lentes: só classe, nome e modelo',
    severity: 'info',
    detail:
      'As regras das lentes avaliam a classe IFC, o nome e o modelo de ' +
      'origem. Não avaliam property sets, quantidades nem ' +
      'classificações: isso obrigaria a ler os psets de todos os ' +
      'elementos, que é caro num modelo grande. O "Apagar" pinta de ' +
      'cinzento, não altera a transparência real.',
    workaround:
      'Para regras sobre propriedades, o Filtro aceita ' +
      'Pset.Propriedade com operadores numéricos.',
  },
  {
    title: 'Nuvens de pontos: LAS até à 1.3, E57 até aos 80 MB',
    severity: 'info',
    detail:
      'Abrimos LAS, LAZ, E57, PLY, XYZ e PTS. O leitor de LAS e LAZ vai ' +
      'até à versão 1.3 do formato: um LAS 1.4, que é a versão ' +
      'corrente, é recusado com uma mensagem que o diz. O E57 é lido ' +
      'com cor, mas só até aos 80 MB, porque a conversão tem de ter a ' +
      'nuvem inteira em memória de uma vez. Nenhum dos leitores faz ' +
      'nível de detalhe: uma nuvem muito densa desenha todos os pontos ' +
      'e pesa.',
    workaround:
      'Um LAS 1.4 exporta-se outra vez em 1.2. Um E57 grande divide-se ' +
      'por scans, ou converte-se para LAZ, que é comprimido e não tem ' +
      'este limite. O CloudCompare lê e escreve todos estes formatos.',
  },
  {
    title: 'O desvio às nuvens de pontos é aproximado',
    severity: 'info',
    detail:
      'A distância é medida às caixas envolventes dos elementos e não à ' +
      'superfície deles, sobre uma grelha espacial e com amostragem até ' +
      '200 mil pontos. Serve para ver onde há divergência; não é uma ' +
      'inspecção metrológica malha-contra-nuvem. Assume coordenadas e ' +
      'tolerância em metros.',
  },
  {
    title: 'O índice de documentação não prova circularidade',
    severity: 'info',
    detail:
      'Conta campos declarados no IFC (identificador, nome, tipo, ' +
      'material, e propriedades cujo nome fala de estado, reutilização ' +
      'ou vida útil) sobre as entidades com identidade própria. É uma ' +
      'medida de completude de informação, não uma avaliação de ' +
      'circularidade nem de sustentabilidade do edifício, e não segue ' +
      'nenhuma norma. Tipo e material só se leem no IFC: num modelo sem ' +
      'IFC essas colunas aparecem como não observadas, e não como zero.',
  },
  {
    title: 'O snapshot MCP é uma fotografia, e leva propriedades',
    severity: 'warn',
    detail:
      'O ficheiro exportado é o modelo no momento em que se exporta: ' +
      'fica desactualizado assim que o modelo muda, e é preciso voltar ' +
      'a exportar. Contém as propriedades do modelo em texto legível, ' +
      'por isso deve ser tratado como o IFC de origem e não deixado em ' +
      'sítio partilhado.',
    workaround:
      'Para leitura sempre actualizada dos ficheiros em disco existe o ' +
      'servidor MCP que lê os IFC directamente, que é outro e ' +
      'complementar deste.',
  },
  {
    title: 'Entrega assinada: o certificado valida-se fora da plataforma',
    severity: 'info',
    detail:
      'A verificação confere a integridade (o resumo guardado na assinatura ' +
      'bate com o conteúdo assinado) e a correspondência dos ficheiros. Não ' +
      'verifica a assinatura criptográfica nem a cadeia de confiança do ' +
      'certificado (se é qualificado, se estava válido e se não foi ' +
      'revogado). Um PDF assinado que não foi gerado pela plataforma lê-se ' +
      '(assinante, data, integridade), mas sem lista de ficheiros para ' +
      'comparar.',
    workaround:
      'Validar o termo assinado na aplicação Autenticação.gov, em ' +
      'validador.autenticacao.gov.pt ou no validador da Comissão Europeia ' +
      '(DSS).',
  },
  {
    title: 'Linhas de modelo e desenho 2D autoral não vêm no IFC',
    severity: 'info',
    detail:
      'As linhas de modelo do Revit e o desenho 2D autoral (folhas, ' +
      'anotações e detalhes feitos na ferramenta de origem) não chegam ao ' +
      'viewer com geometria que se possa desenhar. As Vistas 2D desta app ' +
      'não dependem disso: as plantas e os cortes são gerados aqui, ' +
      'cortando a geometria 3D do próprio IFC.',
    workaround:
      'Usa o modo 2D (comutador "3D | 2D" ou o botão "Vistas 2D") para ' +
      'gerar as plantas e os cortes de que precisas, e exporta em PDF, ' +
      'DXF ou SVG.',
  },
  {
    title: 'Vistas 2D: o que o desenho não faz',
    severity: 'info',
    detail:
      'Por omissão o desenho mostra TUDO o que está dentro da ' +
      'profundidade da vista, incluindo o que está tapado por outro ' +
      'elemento: é por isso que um corte profundo sai carregado. Liga ' +
      '"Linhas ocultas" na barra do modo 2D para esconder o que fica por ' +
      'trás; custa uma passagem extra sobre a geometria (o tempo aparece ' +
      'no rodapé) e a fronteira entre visível e tapado é aproximada, ' +
      'porque o teste é feito sobre uma grelha de profundidade e não por ' +
      'recorte exacto. ' +
      'Num corte, uma superfície inclinada dá área projectada no plano do ' +
      'desenho, não a área real. O DXF sai em R12, com os contornos do ' +
      'poché fechados e prontos a tramar no CAD, porque o formato não tem ' +
      'tramas nativas; não exportamos DWG nem imagem raster. Raios muito ' +
      'pequenos (fillets com 2 a 4 vértices no ficheiro) mantêm a ' +
      'tesselação de origem: não se inventa geometria sem evidência.',
  },
  {
    title: 'Deteção de conflitos: tectos de segurança',
    severity: 'info',
    detail:
      'Para a verificação não pendurar a aplicação há limites: pára nos ' +
      '200 mil pares candidatos (e diz que a lista ficou incompleta) e ' +
      'salta elementos com mais de 20 mil triângulos (e diz quantos ' +
      'saltou). A profundidade de penetração é uma estimativa por ' +
      'sondagem, boa para ordenar por gravidade mas não para medir; a ' +
      'distância de folga é exacta. Os conjuntos definem-se por modelo, ' +
      'categoria IFC ou selecção, não por piso.',
    workaround:
      'Estreitar os conjuntos (menos categorias, ou modelo A contra ' +
      'modelo B) em vez de correr Tudo × Tudo.',
  },
  {
    title: 'IFCEdit cobre só um subconjunto dos atributos',
    severity: 'info',
    detail:
      'O módulo IFCEdit, quando activo, permite editar Name, Description, ' +
      'ObjectType, Tag, LongName, PredefinedType, Classe IFC e valores ' +
      'em Psets / Qsets inline no painel de propriedades. Mudança de ' +
      'classe está restrita a subclasses de IfcBuildingElement com ' +
      'layout STEP idêntico: Doors, Windows, Spaces e elementos MEP ' +
      'não são alterados. Edição de Psets só cobre IfcPropertySingleValue ' +
      'com type wrappers conhecidos (strings, numerics, booleans); ' +
      'propriedades enumeradas, bounded, list, table, reference, e ' +
      'IfcComplexProperty nested permanecem read-only. Adicionar novos ' +
      'psets / propriedades, materiais, classificações e edição de ' +
      'geometria estão planeados para sub-sprints futuros.',
  },
  {
    title: 'BoQ pode ser lento em modelos muito grandes',
    severity: 'info',
    detail:
      'O cálculo de quantidades corre client-side (web-ifc no browser). ' +
      'Em modelos > 500 MB pode demorar vários minutos. A alternativa ' +
      'server-side foi descontinuada por consumir memória excessiva.',
  },
  {
    title: 'Items com geometria batchada com IfcSpace perdem arestas',
    severity: 'info',
    detail:
      'Para evitar caixas wireframe a flutuar (outlines de IfcSpace / ' +
      'storey), saltamos a geração de arestas em qualquer mesh que ' +
      'contenha pelo menos um item de contentor espacial. Se um duct ' +
      'ou parede estiver batchado pelo Fragments no mesmo tile que ' +
      'uma space, perde o contorno; a superfície sombreada continua ' +
      'visível. Caso raro em prática.',
  },
  {
    title: 'Cobertura de Fragments vs IFC original',
    severity: 'info',
    detail:
      'A conversão IFC para Fragments preserva atributos, property sets, ' +
      'relações, types, GUIDs, posições e bounding boxes. Não preserva ' +
      'as IfcShapeRepresentation originais (SweptSolid, BREP, ' +
      'Tessellation, MappedRepresentation): a geometria é re-empacotada ' +
      'em tiles optimizados. Curvas (pipes, columns) são tesseladas com ' +
      '32 segmentos por circunferência. Para inspecção ao nível das ' +
      'representations originais, usar o Parser IFC paralelo (Diagnóstico).',
  },
]

export interface HelpEntry {
  title: string
  body: string
}

export const HELP: HelpEntry[] = [
  {
    title: 'Como começar',
    body:
      '1. Recebes um magic link por email da equipa VN2R. Clica para entrar ' +
      'no teu workspace.\n' +
      '2. Define password (opcional, mas recomendado) em "email > password" ' +
      'na topbar.\n' +
      '3. Clica em "Abrir modelo" e escolhe um ficheiro do teu disco, ou ' +
      'arrasta o ficheiro IFC para o viewer. O viewer carrega-o localmente. ' +
      'Com um modelo já aberto, o botão passa a "Substituir"; para federar ' +
      'mais IFCs usa "Adicionar".\n' +
      '4. Para guardar o estado de trabalho (modelos + layout + BCFs + ' +
      'viewpoints + vistas 2D), usa "Guardar sessão": gera um ficheiro ' +
      '.vn2r-session que reabre tudo quando voltares.',
  },
  {
    title: 'Gravar a sessão (e o que acontece se não gravares)',
    body:
      'O trabalho funciona como num programa de desktop: vive na sessão e ' +
      'só fica permanente quando gravas. "Guardar sessão" (Ctrl+Shift+S, ' +
      'ou menu Sessão na topbar) escreve um ficheiro .vn2r-session com os ' +
      'IFCs, o layout, os BCFs, os viewpoints, as vistas 2D com anotações ' +
      'e cotas, os rascunhos de IDS e a triagem dos conflitos.\n\n' +
      'Enquanto houver trabalho por gravar, um ponto na barra assinala-o e ' +
      'a app avisa se tentares sair. Se o browser fechar ou algo correr ' +
      'mal, na visita seguinte a app PERGUNTA se queres recuperar a sessão ' +
      'anterior, dizendo o que lá está (quantos IFCs, BCFs, vistas 2D, ' +
      'conflitos triados). Podes recuperar ou descartar. Nada volta em ' +
      'silêncio.\n\n' +
      'As preferências (tema, tamanho do texto, larguras das barras, snap, ' +
      'modo de cada painel, vistas guardadas da Tabela) não são trabalho: ' +
      'ficam sempre, mesmo que descartes a recuperação.',
  },
  {
    title: 'Modo local vs cloud',
    body:
      'Modo cloud (típico): entras com magic link, módulos contratados ' +
      'aparecem nos tabs da topbar.\n\n' +
      'Modo local: se a app não conseguir falar com o servidor, entra em ' +
      'modo offline single-user. O viewer continua a funcionar (incluindo ' +
      'sessões, vistas 2D, conflitos e validação IDS); os módulos que ' +
      'dependem do workspace não aparecem.\n\n' +
      'Em qualquer modo, os bytes do IFC vivem no teu computador. A cloud ' +
      'controla quem pode usar o programa, não armazena os teus modelos.',
  },
  {
    title: 'Atalhos úteis',
    body:
      'Ctrl+Shift+S: Guardar sessão | Ctrl+Shift+O: Abrir sessão\n' +
      'F: Focar selecção | P: Pivot aqui | Tab / Shift+Tab: Ciclar hits\n' +
      'H: Esconder | I: Isolar | Shift+I: Inverter | Alt+H: Mostrar tudo\n' +
      'G: Ghost | R: Grelha | E: Arestas | Q: Esconder spaces | S: Section\n' +
      'A: Aparência | C: Colorir por… | Shift+F: Filtro multi-critério\n' +
      'Shift+C: Selecionar todos da classe | Shift+T: todos do tipo\n' +
      'Shift+S: todos no piso | Shift+B: todos no edifício\n' +
      'Ver o painel "Atalhos de teclado" (tecla ?) para a lista completa.',
  },
  {
    title: 'Criar plantas e cortes (Vistas 2D)',
    body:
      'Entra no modo 2D pelo comutador "3D | 2D" junto à área do modelo, ' +
      'pelo botão "Vistas 2D" (ribbon, separador Vista) ou pelo botão "2D" ' +
      'no telemóvel. O 3D fica como está: podes voltar quando quiseres.\n\n' +
      '1. Planta: na coluna da esquerda, "Planta por piso" gera a planta ' +
      'de um IfcBuildingStorey com um clique (corte 1,20 m acima da cota ' +
      'do piso, profundidade 4 m). Se o ficheiro não tiver pisos com cota ' +
      'legível, usa "Planta livre" e escreve a cota e a profundidade.\n' +
      '2. Corte: "Da caixa de corte" usa a face da section box virada ' +
      'para a câmara; "Criar corte por azimute" pede direção e ponto; ou ' +
      'clica em "Desenhar corte" e marca o traço na planta com dois ' +
      'cliques (olha para a esquerda do sentido do traço, e "Inverter ' +
      'direção" troca). Os nomes saem automáticos: Corte A-A, B-B, ...\n' +
      '3. Ajusta com "Corte (m)" e "Prof. (m)" e carrega em "Regenerar". ' +
      '"Camadas" muda cores por categoria; "Preencher cortes" liga o ' +
      'poché.\n' +
      '4. Medir: "Por pontos", "Escolher linhas" (encadeia contornos que ' +
      'já existem no desenho) ou "Área do elemento" (mede a secção de uma ' +
      'parede, pilar ou laje, já sem as aberturas). "Gravar cota" deixa a ' +
      'medição desenhada na vista.\n' +
      '5. "Gravar vista" guarda a vista, e "PDF", "DXF" ou "SVG" exportam ' +
      'na escala e papel escolhidos. As vistas gravadas só ficam ' +
      'permanentes com "Guardar sessão".\n\n' +
      'O botão "Snap" faz o cursor agarrar a vértices, cruzamentos, ' +
      'pontos médios e centros (Alt desliga enquanto o carregas, Shift ' +
      'tranca na horizontal ou vertical). "Destacar" abre o desenho numa ' +
      'janela própria, para arrastares para um segundo ecrã.',
  },
  {
    title: 'Detetar conflitos entre modelos',
    body:
      '1. Carrega os modelos que queres cruzar (usa "Adicionar" para ' +
      'federar o segundo).\n' +
      '2. No separador Ficheiro do ribbon, clica em "Conflitos".\n' +
      '3. Escolhe um preset (Estrutura × MEP, Modelo A × Modelo B) ou ' +
      'define à mão o Conjunto A e o Conjunto B, por modelos e categorias ' +
      'IFC ou a partir da selecção no 3D. Só se testam pares com um ' +
      'elemento de cada lado.\n' +
      '4. Em "Configuração" defines, em milímetros, a tolerância de ' +
      'penetração (para ignorar contactos de construção) e a folga (0 ' +
      'desliga; acima de 0 detecta elementos demasiado próximos).\n' +
      '5. "Detetar conflitos". A verificação corre em segundo plano com ' +
      'progresso e pode ser cancelada, ficando com os resultados ' +
      'parciais.\n\n' +
      'Clicar num conflito leva a câmara ao sítio e selecciona os dois ' +
      'elementos. Cada um pode ficar Novo, Aceite ou Resolvido, com nota; ' +
      'os pareceres mantêm-se quando voltares a correr. "Criar BCF" (ou ' +
      '"Exportar .bcfzip" em modo local) gera um tópico por conflito, com ' +
      'o ponto de vista já apontado ao problema.',
  },
  {
    title: 'Comparar duas versões de um IFC',
    body:
      '1. Carrega as duas versões (usa "Abrir modelo" para a primeira e ' +
      '"Adicionar" para a segunda, para teres as duas no viewport).\n' +
      '2. No separador Ficheiro do ribbon, clica em "Comparar IFC".\n' +
      '3. Escolhe o modelo A (origem) e o B (destino) e clica em Comparar.\n' +
      '4. O 3D fica colorido: vermelho = removido (só em A), verde = novo ' +
      '(só em B), amarelo = alterado. A lista mostra os elementos por ' +
      'categoria; clica numa linha para a ver no 3D.\n' +
      'Compara atributos, propriedades de Psets/Qsets, mudança de piso e ' +
      'posição (elementos movidos). "Exportar CSV" guarda o relatório.',
  },
  {
    title: 'Validar um modelo contra um IDS',
    body:
      'O IDS (buildingSMART Information Delivery Specification) é um ' +
      'ficheiro .ids que descreve requisitos de informação: que ' +
      'elementos devem existir e que atributos ou propriedades têm de ' +
      'ter (ex.: "todas as paredes têm de ter Pset_WallCommon.FireRating").\n\n' +
      '1. Carrega o modelo IFC.\n' +
      '2. No separador Ficheiro do ribbon, clica em "IDS".\n' +
      '3. Clica em "Abrir ficheiro .ids" e escolhe o teu IDS.\n' +
      '4. Confirma o modelo e clica em Validar.\n' +
      'O relatório mostra, por especificação, quantos elementos se ' +
      'aplicam e quantos passam, com a lista de falhas; clica numa falha ' +
      'para a ver no 3D. Os elementos ficam coloridos (verde = cumpre, ' +
      'vermelho = falha) e "Exportar CSV" guarda o relatório. Com o ' +
      'módulo BCF activo, "Exportar para BCF" cria um tópico por ' +
      'especificação com falhas.\n\n' +
      'Avaliam-se os requisitos de entidade, atributo, propriedade ' +
      '(incluindo tipo de dados), classificação, material e "faz parte ' +
      'de" (partOf). O motor passa os 318 casos da suite oficial de ' +
      'testes da buildingSMART. Tudo corre no browser: o IFC e o IDS não ' +
      'são enviados para lado nenhum.',
  },
  {
    title: 'Criar um IDS (e regras condicionais)',
    body:
      'Nos workspaces com o módulo IDS activo, o separador IDS da barra ' +
      'superior tem, além de "Validar", mais dois:\n\n' +
      '"Criar IDS": monta especificações com as seis facets (classe IFC, ' +
      'atributo, propriedade, classificação, material e "faz parte de"), ' +
      'tanto na applicability como nos requisitos, com valores exactos, ' +
      'listas, padrões, intervalos e comprimentos. Os campos sugerem o ' +
      'que existe no esquema IFC e, se tiveres um modelo aberto, o que ' +
      'existe mesmo nele. Avisos em tempo real apanham IDS inválidos à ' +
      'nascença (atributo que a classe não tem, decimal num campo ' +
      'inteiro). "Validar agora" corre contra o modelo sem sair da ' +
      'página, e "Descarregar .ids" escreve o ficheiro conforme a norma. ' +
      'Também podes importar um .ids existente para o editar.\n\n' +
      '"Regras (extensão)": condições SE/ENTÃO/SENÃO para o que o IDS 1.0 ' +
      'não consegue exprimir (grupos TODOS/ALGUM/NENHUM encaixáveis, ' +
      'acções de exigir ou de reportar valores encontrados). Gravam-se em ' +
      '.idsx, um formato nosso, assumidamente marcado como extensão VN2R ' +
      'para não se confundir com IDS standard.\n\n' +
      'Os rascunhos são trabalho: só ficam permanentes com "Guardar ' +
      'sessão".',
  },
  {
    title: 'Telemóvel e modelo de exemplo',
    body:
      'No telemóvel a interface adapta-se: o ribbon dá lugar a uma barra ' +
      'de ação em baixo (Modelos, Abrir, Exemplo, Focar, Medir, 2D, ' +
      'Props) e os painéis abrem como drawers. Um dedo orbita, dois ' +
      'dedos fazem pan e zoom, e um toque simples seleciona. No modo 2D, ' +
      'um dedo arrasta o desenho e dois dedos fazem zoom.\n\n' +
      'Sem um IFC à mão? Clica em "Exemplo" (no ribbon, na barra de baixo ' +
      'ou no ecrã de boas-vindas) para descarregar um modelo público e ' +
      'experimentar o viewer.',
  },
  {
    title: 'Georreferenciação (colocar no mapa)',
    body:
      'O viewer lê automaticamente a georreferenciação que já vem no IFC: ' +
      'o IfcMapConversion (posiciona o modelo) e o IfcProjectedCRS (o ' +
      'sistema de coordenadas / EPSG, visível no editor de posição e na ' +
      'secção CRS do Info do modelo). No sidebar, o botão de posição por ' +
      'modelo mostra CRS / Eastings / Northings / Altura / Heading / ' +
      'Escala e deixa-te reposicionar a origem à mão. O toggle "Origem" ' +
      '(Vista > Vistas) marca no 3D, com tripés de eixos E/N/h, a origem ' +
      'verdadeira do ficheiro de cada modelo e as origens de Site / ' +
      'Building / Storey / Space lidas do STEP (usa o filtro ao lado para ' +
      'escolher categorias e etiquetas). O painel "Mapa" (Vista > Mapa) ' +
      'mostra o modelo num OpenStreetMap, deixa reposicionar a origem ' +
      'com um clique, e "Ver no 3D" desenha o chão OSM por baixo do ' +
      'modelo no viewport, plano ou com relevo real (checkbox "Relevo").\n\n' +
      'Se o IFC NÃO traz georreferenciação (ou vem errada), usa o ' +
      'ifc-georeferencer, a nossa instância da ferramenta open source do ' +
      'buildingSMART NL (link no editor de posição do sidebar): coloca o ' +
      'modelo num mapa real (OpenStreetMap), escreve o IfcMapConversion + ' +
      'IfcProjectedCRS de volta no ficheiro, e recarrega o ficheiro aqui. ' +
      'Tudo corre no browser, o IFC não é enviado para lado nenhum.',
  },
  {
    title: 'Assistente: perguntar ao modelo em vez de o procurar',
    body:
      'O separador Assistente responde a perguntas sobre o modelo que ' +
      'tens aberto: "está georreferenciado e em que sistema de ' +
      'coordenadas", "que pisos tem e a que cotas", "quantas portas ' +
      'existem", "mostra-me as propriedades deste elemento".\n\n' +
      'As respostas vêm sempre de consultas ao modelo, feitas AQUI no ' +
      'teu browser. O ficheiro IFC não é enviado para lado nenhum: só ' +
      'viajam a pergunta e o resultado da consulta. Quando não há ' +
      'consulta que responda, o assistente diz que não sabe em vez de ' +
      'opinar. Não te vai dizer se uma parede está bem dimensionada nem ' +
      'se o projeto cumpre um regulamento, e isso é de propósito.\n\n' +
      'CHAVE DE API. O assistente precisa de um modelo de linguagem, e ' +
      'isso tem custo. Ou a instalação tem chave própria configurada, e ' +
      'nesse caso funciona sem fazeres nada, ou trazes a tua: no painel ' +
      'do Assistente, em "Adicionar a minha chave de API". A tua chave ' +
      'fica guardada NESTE browser e em mais lado nenhum, é enviada em ' +
      'cada pergunta para ser usada, e nunca é gravada na plataforma. ' +
      'Quem usa chave própria paga o seu consumo e não está sujeito ao ' +
      'limite diário.',
  },
  {
    title: 'Usar um modelo de IA local (sem custo por pergunta)',
    body:
      'O assistente não está preso a um fornecedor. Fala o formato da ' +
      'Anthropic e o da OpenAI, e este último é o mesmo que a Azure, a ' +
      'Groq, o OpenRouter, a Mistral e a DeepSeek usam. É também o que o ' +
      'Ollama e o LM Studio servem, o que permite correr um modelo na ' +
      'tua própria máquina, sem custo por pergunta e sem nada sair da ' +
      'rede local.\n\n' +
      'COMO SE CONFIGURA, passo a passo:\n' +
      '1. Instala o Ollama (ollama.com) e descarrega um modelo, por ' +
      'exemplo com "ollama pull qwen2.5:7b".\n' +
      '2. Autoriza esta origem no Ollama: define a variável de ambiente ' +
      'OLLAMA_ORIGINS com https://openbim.vn2r.pt e reinicia-o. Sem ' +
      'isto ele recusa os pedidos vindos do browser.\n' +
      '3. No painel do Assistente, clica em "Alterar", escolhe "Modelo ' +
      'na minha máquina" e escreve o nome do modelo. O endereço já vem ' +
      'preenchido com o do Ollama.\n\n' +
      'A partir daí o teu browser fala DIRECTAMENTE com o teu ' +
      'computador: a pergunta não passa pelo servidor da plataforma e ' +
      'não custa nada. A escolha é tua e fica nesse browser, portanto ' +
      'noutra máquina configuras outra vez, ou usas um serviço online. ' +
      'Dois utilizadores da mesma instalação podem estar a usar modelos ' +
      'diferentes ao mesmo tempo.\n\n' +
      'QUE MODELO ESCOLHER. O que aqui pesa não é o modelo saber de ' +
      'construção, porque não precisa: quem lê o IFC são as consultas. ' +
      'O que ele tem de fazer bem é escolher a consulta certa e ' +
      'preencher-lhe os argumentos sem enganos. Nisso, os modelos da ' +
      'família Qwen (2.5 ou 3) na variante instruct são os mais ' +
      'consistentes do que se consegue correr num portátil; o Llama 3.1 ' +
      'ou 3.3 de 8B e o Mistral Small também servem. Como regra: 14B se ' +
      'a máquina der (perto de 10 GB livres), 8B como mínimo prático ' +
      '(perto de 6 GB). Abaixo de 7B a escolha de ferramentas começa a ' +
      'falhar com frequência.\n\n' +
      'O QUE ESPERAR. Nos testes que fizemos com o Qwen 2.5 de 7B, o ' +
      'modelo escolheu a consulta certa, respondeu só a partir do que ela ' +
      'devolveu, e recusou-se a opinar quando a pergunta não tinha ' +
      'consulta que a respondesse. O trabalho pesado é das consultas, ' +
      'que são código e não mudam com o modelo; ao modelo só se pede que ' +
      'escolha entre poucas opções e escreva a frase. É por isso que um ' +
      'modelo pequeno se aguenta aqui.\n\n' +
      'Onde vais notar a diferença para um serviço online: conversas ' +
      'longas, perguntas ambíguas, e a qualidade do português em ' +
      'respostas compridas. Trocar entre um e outro é escrever outro ' +
      'nome no campo, portanto compara na mesma pergunta quando tiveres ' +
      'dúvidas.',
  },
  {
    title: 'Rever desenhos PDF',
    body:
      'O botão Rever PDF (separador Ficheiro, painel Ferramentas) abre ' +
      'um ambiente de revisão de desenhos por cima do 3D. Abre o PDF, e ' +
      'tens anotações a vermelho, medições, pins de problema, pesquisa ' +
      'no texto do desenho, comparação entre revisões e troca por BCF ' +
      'ou XFDF.\n\n' +
      'MEDIR. As medições só valem depois de calibrar: indica uma ' +
      'distância que conheças no desenho e diz quanto mede na ' +
      'realidade. Enquanto não calibrares, o rodapé diz "Not ' +
      'calibrated" e os valores não significam nada.\n\n' +
      'DOIS ECRÃS. O botão "Janela própria" passa a revisão para uma ' +
      'janela do browser separada, para teres o desenho num monitor e o ' +
      'modelo no outro. É a mesma aplicação, não uma segunda cópia: o ' +
      'desenho que estavas a ver acompanha a mudança, e "Voltar ao ' +
      'painel" traz tudo de volta. Se o browser bloquear a janela, ' +
      'autoriza janelas para este site e tenta outra vez.\n\n' +
      'Os documentos e as anotações ficam neste browser e não sobem ' +
      'para lado nenhum. A interface do motor de revisão está em ' +
      'inglês: é um componente de terceiros que não tem tradução.',
  },
  {
    title: 'Marcadores dos tópicos BCF sobre o modelo',
    body:
      'Um tópico BCF ganha um marcador no sítio do modelo a que diz ' +
      'respeito assim que lhe guardares um viewpoint neste browser. ' +
      'Clicar no marcador repõe essa vista. Os viewpoints que vêm ' +
      'dentro de um .bcfzip importado não trazem posição, por isso não ' +
      'geram marcador até serem recapturados.\n\n' +
      'Uma deteção de conflitos exportada para BCF cria um tópico por ' +
      'conflito, e portanto um marcador por conflito. Para não ficares ' +
      'com o modelo coberto, o painel Pins BCF (separador Vista, painel ' +
      'Vistas) liga e desliga os marcadores e escolhe quais aparecem: por ' +
      'estado, por ficheiro BCF, ou escondendo de uma vez os fechados e ' +
      'resolvidos. A contagem ao lado de cada linha diz quantos estás a ' +
      'esconder.\n\n' +
      'Os marcadores fechados aparecem a verde e os restantes a ' +
      'amarelo. Que estados contam como fechados vem das extensões do ' +
      'próprio ficheiro BCF, não de adivinhar pelo texto.\n\n' +
      'Um marcador aparece mesmo com o elemento tapado por outro: é ' +
      'projetado a partir do ponto, sem se testar o que está à frente.',
  },
  {
    title: 'Guardar o modelo em Fragments e marcar versões',
    body:
      'GUARDAR .FRAG. "Guardar .frag" (separador Ficheiro) escreve o ' +
      'modelo ativo no formato Fragments comprimido, que é muito mais ' +
      'pequeno do que o IFC e abre num instante. Serve para trocar ' +
      'geometria depressa.\n\n' +
      'O QUE UM .FRAG NÃO TRAZ. Um Fragments é geometria já derivada, ' +
      'sem o IFC por trás. Um modelo aberto a partir de um .frag ' +
      'aparece marcado com "sem IFC" na lista de modelos, e tudo o que ' +
      'lê o IFC (o IDS, o BoQ, a Tabela, o IFCEdit, a escrita de ' +
      'georreferenciação e o assistente) diz que não consegue ' +
      'responder, em vez de responder zero. Para esse trabalho, abre o ' +
      '.ifc original.\n\n' +
      'REABRIR DEPRESSA. Ao abrires um IFC, a conversão fica guardada ' +
      'neste browser. Abrir o mesmo ficheiro outra vez reaproveita-a e ' +
      'passa de vários segundos para menos de um. A cache sabe com que ' +
      'versão do motor foi feita e é descartada quando ele muda.\n\n' +
      'MARCOS. "Versões" (separador Ficheiro, painel Comparação) guarda ' +
      'o estado atual com um nome e reabre-o mais tarde, ao lado do ' +
      'modelo atual, para usares o Comparar IFC. Cada marco guarda o ' +
      'IFC e não a sua derivação, precisamente para o Comparar IFC o ' +
      'poder ler. Os marcos sobrevivem a fechar a sessão.',
  },
  {
    title: 'Nuvens de pontos e desvio ao modelo',
    body:
      'Nuvem de pontos (separador Ficheiro, painel Modelo) importa uma nuvem ' +
      'para a cena, por cima do modelo. Formatos: LAS, LAZ, E57, PLY, ' +
      'XYZ e PTS. Podes ajustar o tamanho dos pontos e remover a ' +
      'nuvem.\n\n' +
      'LAS E LAZ. O leitor vai até à versão 1.3 do LAS, inclusive. Um ' +
      'ficheiro 1.4, que é a versão corrente e a que sai de muitos ' +
      'equipamentos, é recusado com essa explicação: volta a exportá-lo ' +
      'em 1.2 ou converte-o, por exemplo no CloudCompare. O LAZ é o ' +
      'mesmo formato comprimido e abre na mesma.\n\n' +
      'E57. É a norma aberta ASTM E2807 e o formato que sai da maior ' +
      'parte dos scanners e do ReCap. Abre com cor. O limite é o ' +
      'tamanho: até 80 MB, porque o ficheiro é convertido inteiro em ' +
      'memória antes de ser lido. Um E57 maior divide-se por scans ou ' +
      'converte-se para LAZ, que é comprimido e não tem este ' +
      'limite.\n\n' +
      'DESVIO. Com um modelo carregado, calcula um mapa de desvio: cada ' +
      'ponto é pintado conforme a distância à geometria, com os ' +
      'limiares que definires. Serve para ver de relance onde a obra se ' +
      'afasta do projeto.\n\n' +
      'O que este número é e não é: a distância é medida às caixas ' +
      'envolventes dos elementos e não à superfície deles, sobre uma ' +
      'grelha, com amostragem até 200 mil pontos. Não é uma inspeção ' +
      'metrológica malha-contra-nuvem, e assume coordenadas e ' +
      'tolerância em metros. As nuvens não entram no ficheiro de sessão ' +
      'e são limpas ao fechar a sessão.',
  },
  {
    title: 'Grafo do modelo e informação para reutilização',
    body:
      'Grafo / Reuso (separador Ficheiro, painel Ferramentas) constrói ' +
      'no browser um índice de todas as entidades do modelo e das ' +
      'relações da estrutura espacial. A pesquisa aceita classe IFC, ' +
      'nome, propriedade ou GlobalId, e traz junto de cada resultado o ' +
      'que lhe está ligado a um salto. Clicar num resultado seleciona o ' +
      'elemento no 3D. A lista mostra os primeiros 50: restringe a ' +
      'procura para ver o resto.\n\n' +
      'REUSO. O segundo separador conta, elemento a elemento, o que ' +
      'está declarado no IFC: identificador, nome, tipo associado, ' +
      'material associado, e propriedades cujo nome fale de estado, ' +
      'reutilização ou vida útil. Conta apenas as entidades com ' +
      'identidade própria; unidades e valores de propriedade ficam de ' +
      'fora, e a linha "Fora da conta" diz quantos.\n\n' +
      'É uma medida de completude de informação. NÃO diz que o edifício ' +
      'é circular nem sustentável, e não segue nenhuma norma. O tipo e ' +
      'o material só se leem no IFC: num modelo sem IFC essas duas ' +
      'linhas aparecem como não observadas, e não como zero.',
  },
  {
    title: 'Dar o modelo a ler ao Claude Code (snapshot MCP)',
    body:
      'Snapshot MCP (separador Ficheiro, painel Ferramentas) exporta um ' +
      'ficheiro .vn2r-mcp.json com o modelo que tens aberto: classes, ' +
      'propriedades, resumo e georreferenciação. Um servidor local, em ' +
      'tools/vn2r-mcp, dá esse ficheiro a ler ao Claude Code ou a outro ' +
      'cliente MCP. Só de leitura: não altera o modelo nem abre portas ' +
      'de rede.\n\n' +
      'É uma fotografia. Fica desatualizado assim que o modelo muda, e ' +
      'é preciso voltar a exportar. E leva as propriedades do modelo em ' +
      'texto legível, por isso trata o ficheiro como tratas o IFC de ' +
      'origem: não o deixes numa pasta partilhada.\n\n' +
      'Não confundir com o servidor MCP que lê os IFC diretamente do ' +
      'disco: esse está sempre atualizado e não precisa de browser ' +
      'aberto. São complementares, e registam-se com nomes diferentes.',
  },
  {
    title: 'Reportar bugs e dar feedback',
    body:
      'Ao reportar um bug, inclui:\n' +
      '- Versão da app (mostrada na tab Versão desta popover).\n' +
      '- Commit (visível na tab Versão; clica para abrir o commit no GitHub).\n' +
      '- Browser e sistema operativo.\n' +
      '- Schema do IFC envolvido (badge na topbar) e, se possível, um ' +
      'modelo mínimo que reproduza o problema.\n\n' +
      'Envia para rui.gavina@vn2r.pt.',
  },
  {
    title: 'IFC 4.3 e geometria',
    body:
      'Se vires fittings de HVAC invisíveis num IFC 4.3, é a limitação do ' +
      'web-ifc 0.0.77 listada em "Limitações". O workaround é re-exportar ' +
      'como IFC 4 Reference View no Revit. Removeremos a limitação assim ' +
      'que o ThatOpen Fragments suporte uma versão mais recente do web-ifc.',
  },
]
